import React, { useEffect, useMemo, useRef, useState } from 'react';
import Hls from 'hls.js';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import WaveformEditor from '../components/WaveformEditor';
import SongLibraryModal from '../components/SongLibraryModal';
import QuickCompareModal from '../components/QuickCompareModal';
import YouTubeSearchModal from '../components/YouTubeSearchModal';
import FootagePickerModal from '../components/FootagePickerModal';
import CompositionPlayer from '../components/CompositionPlayer';
import BakeReceipt from '../components/BakeReceipt';
import WaveTimeline from '../components/WaveTimeline';
import { useMasterThumbs, useFrameAt } from '../components/ClipThumb';
import ColorReframeStudio from '../components/ColorReframeStudio';
import { useAgentHandlers, useAgentContext } from '../agent/agentBus';
import { presetNames } from '../agent/colorActions';
import ReviewExport from '../components/ReviewExport';
import LiveGradeStudio from '../components/LiveGradeStudio';
import ClipInspector from '../components/ClipInspector';
import FrameGrabber from '../components/FrameGrabber';
import JobFanoutGraph from '../components/JobFanoutGraph';
import {
  useMovie,
  useExports,
  useClipReframes,
  useCollection,
  updateMovie,
  deleteMovie,
  requestRender,
  requestMaster,
  buildMovieKey,
  movieOutput,
  getAssetDoc,
  createStillGrabAsset,
  subscribeAsset,
  requestAssetWaveform,
  requestWaveform,
  queueSdkPreview,
  useEditVersions,
  saveEditVersion,
  restoreEditVersion,
  deleteEditVersion,
  branchMovie,
  baseMovieTitle,
  useMovieFamily,
  getEditVersion,
  copyClipReframeDoc,
  DEFAULT_OUTPUT,
  OUTPUT_RESOLUTIONS,
  OUTPUT_ASPECT,
  OUTPUT_FPS,
  OUTPUT_QUALITY,
} from '../data/firestore';
import { uploadFile, cdnUrl, getAssetPlaybackUrl, exportFrameGrab, downloadAsset, requestResolveExport, requestResolveFinish } from '../data/uploadService';
import { httpsify } from '../utils/url';
import { fmtTime as fmt, fmtTimePrecise, makeClipId, clipLength, clipSpeed, clipRealSpeed, assetRealSpeedX, resolveClipUrl, snapFps, masterSegments, masterDuration, clipSourceLength, clipSpeedKeys, clipSpeedMap, speedAtSource, timelineToSrc, srcToTimeline, hasSpeedRamp, clipReframe, editToMaster, masterToEdit, masterCutSig, sdkPreviewFor, effectiveRedDecode } from '../utils/movie';
import { gradingConfig } from '../utils/gradingDefaults';
import { peaksFromAudioFile } from '../utils/audioPeaks';
import { renderEtaLabel, jobEtaLabel, estimateMasterSec, fmtDuration, useNow } from '../utils/eta';
import { gradeSignature, DEFAULT_GRADE, gradeAtTime, sortKeyframes } from '../utils/grade';
import { centerAt as reframeCenterAt, scaleAt as reframeScaleAt } from '../utils/social';
import { estimatePublishCost, fmtUSD } from '../utils/cost';
import { diffEdits } from '../utils/movieDiff';

const IDENTITY_GRADE_SIG = gradeSignature(DEFAULT_GRADE);
const publishedGradeSig = (movie) => movie?.render?.gradeSig || IDENTITY_GRADE_SIG;
const currentGradeSig = (movie) => gradeSignature(movie?.grade || DEFAULT_GRADE);

// Clip playback-speed presets offered in the timeline dropdown. The manual
// number input alongside it accepts any value (e.g. 6.67×) for fine control.
const SPEED_OPTIONS = [0.25, 0.33, 0.5, 0.67, 1, 1.5, 2, 3, 4, 5, 6.67, 8, 10];
const SPEED_MIN = 0.1;
const SPEED_MAX = 20;

// Per-tab layout: which big panels are visible on each editor tab (right side
// panel + timeline lanes). Persisted per tab (cs-panels-<tab>) so e.g. Reframe
// can run full-width while Assemble keeps its clip list. The legacy global
// cs_tl_hidden key seeds the timeline default for continuity.
const PANEL_TABS = ['assemble', 'color', 'reframe'];
const PANEL_DEFAULTS = { side: true, timeline: true };
function readPanelVis(t) {
  const vis = { ...PANEL_DEFAULTS };
  try {
    if (localStorage.getItem('cs_tl_hidden') === '1') vis.timeline = false;
    const saved = JSON.parse(localStorage.getItem(`cs-panels-${t}`) || 'null');
    if (saved && typeof saved === 'object') Object.assign(vis, saved);
  } catch { /* noop */ }
  return vis;
}

const numOf = (v) => { const x = Number(v); return Number.isFinite(x) ? x : null; };
const roundTo = (x, d = 4) => (x == null ? null : Math.round(x * 10 ** d) / 10 ** d);

// Per-clip "speed math": shows the raw camera metadata and the exact
// record-rate ÷ project-rate derivation of the slow-mo speed-up, so the editor
// can see (and sanity-check) where "true speed" comes from for each RED clip.
function ClipMotionPanel({ clip, asset, onSetSpeed, onDeclareSpeed, timelineFps }) {
  const [showRaw, setShowRaw] = useState(false);
  const [capInput, setCapInput] = useState('');
  // Minimized by default so the timeline stays compact; preference is remembered.
  const [open, setOpen] = useState(() => {
    try { return localStorage.getItem('cs-speedmath-open') === '1'; } catch { return false; }
  });
  const toggleOpen = () =>
    setOpen((v) => {
      const n = !v;
      try { localStorage.setItem('cs-speedmath-open', n ? '1' : '0'); } catch { /* ignore */ }
      return n;
    });
  const loading = !asset || asset.id !== clip.assetId;
  const rm = !loading ? asset.redMeta || null : null;
  const all = rm?._all || null;

  // Rational frame rates (numerator/denominator) give the EXACT ratio; the plain
  // decimals are what RED rounds to. We show both because they can disagree
  // (e.g. 160000/24000 = 6.667 exactly, but the stored decimal rounds to 6.67).
  const rec = numOf(rm?.recordFps);
  const proj = numOf(rm?.fps);
  const recNum = numOf(all?.record_framerate_numerator);
  const recDen = numOf(all?.record_framerate_denominator);
  const prjNum = numOf(all?.framerate_numerator);
  const prjDen = numOf(all?.framerate_denominator);

  let exact = null;
  if (recNum && recDen && prjNum && prjDen) exact = (recNum / recDen) / (prjNum / prjDen);
  else if (rec && proj) exact = rec / proj;
  const decimal = rec && proj ? rec / proj : null;
  const stored = numOf(rm?.trueSpeedX);
  const applied = clipSpeed(clip);
  const isSlomo = exact != null && exact > 1.01;
  const realTarget = roundTo(exact, 3);
  const atReal = realTarget != null && Math.abs(applied - realTarget) < 0.02;
  // A clean 2× slow-mo of real motion — the "cinematic" feel that reads as
  // natural for water even though true real-time is faster.
  const halfTarget = realTarget != null ? roundTo(realTarget / 2, 2) : null;
  const atHalf = halfTarget != null && Math.abs(applied - halfTarget) < 0.02;
  const roundingGap = exact != null && stored != null && Math.abs(exact - stored) > 0.005;

  // Independent physical cross-check of the recorded fps from the shutter: a
  // shutter open for `deg/360` of each frame at 1/frac s exposure implies a frame
  // rate of (deg/360)·frac. When it agrees with record_framerate, the slow-mo
  // factor is proven from the optics, not just a (potentially wrong) fps tag.
  const shutterDeg = numOf(all?.shutter_degrees);
  const shutterFrac = numOf(all?.shutter_fractions);
  const impliedFps = shutterDeg && shutterFrac ? (shutterDeg / 360) * shutterFrac : null;
  const shutterMatch = impliedFps != null && rec ? Math.abs(impliedFps - rec) / rec < 0.03 : false;

  // Non-RED fallback: capture rate vs container rate.
  const cap = numOf(asset?.captureFps) || numOf(asset?.srcFps) || numOf(asset?.metadata?.video?.captureFps);
  const base = numOf(asset?.fpsAvg) || numOf(asset?.metadata?.video?.fpsAvg) || numOf(asset?.fps);

  // Manual declaration for conformed exports (e.g. a high-fps .R3D flattened to a
  // 24 fps .mp4) that carry NO embedded capture rate. The owner declares the rate
  // it was shot at; we store it as realSpeedX = capture ÷ container playback rate.
  const containerFps = roundTo(base || numOf(asset?.fps) || 24, 3) || 24;
  const declaredX = numOf(clip.realSpeedX);
  const hasDeclared = declaredX > 1.01 && !isSlomo; // RED metadata wins when present
  // Effective real-motion factor: RED-derived if present, else the manual one.
  const realEff = isSlomo ? realTarget : (hasDeclared ? roundTo(declaredX, 3) : null);
  const atRealEff = realEff != null && Math.abs(applied - realEff) < 0.02;
  const halfEff = realEff != null ? roundTo(realEff / 2, 2) : null;
  const atHalfEff = halfEff != null && Math.abs(applied - halfEff) < 0.02;
  const slomoEff = isSlomo || hasDeclared;
  const capNum = parseFloat(capInput);
  const capValid = Number.isFinite(capNum) && capNum > containerFps * 1.05;
  const applyCapture = () => { if (capValid && onDeclareSpeed) onDeclareSpeed(roundTo(capNum / containerFps, 3)); };
  const clearCapture = () => { if (onDeclareSpeed) onDeclareSpeed(0); setCapInput(''); };
  useEffect(() => {
    setCapInput(numOf(clip.realSpeedX) > 1.01 ? String(roundTo(numOf(clip.realSpeedX) * containerFps, 1)) : '');
  // Reset the field only when switching clips, not on every keystroke/apply.
  }, [clip.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const rawRows = [];
  if (rm) {
    for (const [k, v] of Object.entries(rm)) {
      if (k === '_all' || v == null || typeof v === 'object') continue;
      rawRows.push([k, String(v)]);
    }
    if (all) for (const [k, v] of Object.entries(all)) rawRows.push([k, String(v)]);
  }

  return (
    <div className={`cs-motion${open ? '' : ' is-min'}`}>
      <div className="cs-motion-head">
        <button
          type="button"
          className="cs-motion-toggle"
          onClick={toggleOpen}
          aria-expanded={open}
          title={open ? 'Minimize speed math' : 'Expand camera speed math'}
        >
          <span className="cs-motion-caret">{open ? '▾' : '▸'}</span>
          <span className="cs-motion-title">⏱ Speed math</span>
        </button>
        <span className="cs-motion-sum" title="Applied playback speed for this clip">
          {slomoEff && realEff != null && <em>real {realEff}×</em>}
          {' '}applied <b>{applied}×</b>{atRealEff ? ' ✓' : ''}
        </span>
        {slomoEff && realEff != null && (
          <div className="cs-motion-btns">
            <button
              className={`cs-btn cs-btn-sm ${atRealEff ? 'cs-btn-ghost' : 'cs-btn-primary'}`}
              disabled={atRealEff}
              title={`Set this clip to ${realEff}× so it plays at true real-life motion`}
              onClick={() => onSetSpeed(realEff)}
            >
              {atRealEff ? 'real ✓' : `Real (${realEff}×)`}
            </button>
            {halfEff != null && (
              <button
                className={`cs-btn cs-btn-sm ${atHalfEff ? 'cs-btn-ghost' : ''}`}
                disabled={atHalfEff}
                title={`Set this clip to ${halfEff}× — a 2× slow-motion of real life (the cinematic "natural" look for water)`}
                onClick={() => onSetSpeed(halfEff)}
              >
                {atHalfEff ? '½ real ✓' : `½ real (${halfEff}×)`}
              </button>
            )}
          </div>
        )}
      </div>

      {!open ? null : loading ? (
        <div className="cs-motion-note">Reading camera metadata…</div>
      ) : !rm && !isSlomo ? (
        hasDeclared ? (
          <>
            <div className="cs-motion-grid">
              <span title="Declared by you — this conformed export carries no embedded capture rate.">Capture (declared)</span>
              <b>≈ {roundTo(declaredX * containerFps, 1)} fps</b>
              <span>Plays at</span><b>{roundTo(containerFps, 3)} fps</b>
              <span>Slow-motion factor = capture ÷ playback</span>
              <b>{roundTo(declaredX, 3)}× — speed up this much for real motion</b>
              <span>Applied now</span>
              <b>{applied}× {atRealEff ? '· real motion' : applied === 1 ? '· full slow-mo as placed' : ''}</b>
            </div>
            <div className="cs-motion-declare">
              <div className="cs-motion-note">Manually declared (no embedded capture rate). Adjust the shot rate or clear it.</div>
              <div className="cs-motion-declrow">
                <label>Shot at
                  <input className="cs-input" type="number" min={containerFps} step="1"
                    value={capInput} placeholder="e.g. 160"
                    onChange={(e) => setCapInput(e.target.value)} />
                  fps
                </label>
                {capValid && <span className="cs-motion-deriv">= {roundTo(capNum / containerFps, 2)}× slow-mo</span>}
                <button className="cs-btn cs-btn-sm cs-btn-primary" disabled={!capValid} onClick={applyCapture}>Apply</button>
                <button className="cs-btn cs-btn-sm cs-btn-ghost" onClick={clearCapture}>Clear</button>
              </div>
            </div>
          </>
        ) : cap && base && cap > base * 1.05 ? (
          <div className="cs-motion-grid">
            <span>Capture rate</span><b>{roundTo(cap, 3)} fps</b>
            <span>Container rate</span><b>{roundTo(base, 3)} fps</b>
            <span>True speed = capture ÷ container</span>
            <b>{roundTo(cap, 3)} ÷ {roundTo(base, 3)} = {roundTo(cap / base, 3)}×</b>
          </div>
        ) : (
          <div className="cs-motion-declare">
            <div className="cs-motion-note">
              No capture-rate metadata — this is a conformed {roundTo(containerFps, 0)} fps export. If it was shot in slow motion, declare the rate it was recorded at and the editor will treat it as overcranked footage:
            </div>
            <div className="cs-motion-declrow">
              <label>Shot at
                <input className="cs-input" type="number" min={containerFps} step="1"
                  value={capInput} placeholder="e.g. 160"
                  onChange={(e) => setCapInput(e.target.value)} />
                fps
              </label>
              {capValid && <span className="cs-motion-deriv">= {roundTo(capNum / containerFps, 2)}× slow-mo · speed up this much for real motion</span>}
              <button className="cs-btn cs-btn-sm cs-btn-primary" disabled={!capValid} onClick={applyCapture}>Apply</button>
            </div>
          </div>
        )
      ) : (
        <>
          <div className="cs-motion-grid">
            <span>Recorded (sensor)</span>
            <b>{roundTo(rec, 3)} fps{recNum && recDen ? `  ·  ${recNum}/${recDen}` : ''}</b>
            {impliedFps != null && (
              <>
                <span title="An independent check of the recorded fps straight from the optics: a shutter open for (angle/360) of each frame at the listed exposure can only happen at this frame rate. If it matches the recorded fps, the slow-mo factor is physically proven.">Shutter cross-check</span>
                <b className={shutterMatch ? 'cs-motion-ok' : 'cs-motion-bad'}>
                  {roundTo(shutterDeg, 2)}° @ 1/{Math.round(shutterFrac)} s → {roundTo(impliedFps, 2)} fps {shutterMatch ? '✓ confirms sensor' : '⚠ disagrees'}
                </b>
              </>
            )}
            <span title="The rate RED baked into the clip — what it plays back at, making the overcrank look slow-mo. This is a per-clip camera value, not your movie's timeline.">Clip conform (RED project)</span>
            <b>{roundTo(proj, 3)} fps{prjNum && prjDen ? `  ·  ${prjNum}/${prjDen}` : ''}</b>
            <span title="Your movie's delivery rate — what the finished film renders at. Real-motion speed does NOT depend on this; it's wall-clock based.">Movie timeline (delivery)</span>
            <b>{timelineFps ? `${roundTo(timelineFps, 3)} fps` : '—'}</b>
            <span>True speed = recorded ÷ conform</span>
            <b>
              {exact != null && recNum && prjNum && recDen === prjDen
                ? `${recNum}/${prjNum} = ${roundTo(exact, 4)}×`
                : decimal != null
                ? `${roundTo(rec, 3)} ÷ ${roundTo(proj, 3)} = ${roundTo(decimal, 4)}×`
                : '—'}
            </b>
            <span>Stored on clip</span>
            <b>{stored != null ? `${stored}×` : '—'}{clip.realSpeedX ? ` (clip: ${clip.realSpeedX}×)` : ''}</b>
            <span>Applied now</span>
            <b>{applied}× {atReal ? '· real motion' : applied === 1 ? '· slow-mo as placed' : ''}</b>
          </div>
          {roundingGap && (
            <div className="cs-motion-warn">
              ⚠ Stored {stored}× is the rounded decimal; the exact frame-rate ratio is {roundTo(exact, 4)}×. The “Set real motion” button uses the exact value.
            </div>
          )}
        </>
      )}

      {open && rawRows.length > 0 && (
        <div className="cs-motion-raw">
          <button className="cs-linkbtn" onClick={() => setShowRaw((v) => !v)}>
            {showRaw ? '▾ Hide' : '▸ Show'} all camera metadata ({rawRows.length})
          </button>
          {showRaw && (
            <div className="cs-motion-rawgrid">
              {rawRows.map(([k, v]) => (
                <React.Fragment key={k}>
                  <span>{k}</span>
                  <code>{v}</code>
                </React.Fragment>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Variable-speed (ramp) editor for a single clip. The user scrubs to a point in
// the clip, picks a target speed and a ramp time, and the clip eases from its
// current speed to the target. Stored as speedKeys [{ t(source s), speed }].
function SpeedRampPanel({ clip, playheadSrc, srcLen, clipStartTl, onChange }) {
  // Open by default — collapsed, the panel is a single easy-to-miss label.
  const [open, setOpen] = useState(() => {
    try { return localStorage.getItem('cs-speedramp-open') !== '0'; } catch { return true; }
  });
  const toggleOpen = () =>
    setOpen((v) => {
      const n = !v;
      try { localStorage.setItem('cs-speedramp-open', n ? '1' : '0'); } catch { /* ignore */ }
      return n;
    });
  const [target, setTarget] = useState('2');
  const [rampSec, setRampSec] = useState('1.5');
  const keys = clipSpeedKeys(clip);
  const baseSpeed = clipSpeed(clip);
  const ramped = hasSpeedRamp(clip);
  const pos = Number.isFinite(playheadSrc) ? Math.max(0, Math.min(srcLen, playheadSrc)) : null;
  const curSpeed = pos != null ? speedAtSource(clip, pos) : baseSpeed;
  const r2 = (x) => Math.round(x * 100) / 100;
  // Movie-time of a source second — same units as the playhead readout in the
  // toolbar, so keys can be visually correlated with where the playhead sits.
  const map = clipSpeedMap(clip);
  const tlOf = (srcT) => (clipStartTl != null ? fmtTimePrecise(clipStartTl + srcToTimeline(map, srcT)) : null);
  // Start of the clip's final kept frame — where an "end" key must land so it
  // still maps inside the clip (a key at exactly srcLen would sit past it).
  const fps = clip.fps || clip.srcFps || 30;
  const lastFrameT = r2(Math.max(0, srcLen - 1 / fps));

  const addRamp = () => {
    if (pos == null) return;
    const tv = Math.min(10, Math.max(0.1, parseFloat(target)));
    const rs = Math.max(0.1, parseFloat(rampSec) || 1);
    if (!Number.isFinite(tv)) return;
    const v0 = curSpeed;
    // `rampSec` is OUTPUT (timeline) seconds; convert to a source span using the
    // mean speed across the ramp so the transition lasts ~that long on screen.
    const span = Math.max(0.05, rs * ((v0 + tv) / 2));
    const s1 = Math.min(srcLen, pos + span);
    if (s1 <= pos + 0.02) return;
    const next = keys.length ? keys.slice() : [{ t: 0, speed: r2(baseSpeed) }];
    const put = (t, speed) => {
      const t2 = r2(t);
      const idx = next.findIndex((k) => Math.abs(k.t - t2) < 0.08);
      if (idx >= 0) next[idx] = { t: t2, speed: r2(speed) };
      else next.push({ t: t2, speed: r2(speed) });
    };
    put(pos, v0);
    put(s1, tv);
    onChange(next.sort((a, b) => a.t - b.t));
  };
  // Drop/overwrite a key at source time `t` targeting the panel's speed.
  // Shared by "＋ Key" (playhead) and the ⇤/⇥ first-/last-frame buttons. The
  // 0.08s dedupe also absorbs the seeded t=0 base key when placing a start key.
  const putKeyAt = (t) => {
    const tv = Math.min(10, Math.max(0.1, parseFloat(target)));
    if (!Number.isFinite(tv)) return;
    const next = keys.length ? keys.slice() : [{ t: 0, speed: r2(baseSpeed) }];
    const t2 = r2(t);
    const idx = next.findIndex((k) => Math.abs(k.t - t2) < 0.08);
    if (idx >= 0) next[idx] = { t: t2, speed: r2(tv) };
    else next.push({ t: t2, speed: r2(tv) });
    onChange(next.sort((a, b) => a.t - b.t));
  };
  const setHold = () => { if (pos != null) putKeyAt(pos); };
  const removeKey = (t) => onChange(keys.filter((k) => k.t !== t));
  const clearAll = () => onChange([]);
  const setKeySpeed = (t, speed) => {
    const tv = Math.min(10, Math.max(0.1, speed));
    if (!Number.isFinite(tv)) return;
    onChange(keys.map((k) => (k.t === t ? { t: k.t, speed: r2(tv) } : k)));
  };
  // Accepts "m:ss.ff", "ss.ff" or plain seconds → seconds, or null on garbage.
  const parseTimeStr = (str) => {
    const s = String(str || '').trim();
    if (!s) return null;
    const parts = s.split(':');
    if (parts.length > 2) return null;
    const sec = parseFloat(parts[parts.length - 1]);
    const min = parts.length === 2 ? parseFloat(parts[0]) : 0;
    if (!Number.isFinite(sec) || !Number.isFinite(min) || sec < 0 || min < 0) return null;
    return min * 60 + sec;
  };
  // Move a key to the MOVIE time the user typed: convert back to source
  // seconds through the (pre-edit) speed map — the same convention the
  // displayed time uses — then dedupe against neighbors like put() does.
  const setKeyTime = (t, str) => {
    const entered = parseTimeStr(str);
    if (entered == null) return;
    const src = clipStartTl != null ? timelineToSrc(map, entered - clipStartTl) : entered;
    const t2 = r2(Math.max(0, Math.min(srcLen, src)));
    if (Math.abs(t2 - t) < 0.001) return;
    const next = keys
      .filter((k) => k.t === t || Math.abs(k.t - t2) >= 0.08)
      .map((k) => (k.t === t ? { t: t2, speed: k.speed } : k));
    onChange(next.sort((a, b) => a.t - b.t));
  };

  // Expanded, everything packs into ONE dense wrapping row (controls + key
  // chips) so the panel steals almost no height from the timeline lanes.
  return (
    <div className={`cs-ramp${open ? '' : ' is-min'}`}>
      <div className="cs-ramp-head">
        <button type="button" className="cs-motion-toggle" onClick={toggleOpen} aria-expanded={open}
          title={open ? 'Minimize speed ramps' : 'Expand speed ramps'}>
          <span className="cs-motion-caret">{open ? '▾' : '▸'}</span>
          <span className="cs-motion-title">🏎 Speed ramp</span>
        </button>
        {!open ? (
          <span className="cs-ramp-sum">
            {ramped ? <b>{keys.length} keys</b> : 'fixed'}
            {pos != null && <em> · {r2(curSpeed)}× @ {tlOf(pos) || `${r2(pos)}s`}</em>}
          </span>
        ) : (
          <>
            <label className="cs-ramp-lbl" title="Target speed for the next keyframe">
              <input className="cs-input cs-ramp-num" type="number" min="0.1" max="10" step="0.05"
                value={target} onChange={(e) => setTarget(e.target.value)} />×
            </label>
            {pos != null && (
              <button className="cs-btn cs-btn-sm cs-btn-primary" onClick={setHold}
                title={`Drop a speed keyframe at the playhead (movie time ${tlOf(pos) || '—'}, ${r2(pos)}s into the clip's source) targeting ${target}×. The clip's speed ramps linearly between keyframes.`}>
                ＋ Key
              </button>
            )}
            <button className="cs-btn cs-btn-sm cs-btn-ghost" onClick={() => putKeyAt(0)}
              title={`Keyframe at the clip's FIRST frame (0.00s) targeting ${target}× — pins the speed the clip starts at`}>
              ⇤
            </button>
            <button className="cs-btn cs-btn-sm cs-btn-ghost" onClick={() => putKeyAt(lastFrameT)}
              title={`Keyframe at the clip's LAST frame (${lastFrameT}s) targeting ${target}× — pins the speed the clip ends at`}>
              ⇥
            </button>
            {pos != null && (
              <>
                <label className="cs-ramp-lbl" title={`Shortcut: drops TWO keys — ${r2(curSpeed)}× at the playhead and ${target}× later — easing to the target over this many on-screen seconds`}>
                  ease
                  <input className="cs-input cs-ramp-num" type="number" min="0.1" step="0.1"
                    value={rampSec} onChange={(e) => setRampSec(e.target.value)} />s
                </label>
                <button className="cs-btn cs-btn-sm cs-btn-ghost" onClick={addRamp}
                  title={`Drops TWO keys — ${r2(curSpeed)}× at the playhead and ${target}× ${rampSec}s later — for a smooth transition`}>
                  Ramp
                </button>
              </>
            )}
            {keys.length > 0 && (
              <button className="cs-btn cs-btn-sm cs-btn-ghost" onClick={clearAll} title="Remove every speed keyframe (back to the clip's fixed speed)">
                Clear
              </button>
            )}
            {keys.map((k) => (
              <span className="cs-ramp-key" key={`${k.t}:${k.speed}`} title={`Keyframe at movie time ${tlOf(k.t) || '—'} (${r2(k.t)}s into the clip's source) — edit the speed or remove it`}>
                <input
                  className="cs-input cs-ramp-num"
                  type="number" min="0.1" max="10" step="0.05"
                  key={`ks:${k.t}:${k.speed}`}
                  defaultValue={k.speed}
                  onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                  onBlur={(e) => {
                    const v = parseFloat(e.currentTarget.value);
                    if (Number.isFinite(v) && Math.abs(v - k.speed) > 0.001) setKeySpeed(k.t, v);
                  }}
                />
                <b>×</b>@
                <input
                  className="cs-input cs-ramp-time"
                  type="text"
                  key={`kt:${k.t}`}
                  defaultValue={tlOf(k.t) || `${r2(k.t)}`}
                  title="Keyframe time (movie time m:ss.ff) — type a new time to move this key. Also accepts plain seconds."
                  onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                  onBlur={(e) => setKeyTime(k.t, e.currentTarget.value)}
                />
                <button className="cs-ramp-kdel" title="Remove key" onClick={() => removeKey(k.t)}>×</button>
              </span>
            ))}
            {keys.length === 1 && (
              <span className="cs-motion-note" style={{ margin: 0 }} title="Speed interpolates linearly between keyframes">
                1 key = constant · add a 2nd for a ramp
              </span>
            )}
            {pos != null ? (
              <span className="cs-ramp-sum" title={`Speed under the playhead — movie time ${tlOf(pos) || '—'} = ${r2(pos)}s into this clip's source`}>
                <em>{r2(curSpeed)}× @ {tlOf(pos) || `${r2(pos)}s`}</em>
              </span>
            ) : (
              <span className="cs-motion-note" style={{ margin: 0 }}>
                scrub over the clip for playhead keys — ⇤/⇥ work anywhere
              </span>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// Keyframe thumbnail for a clip row in the Assemble sidebar. Prefers the
// server-rendered thumb (movie.master.thumbs); when a clip has none yet (added
// or split after the last preview build) it live-captures the frame from the
// preview master — the same fallback the timeline tiles use — so every row
// gets an image as long as a master exists.
function ClipRowThumb({ entry, masterUrl, clip }) {
  const ts = entry?.tileStart || 0;
  const te = entry?.tileEnd != null ? entry.tileEnd : ts + 1;
  const frac = Math.max(0, Math.min(1, clip?.thumbFrac == null ? 0.5 : clip.thumbFrac));
  // Live-capture when there's no baked thumb OR the user picked a custom frame
  // (thumbFrac) after the master's thumbs were baked — same rule as the
  // timeline tile, so both surfaces show the SAME picked frame immediately.
  const wantLive = !!masterUrl && (!entry?.rawUrl || clip?.thumbFrac != null);
  const { rawUrl: liveUrl } = useFrameAt(masterUrl, ts + (te - ts) * frac, wantLive);
  const url = (wantLive && liveUrl) || entry?.rawUrl || '';
  if (!url) return null;
  return (
    <img
      className="cs-clipthumb"
      src={url}
      alt=""
      draggable={false}
      title="Clip keyframe (same as the timeline thumbnail — 🖼 in the transport re-sets it)"
    />
  );
}

function readMediaDurationUrl(url) {
  const isHls = /\.m3u8(\?|$)/i.test(url || '');
  return new Promise((resolve) => {
    const el = document.createElement('video');
    el.preload = 'metadata';
    el.crossOrigin = 'anonymous';
    el.muted = true;
    let hls;
    let done = false;
    const finish = (d) => {
      if (done) return;
      done = true;
      try { hls?.destroy(); } catch { /* ignore */ }
      resolve(Number.isFinite(d) && d > 0 ? d : 0);
    };
    el.onloadedmetadata = () => finish(el.duration);
    el.onerror = () => finish(0);
    // HLS .m3u8 can't be read by a bare <video> in Chrome — parse it with
    // hls.js and take the playlist's total duration.
    if (isHls && Hls.isSupported()) {
      hls = new Hls({ maxBufferLength: 1 });
      hls.on(Hls.Events.LEVEL_LOADED, (_e, data) => {
        const d = data?.details?.totalduration;
        if (d) finish(d);
      });
      hls.on(Hls.Events.ERROR, (_e, data) => { if (data?.fatal) finish(el.duration || 0); });
      hls.loadSource(url);
      hls.attachMedia(el);
    } else {
      el.src = url;
    }
    setTimeout(() => finish(el.duration || 0), 8000);
  });
}
function readFileDuration(file) {
  return new Promise((resolve) => {
    const el = document.createElement('video');
    el.preload = 'metadata';
    el.onloadedmetadata = () => resolve(Number.isFinite(el.duration) ? el.duration : 0);
    el.onerror = () => resolve(0);
    el.src = URL.createObjectURL(file);
  });
}

// Best-effort frame-rate measurement: briefly play a muted, hidden <video> and
// count presented frames via requestVideoFrameCallback. Returns a snapped fps
// (e.g. 30 / 59.94 / 120) or 0 when unsupported (e.g. HLS in Chrome).
function estimateFps(url) {
  return new Promise((resolve) => {
    if (!url || typeof HTMLVideoElement === 'undefined' || !('requestVideoFrameCallback' in HTMLVideoElement.prototype)) {
      resolve(0);
      return;
    }
    const el = document.createElement('video');
    el.muted = true;
    el.crossOrigin = 'anonymous';
    el.playsInline = true;
    el.preload = 'auto';
    let first = null;
    let last = null;
    let count = 0;
    let settled = false;
    const cleanup = () => {
      try { el.pause(); el.removeAttribute('src'); el.load(); } catch { /* noop */ }
    };
    const finish = () => {
      if (settled) return;
      settled = true;
      let fps = 0;
      if (first && last && last.mediaTime > first.mediaTime + 1e-3) {
        fps = (last.presentedFrames - first.presentedFrames) / (last.mediaTime - first.mediaTime);
      }
      cleanup();
      resolve(snapFps(fps));
    };
    const onFrame = (now, metadata) => {
      if (!first) first = metadata;
      last = metadata;
      count += 1;
      if (count >= 24 || metadata.mediaTime - first.mediaTime > 0.7) { finish(); return; }
      el.requestVideoFrameCallback(onFrame);
    };
    el.oncanplay = () => {
      el.play().then(() => el.requestVideoFrameCallback(onFrame)).catch(() => finish());
    };
    el.onerror = () => finish();
    el.src = url;
    setTimeout(finish, 5000);
  });
}

let sseq = 0;
const makeSongId = () => `s${Date.now()}${sseq++}`;

const SOURCE_BADGE = { upload: 'Library', spotify: 'Spotify', youtube: 'YouTube', soundcloud: 'SoundCloud' };

const CLIP_COLORS = ['#14e0c8', '#5b8def', '#e0a414', '#e0517a', '#9b6cf2', '#3fbf6f'];

// Fingerprint of everything that's actually baked into the published file.
// Only these changing should force a re-publish — title/poster/hero/etc don't.
// (YouTube/cloud songs are overlaid live at playback, so they're excluded.)
function renderSignature(clips, songs, intro, outro, hasOriginalAudio, output) {
  return JSON.stringify({
    out: output
      ? { h: output.height ?? 1080, f: output.fps ?? 30, q: output.quality || 'standard', r: output.redSource || 'proxy', a: output.aspect || 'source' }
      : null,
    clips: (clips || []).map((c) => ({
      s: c.url || c.s3Key || '',
      i: c.inSec || 0,
      o: c.outSec || 0,
      d: c.dissolveSec || 0,
      // Per-clip fade to black/white at the clip's start/end.
      fi: c.fadeIn && Number(c.fadeIn.sec) > 0 ? `${c.fadeIn.sec}@${c.fadeIn.color || 'black'}` : '',
      fo: c.fadeOut && Number(c.fadeOut.sec) > 0 ? `${c.fadeOut.sec}@${c.fadeOut.color || 'black'}` : '',
      sp: c.speed || 1,
      // Speed ramps (variable speed over time) change the baked retiming.
      sk: Array.isArray(c.speedKeys) && c.speedKeys.length
        ? c.speedKeys.map((k) => `${k.t}@${k.speed}`).join(',')
        : '',
      // Per-clip baked look: only the effective grade (none when set to Raw).
      g: c.useGrade !== false && c.grade ? gradeSignature(c.grade) : '',
      // Keyframed look: every keyframe's time + grade changes the bake.
      gk: c.useGrade !== false && Array.isArray(c.gradeKeyframes) && c.gradeKeyframes.length
        ? c.gradeKeyframes.map((k) => `${k.t}@${gradeSignature(k.grade)}`).join('|')
        : '',
      // Baked subject reframe: crop window + waypoints + zoom keys.
      rf: (() => {
        const rf = clipReframe(c);
        const active = rf.manualCrop.scale < 0.999 || rf.waypoints.length || rf.zoomKeys.some((k) => k.scale < 0.999);
        if (!active) return '';
        return JSON.stringify({
          m: rf.cropMode,
          c: [rf.manualCrop.cx, rf.manualCrop.cy, rf.manualCrop.scale],
          w: rf.waypoints.map((p) => [p.t, p.cx, p.cy]),
          z: rf.zoomKeys.map((k) => [k.t, k.scale]),
          s: rf.smoothing,
        });
      })(),
    })),
    songs: (songs || [])
      .filter((s) => s.source === 'upload' && s.url)
      .map((s) => ({
        u: s.url,
        off: s.movieOffset || 0,
        v: s.volume == null ? 1 : s.volume,
        ts: s.trimStart || 0,
        te: s.trimEnd || 0,
        fi: s.fadeInSec || 0,
        fo: s.fadeOutSec || 0,
        lp: s.loop === true,
      })),
    intro: intro || 0,
    outro: outro || 0,
    hasOrig: hasOriginalAudio !== false,
  });
}

// Map a movie-timeline second to the underlying clip + that clip's local time.
function clipAtMovieTime(clips, t) {
  let acc = 0;
  for (let i = 0; i < clips.length; i += 1) {
    const c = clips[i];
    const len = clipLength(c);
    if (t < acc + len || i === clips.length - 1) {
      return { clip: c, localSec: (c.inSec || 0) + timelineToSrc(clipSpeedMap(c), Math.max(0, t - acc)) };
    }
    acc += len;
  }
  return { clip: null, localSec: 0 };
}

// Where (in movie time) a given clip starts, so a movie-time playhead can be
// mapped into that clip's own source timeline.
function clipStartOffset(clips, clipId) {
  let acc = 0;
  for (const c of clips) {
    if (c.id === clipId) return acc;
    acc += clipLength(c);
  }
  return acc;
}

// Live still preview: seeks a (muted, paused) <video> to the frame at the given
// movie-time so the poster frame updates as the start slider moves. Falls back
// to an explicit poster image when one is set.
function HeroFramePreview({ clips, time, posterUrl }) {
  const ref = React.useRef(null);
  const [src, setSrc] = useState('');
  const [localSec, setLocalSec] = useState(0);
  const [errored, setErrored] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const { clip, localSec: ls } = clipAtMovieTime(clips, time);
    if (!clip) {
      setSrc('');
      return undefined;
    }
    setLocalSec(ls);
    (async () => {
      try {
        const u = await resolveClipUrl(clip, getAssetPlaybackUrl);
        if (!cancelled) {
          setErrored(false);
          setSrc(httpsify(u) || '');
        }
      } catch {
        if (!cancelled) setSrc('');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [clips, time]);

  useEffect(() => {
    const v = ref.current;
    if (!v || !src) return undefined;
    const seek = () => {
      try {
        v.currentTime = Math.min(localSec, (v.duration || localSec) - 0.05);
      } catch {
        /* noop */
      }
    };
    if (v.readyState >= 1) seek();
    else v.addEventListener('loadedmetadata', seek, { once: true });
    return () => v.removeEventListener('loadedmetadata', seek);
  }, [src, localSec]);

  const poster = httpsify(posterUrl);

  return (
    <>
      <div className="cs-poster" style={{ overflow: 'hidden', padding: 0 }}>
        {src && !errored ? (
          <video
            ref={ref}
            src={src}
            muted
            playsInline
            preload="metadata"
            onError={() => setErrored(true)}
            style={{ width: '100%', height: '100%', objectFit: 'cover', background: '#0e0e14' }}
          />
        ) : poster ? (
          <div style={{ width: '100%', height: '100%', background: `#0e0e14 center / cover no-repeat url(${poster})` }} />
        ) : (
          <span style={{ color: 'var(--muted)', padding: 10 }}>Add a clip to preview the hero frame</span>
        )}
      </div>
      {poster && (
        <div style={{ marginTop: 8 }}>
          <div className="cs-help" style={{ marginBottom: 4 }}>Selected poster image</div>
          <div className="cs-poster" style={{ backgroundImage: `url(${poster})` }} />
        </div>
      )}
    </>
  );
}

// ---- Movie versions: branches, lineage, diffs, cherry-pick ------------------
// "Save as new version" forks the movie into a NEW doc (versions are full
// movies — own id, own bakes/URLs) carrying lineage {rootId, parentId,
// branchBaseVersionId, name}. This panel renders the family tree, diffs the
// saved state of any two versions (diffEdits), and offers clip-level
// cherry-pick + section adopts. Pulls go through the caller's normal state
// setters so undo/autosave keep working.
function MovieVersionsPanel({ movie, navigate, focusSignal, onPullClip, onAdoptSongs, onAdoptGrade, onAdoptOutput }) {
  const { family } = useMovieFamily(movie);
  const [formOpen, setFormOpen] = useState(false);
  const panelRef = useRef(null);
  // The top bar's "⎇ Versions" button jumps here with the form ready to go.
  useEffect(() => {
    if (!focusSignal) return;
    setFormOpen(true);
    panelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [focusSignal]);
  const [newTitle, setNewTitle] = useState('');
  const [newName, setNewName] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [compareId, setCompareId] = useState('');
  const [base, setBase] = useState(null); // { snapshot, parentId } — lazy fork-base fetch

  const lineage = movie?.lineage || null;
  const members = family || [];
  const others = members.filter((m) => m.id !== movie.id);

  // Indented tree: children grouped under their parent; a member whose parent
  // was deleted hangs off the root so nothing disappears.
  const tree = useMemo(() => {
    if (!members.length) return [];
    const ids = new Set(members.map((m) => m.id));
    const kids = new Map();
    let rootNode = null;
    members.forEach((m) => {
      const pid = m.lineage?.parentId;
      if (!pid || !ids.has(pid)) {
        if (!m.lineage) { rootNode = m; return; }
      }
      const key = pid && ids.has(pid) ? pid : (rootNode ? rootNode.id : '__orphan');
      if (!kids.has(key)) kids.set(key, []);
      kids.get(key).push(m);
    });
    const out = [];
    const visit = (node, depth) => {
      out.push({ node, depth });
      (kids.get(node.id) || [])
        .sort((x, y) => (x.lineage?.branchedAt || 0) - (y.lineage?.branchedAt || 0))
        .forEach((k) => visit(k, depth + 1));
    };
    if (rootNode) visit(rootNode, 0);
    else members.forEach((m) => out.push({ node: m, depth: 0 }));
    return out;
  }, [members]);

  const createBranch = async () => {
    setBusy(true);
    setErr('');
    try {
      const newId = await branchMovie(movie.id, { title: newTitle, name: newName });
      // Full page load, NOT SPA navigate: the editor hydrates once per mount
      // (`hydrated` never resets on id change), so an in-place route swap would
      // keep the old movie's edit state and autosave it onto the new doc.
      window.location.assign(`/movies/${newId}/edit`);
    } catch (e) {
      setErr(e.message || 'branch failed');
      setBusy(false);
    }
  };

  // Resolve the compare target: a live family member, or the fork-base
  // snapshot stored on the PARENT at branch time.
  const compareBase = compareId === 'base';
  useEffect(() => {
    if (!compareBase || !lineage?.branchBaseVersionId) return;
    let dead = false;
    getEditVersion(lineage.parentId, lineage.branchBaseVersionId)
      .then((v) => { if (!dead) setBase(v ? { snapshot: v.snapshot, parentId: lineage.parentId } : null); })
      .catch(() => { if (!dead) setBase(null); });
    return () => { dead = true; };
  }, [compareBase, lineage?.parentId, lineage?.branchBaseVersionId]);

  const other = compareBase
    ? (base ? { ...base.snapshot, id: base.parentId, title: `fork base (${base.snapshot?.title || ''})` } : null)
    : others.find((m) => m.id === compareId) || null;
  // Pulled clipReframe docs come from the compared doc's movie (the parent,
  // when comparing against the fork base).
  const otherSourceId = compareBase ? lineage?.parentId : compareId;
  const diff = useMemo(() => (other ? diffEdits(movie, other) : null), [movie, other]);

  const dateLabel = (ts) => (ts ? new Date(ts).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '');

  return (
    <div className="cs-section" ref={panelRef}>
      <div className="cs-section-head">
        <h2>Versions</h2>
        <button className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => setFormOpen((v) => !v)}>
          {formOpen ? 'Cancel' : '⎇ Save as new version'}
        </button>
      </div>
      <p className="cs-section-sub">
        A version is a full movie — independently editable, bakeable and watchable — linked to this
        one by lineage. Compare any two versions to see exactly what changed, and pull individual
        clips (or the soundtrack, movie grade, output settings) across.
      </p>

      {formOpen && (
        <div className="cs-branch-form">
          <input
            className="cs-input"
            placeholder={`New title (default: ${baseMovieTitle(movie.title)} (v${(members.length || 1) + 1}))`}
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
          />
          <input
            className="cs-input"
            placeholder="Branch label, optional (e.g. alt cut, client v2)"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
          />
          <button className="cs-btn cs-btn-sm" disabled={busy} onClick={createBranch}>
            {busy ? 'Creating…' : 'Create version'}
          </button>
          {err && <div className="cs-help" style={{ color: '#e88' }}>{err}</div>}
        </div>
      )}

      {members.length > 1 && (
        <div className="cs-lineage">
          {tree.map(({ node, depth }) => (
            <div key={node.id} className={`cs-lineage-row${node.id === movie.id ? ' cs-lineage-current' : ''}`} style={{ paddingLeft: depth * 18 }}>
              <span className="cs-lineage-glyph">{depth ? '└─' : '●'}</span>
              <span className="cs-version-label">
                <strong>{node.title}</strong>
                <em>
                  {node.lineage?.name ? `${node.lineage.name} · ` : ''}
                  {node.lineage ? `branched ${dateLabel(node.lineage.branchedAt)}` : 'root'}
                  {node.render?.status === 'ready' ? ' · baked' : ''}
                </em>
              </span>
              {node.id !== movie.id && (
                <>
                  {/* Full load (not SPA) — the editor hydrates once per mount. */}
                  <button className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => window.location.assign(`/movies/${node.id}/edit`)}>Edit</button>
                  <button className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => navigate(`/watch/${node.id}`)}>Watch</button>
                </>
              )}
            </div>
          ))}
        </div>
      )}

      {members.length > 1 && (
        <label className="cs-help" style={{ display: 'block', marginTop: 8, cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={!!movie.listStandalone}
            onChange={(e) => updateMovie(movie.id, { listStandalone: e.target.checked }).catch(() => {})}
          />{' '}
          Show this version as its own card on the Movies page (instead of inside the family group)
        </label>
      )}

      {(others.length > 0 || lineage) && (
        <div className="cs-field" style={{ marginTop: 12 }}>
          <label>Compare this version against</label>
          <select className="cs-input" value={compareId} onChange={(e) => setCompareId(e.target.value)}>
            <option value="">— pick a version —</option>
            {lineage?.branchBaseVersionId && <option value="base">Fork point (as branched)</option>}
            {others.map((m) => (
              <option key={m.id} value={m.id}>{m.title}{m.lineage?.name ? ` · ${m.lineage.name}` : ''}</option>
            ))}
          </select>
        </div>
      )}

      {compareBase && !base && compareId && <div className="cs-help">Loading fork point…</div>}
      {diff && diff.identical && <div className="cs-help">No differences — the edits are identical.</div>}
      {diff && !diff.identical && (
        <div className="cs-diff">
          {diff.clips.reordered && <div className="cs-diff-row"><span className="cs-badge">order</span> clip order differs</div>}
          {diff.clips.removed.map((c) => (
            <div key={`rm-${c.id}`} className="cs-diff-row">
              <span className="cs-badge cs-badge-removed">only here</span>
              <span className="cs-version-label"><strong>{c.label || c.id}</strong><em>not in the other version</em></span>
            </div>
          ))}
          {diff.clips.added.map((c) => (
            <div key={`add-${c.id}`} className="cs-diff-row">
              <span className="cs-badge cs-badge-added">only there</span>
              <span className="cs-version-label"><strong>{c.label || c.id}</strong><em>exists only in the other version</em></span>
              <button className="cs-btn cs-btn-ghost cs-btn-sm" title="Add this clip to the end of this timeline"
                onClick={() => onPullClip(otherSourceId, c)}>⇤ Pull clip</button>
            </div>
          ))}
          {diff.clips.changed.map((c) => (
            <div key={`ch-${c.id}`} className="cs-diff-row">
              <span className="cs-badge">changed</span>
              <span className="cs-version-label">
                <strong>{c.label}</strong>
                <em>{c.facets.map((f) => `${f.label}: ${f.summary}`).join(' · ')}</em>
              </span>
              <button className="cs-btn cs-btn-ghost cs-btn-sm" title="Replace this clip with the other version's copy (trim, speed, grade, reframe — everything)"
                onClick={() => onPullClip(otherSourceId, other.clips.find((x) => x.id === c.id))}>⇤ Pull clip</button>
            </div>
          ))}
          {diff.sections.map((s) => (
            <div key={s.key} className="cs-diff-row">
              <span className="cs-badge">{s.label}</span>
              <span className="cs-version-label"><em>{s.summary}</em></span>
              {s.key === 'songs' && <button className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => onAdoptSongs(other)}>⇤ Adopt soundtrack</button>}
              {s.key === 'grade' && <button className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => onAdoptGrade(other)}>⇤ Adopt movie grade</button>}
              {s.key === 'output' && <button className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => onAdoptOutput(other)}>⇤ Adopt output</button>}
            </div>
          ))}
          <div className="cs-help" style={{ marginTop: 6 }}>
            Summaries read current → compared. “Pull clip” copies the other version's clip (and its
            stabilization tracks) into this edit — undo works as usual.
          </div>
        </div>
      )}
    </div>
  );
}

export default function EditMovie() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { doc: movie } = useMovie(id);
  const { docs: socialExports } = useExports(id);
  const clipReframes = useClipReframes(id);

  // One editor, three stages. Initial tab derives from the entry path/query so
  // legacy /color and /social links land in the right place.
  const initialTab = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const q = params.get('layer') || params.get('tab');
    const p = location.pathname;
    if (p.endsWith('/social') || q === 'reframe') return 'reframe';
    if (p.endsWith('/color') || p.endsWith('/studio') || q === 'color' || q === 'grade') return 'color';
    return 'assemble';
  }, [location.pathname, location.search]);
  const [tab, setTab] = useState(initialTab);
  // Bumped by the top bar's "⎇ Versions" button: jumps to Details and tells
  // the Versions panel to open its save-as-new-version form + scroll to it.
  const [versionsFocus, setVersionsFocus] = useState(0);

  // Newest saved bake proof sheet for THIS movie: the Color / Reframe tabs
  // deep-link into it (color mode / crop mode) — proofs are where per-clip
  // looks and crop keyframes actually get dialed in against real bake frames.
  // A fresh branch has no sheets of its own yet, so fall back to the newest
  // sheet from anywhere in its version family — the clips are shared, so the
  // parent's proof frames are exactly as relevant.
  const { docs: proofSheetDocs } = useCollection('proofSheets', { orderBy: 'createdAt' });
  const { family: proofFamily } = useMovieFamily(movie ? { id, ...movie } : null);
  const latestProofSheet = useMemo(() => {
    const sheets = proofSheetDocs || [];
    const newest = (list) => [...list].sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0))[0] || null;
    const own = newest(sheets.filter((s) => s.movieId === id));
    if (own) return own;
    const famIds = new Set((proofFamily || []).map((m) => m.id));
    famIds.delete(id);
    return famIds.size ? newest(sheets.filter((s) => famIds.has(s.movieId))) : null;
  }, [proofSheetDocs, id, proofFamily]);

  const [meta, setMeta] = useState({
    title: '', year: '', location: '', synopsis: '', categories: '', thumbUrl: '', heroUrl: '', featured: false,
  });
  const [clips, setClips] = useState([]);
  const [songs, setSongs] = useState([]);
  const [hasOriginalAudio, setHasOriginalAudio] = useState(true);

  // Voice copilot: let the agent move between editor stages.
  useAgentHandlers({
    switch_tab: ({ tab: t }) => {
      if (['assemble', 'color', 'reframe', 'details', 'review'].includes(t)) { setTab(t); return { ok: true, tab: t }; }
      return { ok: false, error: `unknown tab "${t}"` };
    },
  });
  useAgentContext(
    { pageName: 'Movie editor', movieName: movie?.title, clips: clips.map((c, i) => c.label || `Clip ${i + 1}`), presets: presetNames() },
    [movie?.title, clips.length],
  );
  // Viewer-selectable soundtracks (up to 4): overlaid live at playback so each
  // viewer can pick their own song. Kept separate from the baked `songs` above.
  const [viewerSongs, setViewerSongs] = useState([]);
  // Which viewer song plays first (the others are offered as alternatives).
  // 'orig' = default to the movie's own audio; null = first song in the list.
  const [viewerSongDefault, setViewerSongDefault] = useState(null);
  const [intro, setIntro] = useState(0);
  const [outro, setOutro] = useState(0);
  const [output, setOutput] = useState(DEFAULT_OUTPUT);
  const [hero, setHero] = useState({ posterUrl: '', delaySec: 4, startSec: 0, durationSec: 12 });
  const [hydrated, setHydrated] = useState(false);

  // Clips enriched with their validated SDK preview proxy (movie.sdkPreviews):
  // the players then show a RED clip's TRUE bake de-log instead of the flat-log
  // proxy. sdkPreviewFor() drops a preview whose window or SDK development no
  // longer matches the clip, so a stale file can never lie.
  const playerClips = useMemo(() => clips.map((c) => {
    const sp = sdkPreviewFor(movie?.sdkPreviews, c, output);
    return sp ? { ...c, sdkPreview: sp } : c;
  }), [clips, movie?.sdkPreviews, output]);

  // Auto-rebuild SDK previews that went stale — a trim expanded past the
  // decoded window's pad, or the clip's SDK development changed. Only clips
  // that ALREADY have a preview refresh themselves (first builds stay explicit
  // via the proof-sheet tile pick / studio button, so opening an old movie
  // never fans out surprise decode jobs). Debounced so a trim drag settles —
  // and autosave flushes the new window to the doc — before a decode is spent;
  // the guard sig includes builtAt, so even a build raced by a newer trim
  // converges on a follow-up rebuild instead of looping.
  const sdkPrevReqRef = useRef({});
  useEffect(() => {
    const sps = movie?.sdkPreviews;
    if (!id || !sps || !hydrated) return undefined;
    const timer = setTimeout(() => {
      for (const c of clips) {
        if (!c?.isRawVideo || !sps[c.id]) continue;
        if (sdkPreviewFor(sps, c, output)) continue; // still valid
        const rd = effectiveRedDecode(c, output);
        if (rd.pipeline === 'default') continue; // bake stays flat log
        const it = c.grade && c.grade.inputTransform;
        if (c.useGrade !== false && it && it !== 'srgb') continue; // flat-log grade
        const sig = JSON.stringify([c.inSec || 0, c.outSec || 0, rd, sps[c.id].builtAt || 0]);
        if (sdkPrevReqRef.current[c.id] === sig) continue;
        sdkPrevReqRef.current[c.id] = sig;
        queueSdkPreview(id, c.id).catch(() => { delete sdkPrevReqRef.current[c.id]; });
      }
    }, 4000);
    return () => clearTimeout(timer);
  }, [clips, movie?.sdkPreviews, output, id, hydrated]);

  const [picking, setPicking] = useState(false);
  const [pickingYt, setPickingYt] = useState(false);
  const [pickingClips, setPickingClips] = useState(false);
  // Modal targets for the viewer-soundtrack picker (separate from the channels).
  const [pickingViewer, setPickingViewer] = useState(false);
  const [pickingViewerYt, setPickingViewerYt] = useState(false);
  const [comparing, setComparing] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [clipExpanded, setClipExpanded] = useState(null);
  // Per-clip "full details" expander (right-panel clip list) — a Set of clip
  // ids whose parameter/metadata grid is open. Per-session UI state only.
  const [clipInfoOpen, setClipInfoOpen] = useState(() => new Set());
  const toggleClipInfo = (cid) => setClipInfoOpen((prev) => {
    const next = new Set(prev);
    if (next.has(cid)) next.delete(cid); else next.add(cid);
    return next;
  });
  // Per-tab panel visibility (right side panel + timeline lanes) so each stage
  // keeps its own layout — e.g. timeline-only on Assemble, full-width preview
  // on Reframe. Persisted per tab; defaults to everything visible.
  const [panelVis, setPanelVis] = useState(() => {
    const m = {};
    for (const t of PANEL_TABS) m[t] = readPanelVis(t);
    return m;
  });
  const togglePanel = (t, key) => setPanelVis((prev) => {
    const cur = { ...PANEL_DEFAULTS, ...(prev[t] || {}) };
    const next = { ...cur, [key]: !cur[key] };
    try { localStorage.setItem(`cs-panels-${t}`, JSON.stringify(next)); } catch { /* noop */ }
    return { ...prev, [t]: next };
  });
  const tabVis = { ...PANEL_DEFAULTS, ...(panelVis[tab] || {}) };
  // Toolbar slot the timeline portals its zoom + playhead readout into (so
  // they share the transport row instead of costing the timeline its own row).
  const [tlControlsEl, setTlControlsEl] = useState(null);
  // Master toggle for the speed-controls row (Speed math + Speed ramp chips):
  // hidden by default so the toolbar stays one slim line; persisted.
  const [speedRowOpen, setSpeedRowOpen] = useState(() => {
    try { return localStorage.getItem('cs_speedrow_open') === '1'; } catch { return false; }
  });
  const toggleSpeedRow = () => setSpeedRowOpen((prev) => {
    const next = !prev;
    try { localStorage.setItem('cs_speedrow_open', next ? '1' : '0'); } catch { /* noop */ }
    return next;
  });
  // iMovie-style timeline editing: the currently selected clip (for split /
  // delete / copy / speed) and a one-clip clipboard for copy/paste.
  const [selectedClipId, setSelectedClipId] = useState(null);
  // Selected music channel on the timeline (for inline trim / move / delete).
  // Mutually exclusive with the selected clip so toolbars don't fight.
  const [selectedSongId, setSelectedSongId] = useState(null);
  const [clipboard, setClipboard] = useState(null);
  // Full source-asset doc for the selected clip (for the "speed math" panel that
  // shows raw camera metadata + the true-speed derivation). Cached per asset.
  const [selAsset, setSelAsset] = useState(null);
  const assetDocCache = useRef({});
  // Per-clip embedded-audio waveform, keyed by assetId (shared across clips that
  // reuse the same footage). { hasAudio, peaks, status } — fed from the asset's
  // backend audio analysis and rendered as the timeline's clip-audio lane.
  const [clipAudio, setClipAudio] = useState({});
  const audioQueuedRef = useRef(new Set());
  // Save mode: autosave (debounced persist on every edit) vs manual (Save
  // button). Remembered across sessions.
  // Default to autosave so forgetting to hit Save can't lose work; users can
  // opt into Manual, which is remembered ('0').
  const [autosave, setAutosave] = useState(() => {
    try { return localStorage.getItem('cs_movie_autosave') !== '0'; } catch { return true; }
  });
  const setSaveMode = (on) => {
    setAutosave(on);
    try { localStorage.setItem('cs_movie_autosave', on ? '1' : '0'); } catch { /* ignore */ }
  };
  // Preview quality while editing: "smooth" plays the low-res edit proxy (snappy
  // scrubbing); "full" plays the delivery proxy/original (for color checks).
  const [editSmooth, setEditSmooth] = useState(() => {
    try { return localStorage.getItem('cs_movie_editsmooth') !== '0'; } catch { return true; }
  });
  const setPreviewQuality = (smooth) => {
    setEditSmooth(smooth);
    try { localStorage.setItem('cs_movie_editsmooth', smooth ? '1' : '0'); } catch { /* ignore */ }
  };
  const saveRef = useRef(null);
  // Undo / redo history of the whole editable composition. `past`/`future` hold
  // snapshots; `base` is the last-committed snapshot we diff against; `applying`
  // suppresses recording while an undo/redo restores state. histVer just forces a
  // re-render so the buttons' enabled state stays in sync.
  const historyRef = useRef({ past: [], future: [] });
  const histBaseRef = useRef(null);
  const histApplyingRef = useRef(false);
  const histTimer = useRef(null);
  // While > now(), the recorder treats state changes as programmatic (background
  // metadata backfills) and folds them into the baseline instead of recording an
  // undo step. Keeps "Undo" from reverting auto-repairs.
  const histSuppressRef = useRef(0);
  const [histVer, setHistVer] = useState(0);
  const autosaveTimer = useRef(null);
  const [previewTime, setPreviewTime] = useState(0);
  const [saved, setSaved] = useState(false);
  // Unsaved-changes tracking: armed after the first post-hydration render so the
  // initial state load doesn't count as an edit. Drives the leave-page guard and
  // the Save button's "unsaved" marker.
  const [dirty, setDirty] = useState(false);
  const dirtyArmedRef = useRef(false);
  const [savingErr, setSavingErr] = useState(null);
  const [clipBusy, setClipBusy] = useState(false);
  const [clipProgress, setClipProgress] = useState(0);
  const [clipError, setClipError] = useState(null);
  const [heroGrabBusy, setHeroGrabBusy] = useState(false);
  const [heroGrabErr, setHeroGrabErr] = useState(null);
  const [clipGrab, setClipGrab] = useState({ id: null, busy: false, err: null, result: null });
  // Rich frame-grab (FrameGrabber modal): grab a still from a clip with its live
  // color/light look + crop/zoom reframe controls, saved as an editable photo.
  const [grab, setGrab] = useState(null); // { clip, asset, startTime }
  const [grabLoading, setGrabLoading] = useState(null); // clipId while resolving
  const [grabErr, setGrabErr] = useState(null);
  // Proof strip → keyframes bridge state (see extractProofStrip below).
  const [proofBusy, setProofBusy] = useState(null); // clipId while working
  const [proofNote, setProofNote] = useState(null); // { clipId, msg, err }
  const [songUpBusy, setSongUpBusy] = useState(false);
  const [songUpErr, setSongUpErr] = useState(null);
  const songFileRef = useRef(null);
  const [viewerSongUpBusy, setViewerSongUpBusy] = useState(false);
  const [viewerSongUpErr, setViewerSongUpErr] = useState(null);
  const viewerSongFileRef = useRef(null);
  const [studioTime, setStudioTime] = useState(0);
  const studioSeekRef = useRef(null);
  const playerSeekRef = useRef(null);
  // Imperative play/pause handles + playing state for the shared timeline
  // transport (play / pause / frame-step), routed to whichever tab's player is
  // mounted. curTimeRef holds the freshest playhead time for frame stepping.
  const playerCtlRef = useRef(null);
  const colorCtlRef = useRef(null);
  const reframeCtlRef = useRef(null);
  const curTimeRef = useRef(0);
  const [transportPlaying, setTransportPlaying] = useState(false);
  // Last-known play/pause state, carried across tab switches alongside
  // curTimeRef: a remounting player resumes if the previous tab was playing
  // and stays paused if it was paused (no autoplay-on-entry).
  const playStateRef = useRef(false);
  const handlePlayingChange = (p) => { playStateRef.current = p; setTransportPlaying(p); };
  const fpsTried = useRef(new Set()); // clip ids we've already tried to measure fps for
  const durTried = useRef(new Set()); // clip ids we've already tried to measure duration for
  // Movie-wide grade, owned locally so the Color/Light tab previews edits live
  // (per-clip looks live on each clip in `clips`). Persisted debounced, never
  // auto-rendered — Publish is the only render trigger.
  const [gradeState, setGradeState] = useState(null);
  const clipGradeTimer = useRef(null);
  const movieGradeTimer = useRef(null);
  const clipReframeTimer = useRef(null);
  const clipThumbTimer = useRef(null);

  useEffect(() => {
    if (movie && !hydrated) {
      setMeta({
        title: movie.title || '', year: movie.year || '', location: movie.location || '',
        synopsis: movie.synopsis || '', categories: (movie.categories || []).join(', '),
        thumbUrl: movie.thumbUrl || '', heroUrl: movie.heroUrl || '', featured: !!movie.featured,
      });
      // Back-compat: seed a single clip from an existing videoUrl.
      let initialClips = Array.isArray(movie.clips) ? movie.clips : [];
      if (initialClips.length === 0 && (movie.videoUrl || movie.streamUrl)) {
        initialClips = [
          {
            id: makeClipId(),
            label: movie.title || 'Clip 1',
            url: movie.videoUrl || movie.streamUrl,
            inSec: 0,
            outSec: movie.durationSec || 0,
            sourceDurationSec: movie.durationSec || 0,
          },
        ];
      }
      setClips(initialClips);
      setGradeState(movie.grade || null);
    setSongs(movie.songs || []);
    setViewerSongs(movie.viewerSongs || []);
    setViewerSongDefault(movie.viewerSongDefault || null);
    setHasOriginalAudio(movie.hasOriginalAudio !== false);
      setIntro(movie.intro || 0);
      setOutro(movie.outro || 0);
      setOutput(movieOutput(movie));
      setHero({
        posterUrl: movie.hero?.posterUrl || '',
        delaySec: movie.hero?.delaySec == null ? 4 : movie.hero.delaySec,
        startSec: movie.hero?.startSec || 0,
        durationSec: movie.hero?.durationSec || 12,
      });
      setHydrated(true);
    }
  }, [movie, hydrated]);

  const movieDur = useMemo(() => clips.reduce((s, c) => s + clipLength(c), 0), [clips]);
  const hasRedClips = useMemo(() => clips.some((c) => c.isRawVideo), [clips]);
  // Reliable per-clip timeline thumbnails: one frame per clip pulled from the
  // composed master MP4. Used as the fallback in the timeline lane whenever a
  // clip's own proxy can't be grabbed client-side, so every block is populated.
  const thumbSegs = useMemo(() => masterSegments(clips), [clips]);
  // Server-baked timeline thumbnails (reliable; one JPEG per clip from the master
  // compose). Only fall back to in-browser master decoding for legacy masters
  // that predate server thumbs.
  const serverThumbs = movie?.master?.thumbs || {};
  const haveServerThumbs = Object.keys(serverThumbs).length > 0;
  const masterThumbUrl = !haveServerThumbs && movie?.master?.status === 'ready' ? (movie.master.url || '') : '';
  const { frames: clipMasterThumbs } = useMasterThumbs(masterThumbUrl, thumbSegs);
  const clipThumbs = useMemo(() => {
    const out = {};
    thumbSegs.forEach((s) => {
      const cid = s.clip.id;
      const url = serverThumbs[cid] || (clipMasterThumbs[cid] && clipMasterThumbs[cid].rawUrl) || '';
      out[cid] = { rawUrl: url, tileStart: s.tileStart, tileEnd: s.tileEnd };
    });
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverThumbs, clipMasterThumbs, thumbSegs]);
  const masterUrlForThumbs = movie?.master?.status === 'ready' ? (movie.master.url || '') : '';
  // Estimated processing cost of publishing at the chosen output quality. The
  // composite runs on the always-on task (~$0); the RAW pre-pass fans out to
  // ephemeral tasks that bill per-second, which is what this surfaces.
  const pubCost = useMemo(
    () => estimatePublishCost({ ...(movie || {}), durationSec: movie?.durationSec, output }, clips.filter((c) => c.isRawVideo)),
    [movie, output, clips]
  );

  // Live clock so the publish/preview ETAs update while a job is in flight.
  const rStatus = movie?.render?.status;
  const mStatus = movie?.master?.status;
  const now = useNow(
    rStatus === 'rendering' || rStatus === 'queued' ||
    mStatus === 'composing' || mStatus === 'queued',
  );
  const masterEtaSec = useMemo(() => estimateMasterSec(movieDur), [movieDur]);

  // Jump to the hero config when arriving via "Edit hero" (/.../edit#hero).
  useEffect(() => {
    if (hydrated && location.hash === '#hero') {
      const el = document.getElementById('hero');
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        el.classList.add('cs-flash');
        const t = setTimeout(() => el.classList.remove('cs-flash'), 1600);
        return () => clearTimeout(t);
      }
    }
    return undefined;
  }, [hydrated, location.hash]);

  const setM = (k) => (e) =>
    setMeta({ ...meta, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value });

  // ---- Clips --------------------------------------------------------------
  const patchClip = (cid, patch) => setClips((cs) => cs.map((c) => (c.id === cid ? { ...c, ...patch } : c)));
  // assetId → the asset's saved non-identity grade (null when it has none).
  // Filled by the once-per-clip asset sync + the add-clip path; feeds the
  // "Asset look available — Apply" affordance. Never applied automatically.
  const [assetLooks, setAssetLooks] = useState({});
  // Per-clip fade to black/white ({ sec, color } on `fadeIn`/`fadeOut`).
  // A duration of 0 clears the fade (stored as null, never undefined —
  // Firestore rejects undefined fields inside array elements).
  const patchClipFade = (cid, key, sec, color) => {
    const s = Math.max(0, Math.min(5, Number(sec) || 0));
    patchClip(cid, { [key]: s > 0 ? { sec: s, color: color === 'white' ? 'white' : 'black' } : null });
  };
  const removeClip = (cid) => setClips((cs) => cs.filter((c) => c.id !== cid));

  // Toggle a clip's baked look between its saved grade and raw. The published
  // render bakes per-clip looks; if a preview master already exists we re-queue
  // it so the Color tab's side-by-side reflects the change too.
  const setClipLook = (cid, useGrade) => {
    patchClip(cid, { useGrade });
  };

  // Live per-clip grading from the Color/Light tab. Updates local state for an
  // instant preview, persists debounced, and DOES NOT render — building the
  // master / publishing stays a deliberate, explicit action. `gradeLocked`
  // marks the look as deliberately set in THIS movie (vs copied from the asset).
  const onClipGrade = (cid, grade) => {
    setClips((cs) => {
      const next = cs.map((c) => (c.id === cid ? { ...c, grade, useGrade: true, gradeLocked: true } : c));
      if (clipGradeTimer.current) clearTimeout(clipGradeTimer.current);
      clipGradeTimer.current = setTimeout(() => updateMovie(id, { clips: next }).catch(() => {}), 700);
      return next;
    });
  };
  // Remove a clip's look entirely — back to the raw, ungraded source. One-click
  // repair for looks that were auto-copied from the asset by older versions of
  // the editor (nothing is ever auto-applied anymore — see assetLooks below).
  const clearClipLook = (cid) => {
    setClips((cs) => {
      const next = cs.map((c) => (c.id === cid ? { ...c, grade: null, useGrade: true, gradeLocked: true } : c));
      if (clipGradeTimer.current) clearTimeout(clipGradeTimer.current);
      clipGradeTimer.current = setTimeout(() => updateMovie(id, { clips: next }).catch(() => {}), 700);
      return next;
    });
  };
  // Deliberately copy the source asset's saved look onto a movie clip — the
  // explicit counterpart of the auto-seeding this editor used to do. Same
  // write path as manual grading (grade + useGrade + gradeLocked).
  const applyAssetLook = (cid) => {
    const clip = clips.find((c) => c.id === cid);
    const look = clip ? assetLooks[clip.assetId] : null;
    if (look) onClipGrade(cid, look);
  };
  const onMovieGrade = (grade) => {
    setGradeState(grade);
    if (movieGradeTimer.current) clearTimeout(movieGradeTimer.current);
    movieGradeTimer.current = setTimeout(() => updateMovie(id, { grade }).catch(() => {}), 700);
  };
  // Persist which frame represents a clip on the timeline (0..1 within its trim).
  // Debounced like the grade/reframe writers so dragging the playhead and tapping
  // "set thumb" doesn't spam Firestore.
  const setClipThumb = (cid, frac) => {
    setClips((cs) => {
      const next = cs.map((c) => (c.id === cid ? { ...c, thumbFrac: Math.max(0, Math.min(1, frac)) } : c));
      if (clipThumbTimer.current) clearTimeout(clipThumbTimer.current);
      clipThumbTimer.current = setTimeout(() => updateMovie(id, { clips: next }).catch(() => {}), 700);
      return next;
    });
  };
  // Per-clip reframe/crop from the Reframe tab. Like onClipGrade: instant local
  // preview + debounced persist, no render. Each clip carries its own crop window
  // (ratio is chosen per export; center/zoom/subject are per clip).
  const onClipReframe = (cid, reframe) => {
    setClips((cs) => {
      const next = cs.map((c) => (c.id === cid ? { ...c, reframe } : c));
      if (clipReframeTimer.current) clearTimeout(clipReframeTimer.current);
      clipReframeTimer.current = setTimeout(() => updateMovie(id, { clips: next }).catch(() => {}), 700);
      return next;
    });
  };
  useEffect(() => () => {
    if (clipGradeTimer.current) clearTimeout(clipGradeTimer.current);
    if (movieGradeTimer.current) clearTimeout(movieGradeTimer.current);
    if (clipReframeTimer.current) clearTimeout(clipReframeTimer.current);
  }, []);
  const moveClip = (idx, dir) =>
    setClips((cs) => {
      const j = idx + dir;
      if (j < 0 || j >= cs.length) return cs;
      const next = cs.slice();
      [next[idx], next[j]] = [next[j], next[idx]];
      return next;
    });

  // ---- iMovie-style timeline editing -------------------------------------
  // Split the clip under the playhead into two adjacent subclips at the source
  // frame the playhead sits on (so the two halves play seamlessly). The cut
  // point is converted from movie-time to source-time through the clip's speed.
  const splitAtPlayhead = () => {
    const t = curTimeRef.current || 0;
    let acc = 0;
    for (let i = 0; i < clips.length; i += 1) {
      const c = clips[i];
      const len = clipLength(c);
      if (t > acc + 0.05 && t < acc + len - 0.05) {
        const inn = c.inSec || 0;
        const out = c.outSec && c.outSec > 0 ? c.outSec : c.sourceDurationSec || 0;
        const srcSplit = inn + (t - acc) * clipSpeed(c);
        if (!(srcSplit > inn + 0.05) || !(srcSplit < out - 0.05)) return;
        const left = { ...c, outSec: srcSplit };
        const right = { ...c, id: makeClipId(), inSec: srcSplit, outSec: out, dissolveSec: 0 };
        const next = clips.slice();
        next.splice(i, 1, left, right);
        setClips(next);
        setSelectedClipId(right.id);
        return;
      }
      acc += len;
    }
  };
  const duplicateClip = (cid) => {
    const i = clips.findIndex((c) => c.id === cid);
    if (i < 0) return;
    const copy = { ...clips[i], id: makeClipId(), label: `${clips[i].label || 'Clip'} copy` };
    const next = clips.slice();
    next.splice(i + 1, 0, copy);
    setClips(next);
    setSelectedClipId(copy.id);
  };
  const copyClip = (cid) => {
    const c = clips.find((x) => x.id === cid);
    if (c) setClipboard({ ...c });
  };
  const pasteClip = () => {
    if (!clipboard) return;
    const copy = { ...clipboard, id: makeClipId(), label: `${clipboard.label || 'Clip'} copy` };
    const i = selectedClipId ? clips.findIndex((c) => c.id === selectedClipId) : clips.length - 1;
    const at = (i < 0 ? clips.length - 1 : i) + 1;
    const next = clips.slice();
    next.splice(at, 0, copy);
    setClips(next);
    setSelectedClipId(copy.id);
  };
  const deleteClip = (cid) => {
    if (!cid) return;
    removeClip(cid);
    if (clipExpanded === cid) setClipExpanded(null);
    if (selectedClipId === cid) setSelectedClipId(null);
  };
  // Drag-reorder from the timeline. `insert` is the boundary index (0..len) the
  // clip was dropped at; convert it to a destination index accounting for the
  // removal of the dragged clip.
  const reorderClips = (from, insert) => {
    setClips((cs) => {
      if (from < 0 || from >= cs.length) return cs;
      let to = insert > from ? insert - 1 : insert;
      to = Math.max(0, Math.min(cs.length - 1, to));
      if (to === from) return cs;
      const next = cs.slice();
      const [m] = next.splice(from, 1);
      next.splice(to, 0, m);
      return next;
    });
  };

  const measureClip = async (cid, s3Key) => {
    try {
      const url = await getAssetPlaybackUrl(s3Key);
      const d = await readMediaDurationUrl(url);
      if (d) patchClip(cid, { sourceDurationSec: d, outSec: d });
      const fps = await estimateFps(url);
      if (fps) {
        fpsTried.current.add(cid);
        patchClip(cid, { fps });
      }
    } catch {
      /* leave unmeasured */
    }
  };

  // Backfill fps for existing clips (older movies) that never measured it.
  // Best-effort: skip HLS/RED proxies (a bare <video> can't decode them in
  // Chrome) and anything already tried. Runs in the background; never blocks.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      for (const c of clips) {
        if (cancelled) break;
        if (c.fps || fpsTried.current.has(c.id)) continue;
        if (c.streamType === 'hls' || c.isRawVideo) { fpsTried.current.add(c.id); continue; }
        fpsTried.current.add(c.id);
        try {
          // eslint-disable-next-line no-await-in-loop
          const url = c.s3Key ? await getAssetPlaybackUrl(c.s3Key) : httpsify(c.url);
          if (!url) continue;
          // eslint-disable-next-line no-await-in-loop
          const fps = await estimateFps(url);
          if (fps && !cancelled) patchClip(c.id, { fps });
        } catch {
          /* leave unmeasured — readout falls back to ≈30 fps */
        }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clips]);

  // Once-per-clip sync from the source asset doc. Two jobs:
  //  1) Repair RED clips added before their proxy finished decoding — they were
  //     frozen with sourceDurationSec=0 (so they occupy 0 movie-time and the
  //     playhead can't enter them). Backfill the now-known duration + proxy URL.
  //  2) Note which source assets carry a saved color/light look so the UI can
  //     OFFER it ("Asset look available — Apply"). It is never auto-applied:
  //     silently copying the asset grade onto ungraded clips double-graded
  //     footage whose look is already baked into the file's pixels.
  // Runs in the background; never blocks.
  const clipSyncTried = useRef(new Set());
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const patches = new Map(); // clip id -> patch
      // Did we correct anything that changes the baked composition (clip length,
      // playable source, or per-clip look)? If so we must persist it and rebuild
      // the preview master — otherwise the Color/Light tab keeps playing a stale
      // master that was composed before the RED clip had a real duration, and the
      // playhead can't scrub into that (missing) region.
      let compositionChanged = false;
      for (const c of clips) {
        if (cancelled) break;
        if (!c.assetId || !c.tripId) continue;
        // Any clip frozen at length 0 needs its duration backfilled from the
        // asset doc — not just RED. Non-RED HLS "stream" proxies hit this too:
        // their .m3u8 isn't readable by a bare <video>, so measureClip leaves
        // them at 0 (a sliver you can't scrub into).
        const needsDur = !(c.sourceDurationSec > 0);
        const needsUrl = !c.url && !c.s3Key;
        // Slow-mo "real motion" speed stat — backfill for RED clips added before
        // we tracked it (display-only; doesn't change the composition).
        const needsReal = c.isRawVideo && !(c.realSpeedX > 0);
        // Lightweight edit-proxy URLs — backfill when missing AND refresh when
        // the asset's proxies were rebuilt (e.g. the 540p→1080p edit-proxy
        // backfill): clip docs stamp these URLs at add-time, so without this
        // one asset-doc check per session they'd scrub the stale proxy forever.
        const needsEdit = true;
        // RED provenance flag — clips added before isRawVideo was stamped onto
        // movie clips lack it, which hides the Color tab's RED decode controls
        // and undercounts the RAW fan-out in publish cost estimates.
        const needsRed = !c.isRawVideo;
        if (!needsDur && !needsUrl && !needsReal && !needsEdit && !needsRed) continue;
        if (clipSyncTried.current.has(c.id)) continue;
        clipSyncTried.current.add(c.id);
        try {
          // eslint-disable-next-line no-await-in-loop
          const a = await getAssetDoc(c.tripId, c.assetId);
          if (!a || cancelled) continue;
          // Legacy MP4 proxies were stored as a separate sibling asset linked via
          // vproxy.proxyAssetId, leaving proxyHlsUrl/editMp4Url empty on this asset.
          // Resolve from the sibling so old uploads self-heal (newer proxies write
          // the URLs inline and skip this).
          let proxyHls = a.proxyHlsUrl;
          let editMp4 = a.editMp4Url;
          let editHls = a.editProxyUrl;
          if (!proxyHls && a.vproxy?.proxyAssetId) {
            // eslint-disable-next-line no-await-in-loop
            const p = await getAssetDoc(c.tripId, a.vproxy.proxyAssetId);
            if (p) {
              proxyHls = p.proxyHlsUrl || proxyHls;
              editMp4 = editMp4 || p.editMp4Url;
              editHls = editHls || p.editProxyUrl;
            }
          }
          const dur =
            a.durationSec || a.duration || a.metadata?.durationSec || a.metadata?.video?.duration || 0;
          const patch = {};
          if (needsDur && dur > 0) {
            patch.sourceDurationSec = dur;
            if (!(c.outSec > 0)) patch.outSec = dur;
            compositionChanged = true;
          }
          if (needsUrl && proxyHls) {
            patch.url = proxyHls;
            patch.streamType = 'hls';
            compositionChanged = true;
          }
          // Record whether the asset carries a saved non-identity look. NEVER
          // auto-apply it to the clip — the UI offers it as an explicit
          // "Asset look available — Apply" action instead (silently seeding it
          // double-graded footage whose look is already baked into the pixels).
          {
            const assetSig = a.grade ? gradeSignature(a.grade) : IDENTITY_GRADE_SIG;
            const savedLook = assetSig !== IDENTITY_GRADE_SIG ? a.grade : null;
            setAssetLooks((m) => (m[c.assetId] !== undefined ? m : { ...m, [c.assetId]: savedLook }));
          }
          if (needsReal) {
            const rx = assetRealSpeedX(a);
            if (rx > 0) patch.realSpeedX = rx; // display stat only — no recompose
          }
          if (needsRed && a.isRawVideo) {
            patch.isRawVideo = true; // display/controls flag only — no recompose
          }
          if (needsEdit) {
            // editor-only sources; no recompose — replace on ANY difference so
            // rebuilt proxies (new immutable prefixes) supersede stale clip URLs
            if (editHls && c.editUrl !== editHls) patch.editUrl = editHls;
            if (editMp4 && c.editMp4Url !== editMp4) patch.editMp4Url = editMp4;
          }
          if (Object.keys(patch).length) {
            patches.set(c.id, patch);
            histSuppressRef.current = Date.now() + 1500; // don't record auto-repairs as undo steps
            patchClip(c.id, patch);
          }
        } catch {
          /* leave as-is; user can re-add the clip once the proxy is ready */
        }
      }
      if (cancelled || !compositionChanged || patches.size === 0) return;
      // Persist the repaired composition so the studio (which reads the saved
      // movie doc, not local edits) sees the real clip duration, and re-queue
      // the master so the Color/Light preview is recomposed with the RED clip.
      const repaired = clips.map((c) =>
        patches.has(c.id) ? { ...c, ...patches.get(c.id) } : c
      );
      try {
        await updateMovie(id, { clips: repaired, master: { status: 'queued' } });
      } catch {
        /* best-effort; the manual Save / republish path still recovers this */
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clips]);

  // Load the selected clip's full source asset (raw camera metadata) for the
  // "speed math" panel. Cached per asset so reselecting is instant.
  useEffect(() => {
    const c = clips.find((x) => x.id === selectedClipId);
    if (!c || !c.assetId || !c.tripId) { setSelAsset(null); return undefined; }
    const cached = assetDocCache.current[c.assetId];
    if (cached !== undefined) { setSelAsset(cached); return undefined; }
    let cancelled = false;
    getAssetDoc(c.tripId, c.assetId)
      .then((a) => {
        assetDocCache.current[c.assetId] = a || null;
        if (!cancelled) setSelAsset(a || null);
      })
      .catch(() => { if (!cancelled) setSelAsset(null); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClipId]);

  // Subscribe to each clip's source asset for its embedded-audio waveform, and
  // kick off backend analysis the first time we see an un-analyzed video asset.
  // Keyed on the distinct (tripId, assetId) pairs so it only re-wires when the
  // set of source assets changes — not on every grade/trim tweak.
  const audioAssetKey = useMemo(() => {
    const seen = new Set();
    const pairs = [];
    for (const c of clips) {
      if (!c.assetId || !c.tripId) continue;
      const k = `${c.tripId}/${c.assetId}`;
      if (seen.has(k)) continue;
      seen.add(k);
      pairs.push({ tripId: c.tripId, assetId: c.assetId });
    }
    return pairs;
  }, [clips]);
  useEffect(() => {
    if (!audioAssetKey.length) return undefined;
    const unsubs = audioAssetKey.map(({ tripId, assetId }) =>
      subscribeAsset(tripId, assetId, (a) => {
        if (!a) return;
        const status = a.audioWaveform?.status || 'none';
        // Whether the source carries an audio stream at all. ffprobe (the meta
        // worker) records metadata.audio for regular video; RED isn't probed so
        // it stays unknown (null) until the user runs analysis on its proxy.
        const audioMeta = a.metadata?.audio ? true : (a.metadata?.probedAt ? false : null);
        const hasAudio = typeof a.hasAudio === 'boolean' ? a.hasAudio : audioMeta === true;
        const peaks = Array.isArray(a.audioPeaks) ? a.audioPeaks : [];
        setClipAudio((prev) => {
          const cur = prev[assetId];
          if (cur && cur.status === status && cur.hasAudio === hasAudio && cur.audioMeta === audioMeta && cur.peaks === peaks) return prev;
          return { ...prev, [assetId]: { hasAudio, audioMeta, peaks, status, isRawVideo: !!a.isRawVideo } };
        });
      })
    );
    return () => unsubs.forEach((u) => { try { u(); } catch { /* noop */ } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audioAssetKey]);

  // Waveform analysis is an on-demand service (it decodes audio with ffmpeg on
  // the backend, so it can take a moment) — trigger it per clip when wanted.
  const analyzeClipAudio = (assetId) => {
    const c = clips.find((x) => x.assetId === assetId);
    if (!c || !c.tripId || !assetId) return;
    audioQueuedRef.current.add(assetId);
    setClipAudio((prev) => ({ ...prev, [assetId]: { ...(prev[assetId] || {}), status: 'queued' } }));
    requestAssetWaveform(c.tripId, assetId).catch(() => {});
  };
  // Fire analysis for every clip whose audio hasn't been looked at yet (skips
  // ones already analyzed / queued / known to have no audio track).
  const analyzeAllClipAudio = () => {
    const seen = new Set();
    clips.forEach((c) => {
      if (!c.assetId || !c.tripId || seen.has(c.assetId)) return;
      seen.add(c.assetId);
      const ca = clipAudio[c.assetId];
      if (ca && (ca.status === 'ready' || ca.status === 'queued' || ca.status === 'analyzing')) return;
      if (ca && ca.audioMeta === false) return; // ffprobe already said no audio
      analyzeClipAudio(c.assetId);
    });
  };
  // Extract a song's waveform on demand. Uploaded / URL-backed songs decode in
  // the browser (instant, no backend); library songs without a URL fall back to
  // the backend waveform worker.
  const analyzeSong = async (song) => {
    if (!song) return;
    if (song.url) {
      patchSong(song.id, { waveform: { status: 'analyzing', startedAt: Date.now() } });
      try {
        const resp = await fetch(httpsify(song.url));
        const buf = await resp.arrayBuffer();
        const { peaks, durationSec } = await peaksFromAudioFile(buf);
        if (peaks && peaks.length) {
          patchSong(song.id, {
            peaks,
            songDurationSec: song.songDurationSec || durationSec || 0,
            waveform: { status: 'ready', finishedAt: Date.now() },
          });
        } else {
          patchSong(song.id, { waveform: { status: 'error', message: 'could not decode audio' } });
        }
      } catch (e) {
        patchSong(song.id, { waveform: { status: 'error', message: String(e.message || e).slice(0, 200) } });
      }
      return;
    }
    if (song.libraryId) {
      patchSong(song.id, { waveform: { status: 'queued', requestedAt: Date.now() } });
      requestWaveform(song.libraryId).catch(() => {});
    }
  };

  // Keyboard transport: Space = play/pause, ←/→ = frame step. Skipped while
  // typing in a field or focused on a button (so it never double-fires). The
  // live handlers are read through a ref so this listener stays stable.
  const tabRef = useRef(tab);
  tabRef.current = tab;
  const transportApiRef = useRef({});
  // Live handles for the iMovie-style clip-edit shortcuts, read through a ref so
  // the global keydown listener stays stable while always seeing fresh state.
  const editApiRef = useRef({});
  useEffect(() => {
    const onKey = (e) => {
      if (tabRef.current === 'details') return;
      const el = document.activeElement;
      const tag = el && el.tagName;
      const typing = tag === 'INPUT' || tag === 'TEXTAREA' || (el && el.isContentEditable);
      if (typing) return;

      // Undo / redo — available on every editing tab. ⌘Z / Ctrl+Z to undo,
      // ⌘⇧Z / Ctrl+Y to redo.
      const modk = e.metaKey || e.ctrlKey;
      if (modk && (e.key === 'z' || e.key === 'Z')) {
        e.preventDefault();
        if (e.shiftKey) editApiRef.current.redo?.(); else editApiRef.current.undo?.();
        return;
      }
      if (modk && (e.key === 'y' || e.key === 'Y')) { e.preventDefault(); editApiRef.current.redo?.(); return; }

      // Clip-editing shortcuts (Assemble tab) — split / delete / copy / paste /
      // duplicate. These act on the selected clip (or the playhead for split).
      if (tabRef.current === 'assemble') {
        const mod = e.metaKey || e.ctrlKey;
        const api = editApiRef.current;
        if (mod && (e.key === 'b' || e.key === 'B')) { e.preventDefault(); api.split?.(); return; }
        if (mod && (e.key === 'c' || e.key === 'C')) { if (api.canCopy?.()) { e.preventDefault(); api.copy?.(); } return; }
        if (mod && (e.key === 'v' || e.key === 'V')) { if (api.canPaste?.()) { e.preventDefault(); api.paste?.(); } return; }
        if (mod && (e.key === 'd' || e.key === 'D')) { if (api.canCopy?.()) { e.preventDefault(); api.duplicate?.(); } return; }
        if ((e.key === 'Delete' || e.key === 'Backspace') && api.canDelete?.()) { e.preventDefault(); api.del?.(); return; }
      }

      // Transport (all tabs except details). Skip when a button has focus so
      // Space doesn't also activate the focused button.
      if (tag === 'BUTTON') return;
      if (e.code === 'Space') { e.preventDefault(); transportApiRef.current.toggle?.(); }
      else if (e.code === 'ArrowLeft') { e.preventDefault(); transportApiRef.current.step?.(-1, e.shiftKey ? 10 : 1); }
      else if (e.code === 'ArrowRight') { e.preventDefault(); transportApiRef.current.step?.(1, e.shiftKey ? 10 : 1); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Autosave: when enabled, debounce-persist the whole working state on any edit
  // (clips, trims, speed, order, songs, fades, output, metadata). Manual mode
  // leaves saving to the Save button. `save` is read through a ref so this effect
  // doesn't need it in deps. Skipped until the editor has hydrated.
  useEffect(() => {
    if (!autosave || !hydrated) return undefined;
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    autosaveTimer.current = setTimeout(() => { saveRef.current?.(); }, 900);
    return () => { if (autosaveTimer.current) clearTimeout(autosaveTimer.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autosave, hydrated, clips, songs, viewerSongs, viewerSongDefault, hasOriginalAudio, intro, outro, output, hero, meta, gradeState]);

  // Mark the editor dirty on any edit to a persisted field (same surface as
  // autosave). Skips the first render after hydration so loading isn't "dirty".
  useEffect(() => {
    if (!hydrated) return;
    if (!dirtyArmedRef.current) { dirtyArmedRef.current = true; return; }
    setDirty(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hydrated, clips, songs, viewerSongs, viewerSongDefault, hasOriginalAudio, intro, outro, output, hero, meta, gradeState]);

  // Guard against losing work: warn before unloading the tab while edits are
  // unsaved (covers Manual mode and the brief autosave debounce window).
  useEffect(() => {
    const onBeforeUnload = (e) => {
      if (!dirty) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [dirty]);

  // ---- Undo / redo --------------------------------------------------------
  // A snapshot of the whole editable composition. State is treated immutably
  // everywhere, so storing references (not deep clones) is safe.
  const snapshot = () => ({
    clips, songs, viewerSongs, viewerSongDefault, hasOriginalAudio,
    intro, outro, output, hero, meta, gradeState,
  });
  const applySnapshot = (s) => {
    if (!s) return;
    histApplyingRef.current = true;
    if (histTimer.current) { clearTimeout(histTimer.current); histTimer.current = null; }
    setClips(s.clips);
    setSongs(s.songs);
    setViewerSongs(s.viewerSongs);
    setViewerSongDefault(s.viewerSongDefault);
    setHasOriginalAudio(s.hasOriginalAudio);
    setIntro(s.intro);
    setOutro(s.outro);
    setOutput(s.output);
    setHero(s.hero);
    setMeta(s.meta);
    setGradeState(s.gradeState);
    histBaseRef.current = s;
  };
  // Record history. Coalesces rapid edits (e.g. slider drags) into one step via
  // a short debounce. The first hydrated render seeds the baseline without
  // recording. Restores triggered by undo/redo are skipped.
  useEffect(() => {
    if (!hydrated) return undefined;
    if (histBaseRef.current === null) { histBaseRef.current = snapshot(); return undefined; }
    if (histApplyingRef.current) { histApplyingRef.current = false; histBaseRef.current = snapshot(); return undefined; }
    if (Date.now() < histSuppressRef.current) { histBaseRef.current = snapshot(); return undefined; }
    if (histTimer.current) clearTimeout(histTimer.current);
    histTimer.current = setTimeout(() => {
      const h = historyRef.current;
      h.past.push(histBaseRef.current);
      if (h.past.length > 100) h.past.shift();
      h.future = [];
      histBaseRef.current = snapshot();
      histTimer.current = null;
      setHistVer((v) => v + 1);
    }, 500);
    return () => { if (histTimer.current) clearTimeout(histTimer.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hydrated, clips, songs, viewerSongs, viewerSongDefault, hasOriginalAudio, intro, outro, output, hero, meta, gradeState]);

  const undo = () => {
    const h = historyRef.current;
    // Flush any pending coalesced edit into history first so a quick edit→undo
    // still has something to undo.
    if (histTimer.current) {
      clearTimeout(histTimer.current);
      histTimer.current = null;
      h.past.push(histBaseRef.current);
      h.future = [];
      histBaseRef.current = snapshot();
    }
    if (!h.past.length) return;
    h.future.push(snapshot());
    applySnapshot(h.past.pop());
    setHistVer((v) => v + 1);
  };
  const redo = () => {
    const h = historyRef.current;
    if (!h.future.length) return;
    h.past.push(snapshot());
    applySnapshot(h.future.pop());
    setHistVer((v) => v + 1);
  };
  // `histVer` is read here only so React re-derives these flags whenever history
  // changes (it's bumped on every record / undo / redo).
  const canUndo = histVer >= 0 && (historyRef.current.past.length > 0 || histTimer.current != null);
  const canRedo = historyRef.current.future.length > 0;

  // DaVinci Resolve round-trip + headless "pro finish" request state. These must
  // stay above the guards below so the hook order never changes between renders.
  const [resolveExp, setResolveExp] = useState({ busy: false, err: null });
  const [proFinish, setProFinish] = useState({ busy: false, err: null });
  // Edit versions: live list of saved snapshots (every publish auto-saves one;
  // the panel below Publish can save/restore/delete them).
  const editVersions = useEditVersions(id);
  const [versionBusy, setVersionBusy] = useState('');

  // ---- Version cherry-pick handlers (MovieVersionsPanel) -------------------
  // Pulls land in the normal working state (setClips/setSongs/…) so undo and
  // autosave behave exactly like hand edits. The clip's stabilization/reframe
  // analysis doc copies alongside it (keyed by clip id, stored per movie).
  const pullClipFromVersion = (srcMovieId, clip) => {
    if (!clip) return;
    setClips((l) => {
      const i = l.findIndex((c) => c.id === clip.id);
      if (i === -1) return [...l, clip];
      const copy = [...l];
      copy[i] = clip;
      return copy;
    });
    if (srcMovieId) copyClipReframeDoc(srcMovieId, id, clip.id).catch(() => {});
  };
  const adoptSongsFromVersion = (other) => setSongs(other?.songs || []);
  const adoptGradeFromVersion = (other) => onMovieGrade(other?.grade || null);
  const adoptOutputFromVersion = (other) => setOutput(movieOutput(other || {}));

  // Guard AFTER all hooks so hook order stays stable (React requires hooks to
  // run unconditionally on every render).
  if (movie === undefined) return <div className="cs-page">Loading…</div>;
  if (movie === null) return <div className="cs-page">Movie not found.</div>;

  const addFootageClips = (assets) => {
    setPickingClips(false);
    const list = Array.isArray(assets) ? assets : [assets];
    const newClips = list.map((a) => {
      // Note any look the clip was graded with (saved on the asset) so the
      // Color tab can OFFER it — but never auto-apply it: the grade may already
      // be baked into the file's pixels (externally graded uploads), and
      // applying it silently double-grades. New clips always start raw.
      const savedGrade = a.grade && gradeSignature(a.grade) !== IDENTITY_GRADE_SIG ? a.grade : null;
      if (savedGrade) setAssetLooks((m) => ({ ...m, [a.id]: savedGrade }));
      const look = { gradeAssetId: a.id };
      // Slow-mo footage: the speed-up that plays it at real-life motion, kept on
      // the clip so the timeline can show "real motion ＝ N×" next to speed.
      const realX = assetRealSpeedX(a);
      const realLook = realX > 0 ? { realSpeedX: realX } : {};
      // Lightweight edit proxies for snappy scrubbing — editor-only; render/
      // publish keep using `url`/`s3Key` (originals/full proxy). The MP4 is a
      // single progressive file the editor fully prefetches (zero buffering);
      // the HLS proxy is the fallback when no MP4 has been generated yet.
      const editLook = {
        ...(a.editProxyUrl ? { editUrl: a.editProxyUrl } : {}),
        ...(a.editMp4Url ? { editMp4Url: a.editMp4Url } : {}),
      };
      // RED R3D footage isn't directly readable — edit against its HLS proxy.
      // Its duration is already known from the proxy, so no measure pass.
      if (a.isRawVideo) {
        const dur = a.durationSec || 0;
        return {
          id: makeClipId(),
          label: a.name || 'Clip',
          tripId: a.tripId,
          assetId: a.id,
          url: a.proxyHlsUrl || '',
          streamType: 'hls',
          isRawVideo: true,
          inSec: 0,
          outSec: dur,
          sourceDurationSec: dur,
          ...realLook,
          ...editLook,
          ...look,
        };
      }
      // Non-RED footage. Seed the duration from the asset doc up front so the
      // timeline has the right length immediately — measureClip then refines it
      // (and fps). This matters for HLS "stream" proxies whose .m3u8 a bare
      // <video> can't read, which previously left the clip at length 0 (a sliver
      // you couldn't scrub into).
      const dur =
        a.durationSec || a.duration || a.metadata?.durationSec || a.metadata?.video?.duration || 0;
      const base = {
        id: makeClipId(),
        label: a.name || 'Clip',
        tripId: a.tripId,
        assetId: a.id,
        inSec: 0,
        outSec: dur,
        sourceDurationSec: dur,
        ...realLook,
        ...editLook,
        ...look,
      };
      // Prefer the raw key when present (progressive MP4, fully measurable);
      // otherwise edit/play against the HLS proxy stream.
      if (a.s3Key) return { ...base, s3Key: a.s3Key };
      return { ...base, url: a.proxyHlsUrl || '', streamType: 'hls' };
    });
    setClips((cs) => [...cs, ...newClips]);
    newClips.forEach((c) => (c.s3Key ? measureClip(c.id, c.s3Key) : null));
  };

  const uploadClipFile = async (file) => {
    if (!file) return;
    setClipError(null);
    setClipBusy(true);
    setClipProgress(0);
    try {
      const objUrl = URL.createObjectURL(file);
      const durationSec = await readFileDuration(file);
      const fps = await estimateFps(objUrl).catch(() => 0);
      URL.revokeObjectURL(objUrl);
      const key = buildMovieKey(id, 'clips/', file.name);
      await uploadFile(file, {
        key,
        contentType: file.type || 'video/mp4',
        bucket: 'movies',
        onProgress: setClipProgress,
      });
      const newId = makeClipId();
      if (fps) fpsTried.current.add(newId);
      setClips((cs) => [
        ...cs,
        {
          id: newId,
          label: file.name,
          url: cdnUrl(key),
          inSec: 0,
          outSec: durationSec || 0,
          sourceDurationSec: durationSec || 0,
          ...(fps ? { fps } : {}),
        },
      ]);
    } catch (err) {
      setClipError(err.message || String(err));
    } finally {
      setClipBusy(false);
      setClipProgress(0);
    }
  };

  // ---- Songs --------------------------------------------------------------
  const patchSong = (sid, patch) => setSongs((l) => l.map((s) => (s.id === sid ? { ...s, ...patch } : s)));
  const removeSong = (sid) => setSongs((l) => l.filter((s) => s.id !== sid));
  const attachFromLibrary = (libSong) => {
    const base = {
      id: makeSongId(),
      label: libSong.label,
      libraryId: libSong.id,
      songDurationSec: libSong.durationSec || 0,
      peaks: libSong.peaks || [],
      waveform: libSong.waveform || { status: (libSong.peaks || []).length ? 'ready' : 'none' },
      trimStart: 0,
      trimEnd: libSong.durationSec || 0,
      movieOffset: 0,
      volume: 1,
      fadeInSec: 0,
      fadeOutSec: 0,
    };
    const song =
      libSong.source === 'youtube'
        ? { ...base, source: 'youtube', videoId: libSong.videoId, thumb: libSong.thumb || '' }
        : { ...base, source: 'upload', url: libSong.url };
    setSongs((l) => [...l, song]);
    setPicking(false);
  };
  const attachYouTube = (r) => {
    setSongs((l) => [
      ...l,
      { id: makeSongId(), source: 'youtube', label: r.title, videoId: r.videoId, thumb: r.thumb || '', libraryId: null, songDurationSec: r.durationSec || 0, trimStart: 0, trimEnd: r.durationSec || 0, movieOffset: 0, volume: 1 },
    ]);
    setPickingYt(false);
  };
  // ---- Viewer-selectable soundtracks (max 4) ------------------------------
  const VIEWER_MAX = 4;
  const buildSong = (src) => {
    const base = {
      id: makeSongId(),
      label: src.label,
      songDurationSec: src.songDurationSec || src.durationSec || 0,
      trimStart: 0,
      trimEnd: src.songDurationSec || src.durationSec || 0,
      movieOffset: 0,
      volume: 1,
      fadeInSec: 0,
      fadeOutSec: 0,
      ...(src.peaks && src.peaks.length ? { peaks: src.peaks } : {}),
      ...(src.waveform ? { waveform: src.waveform } : {}),
    };
    return src.source === 'youtube'
      ? { ...base, source: 'youtube', videoId: src.videoId, thumb: src.thumb || '' }
      : { ...base, source: 'upload', url: src.url };
  };
  const renameViewerSong = (sid, label) =>
    setViewerSongs((l) => l.map((s) => (s.id === sid ? { ...s, label } : s)));
  const removeViewerSong = (sid) => {
    setViewerSongs((l) => l.filter((s) => s.id !== sid));
    setViewerSongDefault((d) => (d === sid ? null : d));
  };
  // Copy a viewer song onto the baked timeline lane, where trim/drag/loop work.
  const viewerSongToTimeline = (s) => {
    const copy = { ...buildSong(s), label: s.label, movieOffset: 0 };
    setSongs((l) => [...l, copy]);
    setTab('assemble');
    setSelectedSongId(copy.id);
    setSelectedClipId(null);
  };
  const addViewerFromLibrary = (libSong) => {
    setPickingViewer(false);
    setViewerSongs((l) => (l.length >= VIEWER_MAX ? l : [...l, buildSong(libSong)]));
  };
  const addViewerYouTube = (r) => {
    setPickingViewerYt(false);
    setViewerSongs((l) =>
      l.length >= VIEWER_MAX ? l : [...l, buildSong({ ...r, source: 'youtube', label: r.title })]
    );
  };
  const uploadViewerSong = async (e) => {
    const file = e.target.files?.[0];
    if (e.target) e.target.value = '';
    if (!file || viewerSongs.length >= VIEWER_MAX) return;
    setViewerSongUpErr(null);
    setViewerSongUpBusy(true);
    try {
      const durationSec = await readFileDuration(file).catch(() => 0);
      const { peaks, durationSec: decDur } = await peaksFromAudioFile(file).catch(() => ({ peaks: null, durationSec: 0 }));
      const dur = durationSec || decDur || 0;
      const key = buildMovieKey(id, 'audio/', file.name);
      await uploadFile(file, { key, contentType: file.type || 'audio/mpeg', bucket: 'movies' });
      setViewerSongs((l) => [
        ...l,
        buildSong({
          source: 'upload',
          label: file.name.replace(/\.[^.]+$/, ''),
          url: cdnUrl(key),
          songDurationSec: dur,
          peaks: peaks && peaks.length ? peaks : [],
          waveform: { status: peaks && peaks.length ? 'ready' : 'none' },
        }),
      ]);
    } catch (err) {
      setViewerSongUpErr(err.message || String(err));
    } finally {
      setViewerSongUpBusy(false);
    }
  };

  // Upload an audio file straight onto a new channel (movies bucket).
  const uploadSong = async (e) => {
    const file = e.target.files?.[0];
    if (e.target) e.target.value = '';
    if (!file) return;
    setSongUpErr(null);
    setSongUpBusy(true);
    try {
      const durationSec = await readFileDuration(file).catch(() => 0);
      // Decode the waveform locally (we hold the File) so the music lane shows a
      // real waveform immediately — songs added straight to a movie never enter
      // the library collection the backend waveform worker watches.
      const { peaks, durationSec: decDur } = await peaksFromAudioFile(file).catch(() => ({ peaks: null, durationSec: 0 }));
      const dur = durationSec || decDur || 0;
      const key = buildMovieKey(id, 'audio/', file.name);
      await uploadFile(file, { key, contentType: file.type || 'audio/mpeg', bucket: 'movies' });
      setSongs((l) => [
        ...l,
        {
          id: makeSongId(),
          source: 'upload',
          label: file.name.replace(/\.[^.]+$/, ''),
          url: cdnUrl(key),
          songDurationSec: dur,
          peaks: peaks && peaks.length ? peaks : [],
          waveform: { status: peaks && peaks.length ? 'ready' : 'none' },
          trimStart: 0,
          trimEnd: dur,
          movieOffset: 0,
          volume: 1,
          fadeInSec: 0,
          fadeOutSec: 0,
        },
      ]);
    } catch (err) {
      setSongUpErr(err.message || String(err));
    } finally {
      setSongUpBusy(false);
    }
  };

  // ---- Save / delete ------------------------------------------------------
  const save = async () => {
    setSavingErr(null);
    try {
      const categories = meta.categories.split(',').map((c) => c.trim()).filter(Boolean);
      const firstClip = clips[0];
      const patch = {
        title: meta.title.trim() || 'Untitled',
        year: Number(meta.year) || new Date().getFullYear(),
        location: meta.location,
        synopsis: meta.synopsis,
        categories: categories.length ? categories : ['Surf'],
        thumbUrl: meta.thumbUrl.trim(),
        heroUrl: meta.heroUrl.trim() || meta.thumbUrl.trim(),
        featured: meta.featured,
        hero: {
          posterUrl: hero.posterUrl || '',
          delaySec: Number(hero.delaySec) || 0,
          startSec: Number(hero.startSec) || 0,
          durationSec: Number(hero.durationSec) || 12,
        },
        clips,
        grade: gradeState,
        songs,
        viewerSongs,
        viewerSongDefault,
        hasOriginalAudio,
        intro,
        outro,
        output,
        // Dissolve-aware: each cross-dissolve overlaps the next clip, so the
        // published file is shorter than the hard-cut sum. Matches render.js.
        durationSec: Math.round(masterDuration(clips)),
        // NEVER stomp streamType here: publishing writes streamType:'hls' next
        // to streamUrl, and an edit save used to overwrite it with 'mp4' —
        // mislabeling the published .m3u8 for every player that trusts the tag.
        ...(movie.streamUrl ? {} : { streamType: 'mp4' }),
        status: clips.length ? 'ready' : 'uploading',
        // Keep a legacy single-file pointer for any old readers.
        videoUrl: firstClip && firstClip.url ? firstClip.url : movie.videoUrl || '',
      };
      await updateMovie(id, patch);
      // Persist only. Rendering (the preview master and the published stream) is
      // a deliberate action — Publish / Build preview — so iterative editing
      // never burns compute on intermediate states.
      setDirty(false);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      setSavingErr(err.message || String(err));
    }
  };
  saveRef.current = save;
  const onDelete = async () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete “${meta.title || movie.title}”? This can’t be undone.`)) return;
    await deleteMovie(id);
    navigate('/');
  };

  const publish = async () => {
    // Surface the estimated processing cost as the final step of submitting the
    // job, so an expensive RAW/4K publish is a deliberate choice.
    if (pubCost.wantRaw && pubCost.fanout > 0.01) {
      const ok = window.confirm(
        `Publish at this quality?\n\n` +
          `Estimated processing cost: ${fmtUSD(pubCost.usd)} (RAW re-decode fan-out ${fmtUSD(pubCost.fanout)}).\n` +
          `This launches ephemeral tasks that bill per-second; track them on the Processes page.`
      );
      if (!ok) return;
    }
    // Persist the latest edits, then explicitly render the published stream and
    // stamp the fingerprint we just published so the "unpublished changes" pill
    // clears.
    await save();
    await updateMovie(id, { publishedSig: draftSig }).catch(() => {});
    await requestRender(id);
  };

  // Build a DaVinci Resolve round-trip bundle (FCPXML + per-clip LUTs + a media
  // manifest with presigned download URLs) and save it as a .zip the owner opens
  // in their own copy of Resolve. JSZip is loaded lazily to keep the main bundle
  // lean.
  const exportToResolve = async () => {
    setResolveExp({ busy: true, err: null });
    try {
      await save();
      const bundle = await requestResolveExport(id);
      const { default: JSZip } = await import('jszip');
      const zip = new JSZip();
      (bundle.files || []).forEach((f) => zip.file(f.path, f.content));
      const blob = await zip.generateAsync({ type: 'blob' });
      const safe = (s) => String(s || 'movie').replace(/[^\w.\- ]+/g, '_').trim() || 'movie';
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `${safe(bundle.movieTitle)}-resolve.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(a.href), 4000);
      setResolveExp({ busy: false, err: null });
    } catch (err) {
      setResolveExp({ busy: false, err: err.message || 'Export failed' });
    }
  };

  // "Pro finish" on the headless Resolve node. Gated behind an env flag until
  // the node (scripts/resolve-node/) is provisioned — see its README.
  const RESOLVE_FINISH_ON = process.env.REACT_APP_RESOLVE_FINISH === '1';
  const finishInResolve = async () => {
    setProFinish({ busy: true, err: null });
    try {
      await save();
      await requestResolveFinish(id, { height: output.height || 2160, fps: output.fps || 0, codec: 'h265' });
      setProFinish({ busy: false, err: null });
    } catch (err) {
      setProFinish({ busy: false, err: err.message || 'Pro finish failed' });
    }
  };

  // Export a full-resolution still from a single clip (independent of the hero
  // poster). Grabs just inside the clip's trim in-point. Raw footage only.
  const grabClipFrame = async (c) => {
    if (!c.s3Key) {
      setClipGrab({ id: c.id, busy: false, err: 'Frame export needs raw footage (uploaded movie files have no source key).', result: null });
      return;
    }
    setClipGrab({ id: c.id, busy: true, err: null, result: null });
    try {
      // Map the movie playhead into THIS clip's source time so we grab the frame
      // the slider is parked on (not the clip's trim in-point). Clamp to the
      // clip's trim window when the playhead sits in a different clip.
      const start = clipStartOffset(clips, c.id);
      const inS = c.inSec || 0;
      const outS = c.outSec || c.sourceDurationSec || inS + clipLength(c) * clipSpeed(c);
      // Movie-time past the clip start maps to SOURCE time via the speed factor.
      let at = inS + Math.max(0, previewTime - start) * clipSpeed(c);
      at = Math.min(Math.max(at, inS), Math.max(inS, outS - 0.05));
      const idx = clips.findIndex((x) => x.id === c.id);
      const fps = c.fps || 30;
      const frame = Math.floor((at - inS) * fps) + 1; // frame 1 = clip's trim in-point
      const movieTitle = meta.title || movie.title || 'movie';
      const { url, key } = await exportFrameGrab({ s3Key: c.s3Key, timeSec: at, format: 'jpg' });
      const safe = (s) => String(s).replace(/[^\w.\- ]+/g, '_').trim();
      const name = `${safe(movieTitle) || 'movie'}-clip${idx + 1}-frame${frame}`;
      setClipGrab({
        id: null,
        busy: false,
        err: null,
        result: { clipId: c.id, url, key, name, atSec: at, frame, fps, approxFps: !c.fps, clipIdx: idx, clipLabel: c.label, movieTitle },
      });
      window.open(httpsify(url), '_blank', 'noopener');
    } catch (err) {
      setClipGrab({ id: c.id, busy: false, err: err.message || String(err), result: null });
    }
  };

  const downloadGrab = async () => {
    const r = clipGrab.result;
    if (!r) return;
    try {
      await downloadAsset({ key: r.key, name: r.name, bucket: 'movies' });
    } catch (err) {
      setClipGrab((g) => ({ ...g, err: `Download failed: ${err.message || err}` }));
    }
  };

  // Open the full frame-grab studio for a clip: scrub to a frame, apply this
  // clip's live color/light look + crop/zoom reframe, and save it as a
  // non-destructive, exportable photo in the trip's media. `atSrcSec` is the
  // source-time to land on (defaults to the clip's in-point). Needs the clip's
  // source asset doc (raw footage / RED); uploaded movie files have none.
  const openGrab = async (clip, atSrcSec) => {
    if (!clip) return;
    setGrabErr(null);
    if (!clip.assetId || !clip.tripId) {
      setGrabErr('This clip has no source footage to grab from (it’s an uploaded movie file). Add it from a trip to grab editable stills.');
      return;
    }
    setGrabLoading(clip.id);
    try {
      let asset = assetDocCache.current[clip.assetId];
      if (asset === undefined) {
        asset = await getAssetDoc(clip.tripId, clip.assetId);
        assetDocCache.current[clip.assetId] = asset || null;
      }
      if (!asset) { setGrabErr('Couldn’t load this clip’s source footage.'); return; }
      setGrab({ clip, asset, startTime: Math.max(0, Number(atSrcSec) || clip.inSec || 0) });
    } catch (err) {
      setGrabErr(err.message || String(err));
    } finally {
      setGrabLoading(null);
    }
  };
  // Grab from whatever clip the playhead is currently over (works on every tab).
  const grabAtPlayhead = () => {
    const curTime = tab === 'assemble' ? previewTime : studioTime;
    const { clip, localSec } = clipAtMovieTime(clips, curTime);
    openGrab(clip, localSec);
  };

  // ---- Proof strip → keyframes bridge --------------------------------------
  // Review-then-bake workflow for the movie publish: extract a strip of frames
  // across a clip (each carrying the clip's current look + reframe window at
  // that moment), dial grade/crop per frame in the photo editor / proof sheet,
  // then promote the reviewed frames back onto the movie clip as gradeKeyframes
  // + reframe waypoints/zoomKeys. The publish interpolates smoothly between them.
  const PROOF_FRAMES = 9;

  // The reframe crop window (normalized {x,y,w,h}) a clip shows at master-time
  // `mt` — mirrors the studio preview + publish bake (sans EMA smoothing, which
  // is irrelevant for a single reviewed frame).
  const reframeWindowAt = (clip, mt) => {
    const rf = clipReframe(clip);
    const s = reframeScaleAt(rf.zoomKeys, rf.manualCrop.scale, mt);
    if (s >= 0.999) return null;
    const c = rf.cropMode === 'manual' || !rf.waypoints.length
      ? { cx: rf.manualCrop.cx, cy: rf.manualCrop.cy }
      : reframeCenterAt(rf.waypoints, mt);
    const w = Math.min(1, s);
    const h = Math.min(1, s);
    const x = Math.min(1 - w, Math.max(0, c.cx - w / 2));
    const y = Math.min(1 - h, Math.max(0, c.cy - h / 2));
    const r4 = (v) => Math.round(v * 10000) / 10000;
    return { x: r4(x), y: r4(y), w: r4(w), h: r4(h) };
  };

  const extractProofStrip = async (clip, idx) => {
    setProofNote(null);
    if (!clip.assetId || !clip.tripId) {
      setProofNote({ clipId: clip.id, err: 'This clip has no source footage (uploaded movie file) — proof strips need a trip clip.' });
      return;
    }
    setProofBusy(clip.id);
    try {
      const asset = await getAssetDoc(clip.tripId, clip.assetId);
      if (!asset) throw new Error('couldn’t load this clip’s source footage');
      const li = clipLength(clip);
      const seg = masterSegments(clips).find((sg) => sg.clip.id === clip.id);
      const tileStart = seg ? seg.tileStart : 0;
      const map = clipSpeedMap(clip);
      const fps = clip.fps || 30;
      const movieTitle = meta.title || movie.title || 'movie';
      const sheetName = `Movie · ${movieTitle} · clip ${idx + 1}`;
      const inset = Math.min(0.2, li / 20);
      const frames = [];
      for (let j = 0; j < PROOF_FRAMES; j += 1) {
        const tLocal = PROOF_FRAMES === 1
          ? li / 2
          : inset + ((li - 2 * inset) * j) / (PROOF_FRAMES - 1);
        const srcSec = (clip.inSec || 0) + timelineToSrc(map, tLocal);
        const kfs = sortKeyframes(clip.gradeKeyframes);
        const grade = kfs.length
          ? gradeAtTime(kfs, tLocal, clip.useGrade === false ? null : clip.grade)
          : (clip.useGrade === false ? null : clip.grade || null);
        const crop = reframeWindowAt(clip, tileStart + tLocal);
        const frame = Math.floor((srcSec - (clip.inSec || 0)) * fps) + 1;
        // eslint-disable-next-line no-await-in-loop
        const assetId = await createStillGrabAsset(clip.tripId, {
          source: { id: clip.assetId, name: asset.name, grade: asset.grade, isRawVideo: asset.isRawVideo },
          timeSec: srcSec,
          crop,
          applyGrade: !!grade,
          grade,
          redSource: !!asset.isRawVideo,
          name: `${movieTitle} — clip ${idx + 1} proof f${frame}`,
          tags: ['movie-proof'],
          movieId: id,
          movieTitle,
          proofSheet: { name: sheetName, kind: 'movie-clip-strip', clipId: clip.id, movieId: id },
          proofFrame: frame,
        });
        frames.push({ assetId, t: Math.round(tLocal * 1000) / 1000 });
      }
      patchClip(clip.id, { proofStrip: { requestedAt: Date.now(), sheetName, frames } });
      setProofNote({ clipId: clip.id, msg: `${frames.length} proof frames baking — review them on the Proof sheets page, dial grade/crop per frame, then apply them back here as keyframes.` });
    } catch (err) {
      setProofNote({ clipId: clip.id, err: err.message || String(err) });
    } finally {
      setProofBusy(null);
    }
  };

  // Read the reviewed strip back and write keyframes onto the movie clip:
  // each frame's (possibly edited) grade → gradeKeyframes[{t, grade}] (clip-local
  // timeline seconds) and its crop → reframe waypoints + zoomKeys (master time).
  const promoteProofStrip = async (clip) => {
    setProofNote(null);
    const strip = clip.proofStrip;
    if (!strip || !Array.isArray(strip.frames) || !strip.frames.length) return;
    setProofBusy(clip.id);
    try {
      const seg = masterSegments(clips).find((sg) => sg.clip.id === clip.id);
      const tileStart = seg ? seg.tileStart : 0;
      const rows = [];
      let pendingCount = 0;
      for (const f of strip.frames) {
        // eslint-disable-next-line no-await-in-loop
        const a = await getAssetDoc(clip.tripId, f.assetId);
        if (!a) continue; // frame was deleted — skip it
        const st = a.stillGrab?.status;
        if (st === 'queued' || st === 'rendering') pendingCount += 1;
        rows.push({ t: f.t, grade: a.grade || null, crop: a.crop || null });
      }
      if (!rows.length) throw new Error('none of the strip’s frames exist anymore — extract a new strip');
      const gradeKeyframes = rows
        .filter((r) => r.grade)
        .map((r) => ({ t: r.t, grade: r.grade }));
      const cropRows = rows.filter((r) => r.crop && r.crop.w > 0 && r.crop.h > 0);
      const r3 = (v) => Math.round(v * 1000) / 1000;
      const patch = {};
      if (gradeKeyframes.length >= 1) {
        patch.gradeKeyframes = gradeKeyframes;
        // Mirror the first keyframe into `grade` for single-grade consumers
        // (matches the clip-asset keyframe convention).
        patch.grade = gradeKeyframes[0].grade;
        patch.useGrade = true;
        // Promoting a reviewed strip is a deliberate act — mark the look as
        // set-in-this-movie so it isn't flagged as "copied from asset".
        patch.gradeLocked = true;
      }
      if (cropRows.length >= 1) {
        patch.reframe = {
          ...clipReframe(clip),
          cropMode: 'subject',
          subjectTrackId: null,
          waypoints: cropRows.map((r) => ({
            t: r3(tileStart + r.t),
            cx: r3(r.crop.x + r.crop.w / 2),
            cy: r3(r.crop.y + r.crop.h / 2),
          })),
          zoomKeys: cropRows.map((r) => ({
            t: r3(tileStart + r.t),
            scale: r3(Math.min(1, Math.max(0.25, Math.max(r.crop.w, r.crop.h)))),
          })),
        };
      }
      if (!Object.keys(patch).length) throw new Error('the reviewed frames carry no grade or crop to promote');
      patchClip(clip.id, patch);
      setProofNote({
        clipId: clip.id,
        msg: `Applied ${gradeKeyframes.length} grade keyframe${gradeKeyframes.length === 1 ? '' : 's'}`
          + (cropRows.length ? ` + ${cropRows.length} reframe waypoint${cropRows.length === 1 ? '' : 's'}` : '')
          + (pendingCount ? ` (⚠ ${pendingCount} frame${pendingCount === 1 ? ' is' : 's are'} still baking — their grades may be stale; re-apply once ready)` : '')
          + '. Save + re-publish to bake.',
      });
    } catch (err) {
      setProofNote({ clipId: clip.id, err: err.message || String(err) });
    } finally {
      setProofBusy(null);
    }
  };
  // Set the timeline thumbnail for the clip under the playhead to the current frame.
  const setThumbAtPlayhead = () => {
    const curTime = tab === 'assemble' ? previewTime : studioTime;
    const { clip, localSec } = clipAtMovieTime(clips, curTime);
    if (!clip) return;
    const inn = clip.inSec || 0;
    const srcLen = clipSourceLength(clip) || ((clip.outSec || clip.sourceDurationSec || 0) - inn) || 1;
    setClipThumb(clip.id, (localSec - inn) / srcLen);
  };

  // Grab a full-resolution still at the current preview time for the hero
  // poster. Only works when the underlying clip is raw footage (has s3Key).
  const grabHeroPoster = async () => {
    setHeroGrabErr(null);
    const { clip: target, localSec: local } = clipAtMovieTime(clips, hero.startSec);
    if (!target || !target.s3Key) {
      setHeroGrabErr('Poster grab needs a raw-footage clip. For uploaded movie files, paste a poster URL.');
      return;
    }
    setHeroGrabBusy(true);
    try {
      const { url } = await exportFrameGrab({ s3Key: target.s3Key, timeSec: local, format: 'jpg' });
      setHero((h) => ({ ...h, posterUrl: url }));
    } catch (err) {
      setHeroGrabErr(err.message || String(err));
    } finally {
      setHeroGrabBusy(false);
    }
  };
  const render = movie.render || {};
  const rendering = render.status === 'queued' || render.status === 'rendering';
  // Has the baked composition diverged from what's published? (Metadata edits
  // like title/poster/hero don't count — those never need a re-publish.)
  const compositionDirty =
    hydrated &&
    (renderSignature(clips, songs, intro, outro, hasOriginalAudio, output) !==
      renderSignature(movie.clips, movie.songs, movie.intro, movie.outro, movie.hasOriginalAudio, movieOutput(movie)) ||
      currentGradeSig(movie) !== publishedGradeSig(movie));

  // Draft vs published: a stable fingerprint of everything baked into the
  // stream (composition + per-clip looks + movie-wide grade). `publishedSig` is
  // stamped on Publish; when it diverges we surface "unpublished changes" so the
  // user knows a render is needed — but nothing renders until they ask.
  const draftSig =
    `${renderSignature(clips, songs, intro, outro, hasOriginalAudio, output)}|${gradeSignature(gradeState || DEFAULT_GRADE)}`;
  const isPublished =
    hydrated &&
    render.status === 'ready' &&
    (movie.publishedSig ? movie.publishedSig === draftSig : !compositionDirty);
  const unpublished = hydrated && clips.length > 0 && !isPublished && !rendering;
  // The preview master (used by the Reframe tab + Quick Compare + social export)
  // is built only on request now. Flag when it's missing or behind the cut.
  const masterStatus = movie.master?.status;
  const masterBuilding = masterStatus === 'composing' || masterStatus === 'queued';
  // Stale = the baked master no longer matches the cut. Two independent tells:
  // its duration diverges from the DISSOLVE-AWARE master length (movieDur is the
  // hard-cut edit length — comparing against that flagged every dissolve as
  // stale), or the composition fingerprint stamped at build-request time no
  // longer matches the live clips (catches reorders/slips that keep the length).
  const masterStale =
    masterStatus === 'ready' &&
    (Math.abs((movie.master?.durationSec || 0) - masterDuration(clips)) > 0.75 ||
      (movie.master?.cutSig ? movie.master.cutSig !== masterCutSig(clips) : false));
  const masterMissing = !masterStatus || masterStatus === 'error';

  const buildPreview = () => {
    requestMaster(id, masterCutSig(clips)).catch(() => {});
  };

  // Jump from the Review stage straight to a clip in the Color / Reframe studio:
  // switch the tab, then seek the shared studio player to that clip's start once
  // it has mounted (the studios are playhead-driven, not clip-selected).
  const openClipInStudio = (cid, dest) => {
    const seg = masterSegments(clips).find((s) => s.clip.id === cid);
    if (dest === 'assemble') {
      setTab('assemble');
      setSelectedClipId(cid);
      if (seg) setTimeout(() => handleTimelineSeek(seg.tileStart + 0.05), 350);
      return;
    }
    setTab(dest === 'color' ? 'color' : 'reframe');
    // Color's studio runs on EDIT seconds; Reframe's master player runs on
    // MASTER seconds — pick the clip start on the destination's own clock.
    const start = dest === 'color' ? clipStartOffset(clips, cid) : seg ? seg.tileStart : null;
    if (start != null) setTimeout(() => studioSeekRef.current?.(start + 0.05), 350);
  };

  const posterBg = httpsify(meta.heroUrl || meta.thumbUrl);

  // Click-to-seek from the shared timeline: assemble drives the composition
  // player; color/reframe drive the studio's live/preview player. EVERYTHING in
  // this page (curTimeRef, studioTime, the WaveTimeline layout) speaks EDIT
  // seconds; only the Reframe studio plays the dissolve-composed MASTER, whose
  // clock runs shorter by each dissolve's overlap — so convert at this boundary.
  const handleTimelineSeek = (sec) => {
    curTimeRef.current = sec;
    if (tab === 'assemble') playerSeekRef.current?.(sec);
    else if (tab === 'reframe') studioSeekRef.current?.(editToMaster(clips, sec));
    else studioSeekRef.current?.(sec);
  };
  // Track the freshest playhead time per tab (for frame stepping) while still
  // updating the displayed state. The reframe studio reports MASTER seconds —
  // normalize to EDIT so the shared playhead and cross-tab carry stay honest.
  const handlePreviewTime = (t) => { curTimeRef.current = t; setPreviewTime(t); };
  const handleStudioTime = (t) => {
    const e = tab === 'reframe' ? masterToEdit(clips, t) : t;
    curTimeRef.current = e;
    setStudioTime(e);
  };

  // The player control handle for the active tab.
  const activeCtl = () =>
    (tab === 'assemble' ? playerCtlRef : tab === 'color' ? colorCtlRef : reframeCtlRef).current;

  const transportToggle = () => activeCtl()?.toggle?.();
  // Frame step: pause, then nudge the playhead by `n` frames (default 1) of the
  // clip under the playhead (its own fps, else the output fps, else 30).
  const stepFrame = (dir, n = 1) => {
    activeCtl()?.pause?.();
    const at = curTimeRef.current || 0;
    const { clip } = clipAtMovieTime(clips, at);
    const fps = clip?.fps || clip?.srcFps || output?.fps || 30;
    const next = Math.max(0, Math.min(movieDur, at + dir * (n / fps)));
    handleTimelineSeek(next);
  };
  transportApiRef.current = { toggle: transportToggle, step: stepFrame };
  editApiRef.current = {
    split: splitAtPlayhead,
    // Delete acts on whichever element is selected — the song takes priority
    // when one is picked on the timeline, otherwise the clip.
    del: () => {
      if (selectedSongId) { removeSong(selectedSongId); setSelectedSongId(null); }
      else deleteClip(selectedClipId);
    },
    copy: () => copyClip(selectedClipId),
    paste: pasteClip,
    duplicate: () => duplicateClip(selectedClipId),
    canCopy: () => !!selectedClipId,
    canDelete: () => !!selectedClipId || !!selectedSongId,
    canPaste: () => !!clipboard,
    undo,
    redo,
  };

  // Shared clips + audio timeline, rendered on every tab. The movie-level
  // fade sliders only show on Assemble (where you set them).
  const renderTimeline = (withFades, docked) => {
    const curTime = tab === 'assemble' ? previewTime : studioTime;
    const tlHidden = !tabVis.timeline;
    // What the current tab's right panel holds — for the restore chip label.
    const sidePanelName = tab === 'assemble' ? 'clips' : 'tools';
    const selIdx = clips.findIndex((c) => c.id === selectedClipId);
    const selClip = selIdx >= 0 ? clips[selIdx] : null;
    const selSpeed = selClip ? clipSpeed(selClip) : 1;
    // Source-time position of the playhead within the selected clip (for placing
    // speed-ramp keys at the current scrub position). Uses the SAME time base as
    // the playhead readout and the preview clock (plain ramp-aware accumulation
    // — clipAtMovieTime), NOT the dissolve-overlapped master tiles: with
    // transitions those diverge and keys landed at the wrong source second.
    let selPlayheadSrc = null;
    if (selClip) {
      const start = clipStartOffset(clips, selClip.id);
      const within = curTime - start;
      if (within >= -0.5 && within <= clipLength(selClip) + 0.5) {
        selPlayheadSrc = Math.max(0, Math.min(clipSourceLength(selClip), timelineToSrc(clipSpeedMap(selClip), within)));
      }
    }
    const speedPreset = SPEED_OPTIONS.find((p) => Math.abs(p - selSpeed) < 0.001);
    const speedIsPreset = speedPreset != null;
    const speedSelectValue = selClip ? (speedIsPreset ? String(speedPreset) : 'custom') : '1';
    const realX = selClip ? clipRealSpeed(selClip) : 0; // real-motion speed-up for slow-mo clips
    const atRealSpeed = realX > 0 && Math.abs(selSpeed - realX) < 0.02;
    // Jump the playhead to the selected clip's first/last kept frame (movie
    // time). Last frame goes through the clip's speed map so it lands on the
    // same source second the ramp panel's ⇥ key uses.
    const jumpToClipEdge = (which) => {
      if (!selClip) return;
      activeCtl()?.pause?.();
      const start = clipStartOffset(clips, selClip.id);
      if (which === 'start') { handleTimelineSeek(start); return; }
      const fps = selClip.fps || selClip.srcFps || output?.fps || 30;
      const lastSrc = Math.max(0, clipSourceLength(selClip) - 1 / fps);
      handleTimelineSeek(start + srcToTimeline(clipSpeedMap(selClip), lastSrc));
    };
    const selSong = selectedSongId ? songs.find((s) => s.id === selectedSongId) : null;
    const selSongTrimEnd = selSong ? (selSong.trimEnd && selSong.trimEnd > 0 ? selSong.trimEnd : selSong.songDurationSec || 0) : 0;
    return (
    <div className={`cs-section${docked ? ' cs-studio-timeline' : ''}`}>
      <div className="cs-section-head cs-tl-head">
        {/* Undo/redo live on every tab (grade & reframe edits are in the same
            history); the clip-editing tools stay Assemble-only. */}
        {tab !== 'assemble' && (
          <div className="cs-tl-edit" role="group" aria-label="Undo / redo">
            <button className="cs-tl-tbtn" title="Undo (⌘Z)" aria-label="Undo" onClick={undo} disabled={!canUndo}>↶</button>
            <button className="cs-tl-tbtn" title="Redo (⌘⇧Z / ⌘Y)" aria-label="Redo" onClick={redo} disabled={!canRedo}>↷</button>
          </div>
        )}
        {tab === 'assemble' && (
        <div className="cs-tl-edit" role="group" aria-label="Clip editing">
          {/* Logical button GROUPS — the row justifies the groups across the
              strip (space-between) while each group's buttons stay together. */}
          <span className="cs-tl-grp" role="group" aria-label="Undo / redo">
          <button className="cs-tl-tbtn" title="Undo (⌘Z)" aria-label="Undo" onClick={undo} disabled={!canUndo}>↶</button>
          <button className="cs-tl-tbtn" title="Redo (⌘⇧Z / ⌘Y)" aria-label="Redo" onClick={redo} disabled={!canRedo}>↷</button>
          </span>
          <span className="cs-tl-grp" role="group" aria-label="Clip edit tools">
          <button className="cs-tl-tbtn" title="Split clip at the playhead (⌘B)" aria-label="Split clip" onClick={splitAtPlayhead} disabled={!clips.length}>✂</button>
          <button className="cs-tl-tbtn" title="Duplicate selected clip (⌘D)" aria-label="Duplicate clip" onClick={() => duplicateClip(selectedClipId)} disabled={!selClip}>⧉</button>
          <button className="cs-tl-tbtn" title="Copy selected clip (⌘C)" aria-label="Copy clip" onClick={() => copyClip(selectedClipId)} disabled={!selClip}>⎘</button>
          <button className="cs-tl-tbtn" title="Paste clip after the selection (⌘V)" aria-label="Paste clip" onClick={pasteClip} disabled={!clipboard}>⎗</button>
          <button className="cs-tl-tbtn cs-tl-del" title="Delete selected clip (Delete)" aria-label="Delete clip" onClick={() => deleteClip(selectedClipId)} disabled={!selClip}>🗑</button>
          </span>
          <span className="cs-tl-grp" role="group" aria-label="Clip speed">
          <span className="cs-tl-time" title="Selected clip playback speed">⏩</span>
          <select
            className="cs-tl-select"
            value={speedSelectValue}
            disabled={!selClip}
            title={selClip ? `Clip ${selIdx + 1} playback speed` : 'Select a clip first'}
            onChange={(e) => {
              if (e.target.value === 'custom') return;
              const v = parseFloat(e.target.value);
              // Constant speed REPLACES any speed ramp — with speedKeys left in
              // place the keyed path keeps winning in preview/timeline math and
              // this control silently does nothing (while historically still
              // leaking the stale `speed` into the bake).
              if (selectedClipId && Number.isFinite(v)) patchClip(selectedClipId, { speed: v, speedKeys: null });
            }}
          >
            {selClip && !speedIsPreset && <option value="custom">{selSpeed}× (custom)</option>}
            {SPEED_OPTIONS.map((p) => (
              <option key={p} value={p}>{p}×</option>
            ))}
          </select>
          <input
            key={`spd:${selectedClipId || 'none'}:${selSpeed}`}
            className="cs-tl-num"
            type="number"
            min={SPEED_MIN}
            max={SPEED_MAX}
            step="0.01"
            defaultValue={selClip ? selSpeed : ''}
            disabled={!selClip}
            title="Type any speed, e.g. 6.67"
            onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
            onBlur={(e) => {
              const v = parseFloat(e.currentTarget.value);
              if (selectedClipId && Number.isFinite(v) && v > 0) {
                // Constant speed replaces any ramp — see the dropdown handler.
                patchClip(selectedClipId, { speed: Math.min(SPEED_MAX, Math.max(SPEED_MIN, v)), speedKeys: null });
              }
            }}
          />
          <span className="cs-tl-time">×</span>
          {realX > 0 && (
            <button
              className={`cs-tl-real${atRealSpeed ? ' is-on' : ''}`}
              title={`Shot in slow motion. Real-life motion plays at ${realX}× — click to set it.${atRealSpeed ? ' (currently real time)' : ''}`}
              disabled={!selClip || atRealSpeed}
              onClick={() => selectedClipId && patchClip(selectedClipId, { speed: realX })}
            >
              ⏱ {realX}×{atRealSpeed ? ' ✓' : ''}
            </button>
          )}
          <button
            className={`cs-tl-tbtn${speedRowOpen ? ' is-on' : ''}`}
            title={speedRowOpen
              ? 'Hide the speed tools row (speed math + speed-ramp keyframes)'
              : 'Show speed tools — camera speed math and speed-ramp keyframes for the selected clip'}
            aria-label="Toggle speed tools"
            aria-expanded={speedRowOpen}
            onClick={toggleSpeedRow}
            disabled={!selClip}
          >
            {speedRowOpen ? '🏎▾' : '🏎▸'}
          </button>
          </span>
          {/* Per-clip fade to black/white + cross-dissolve into the next clip.
              Fades preview live as an overlay; the dissolve previews as a brief
              fade-through and bakes as a true xfade on Publish. */}
          <span className="cs-tl-grp" role="group" aria-label="Clip fade in / fade out / cross-dissolve">
          <span className="cs-tl-time" title="Fade IN at this clip's start (seconds; 0 = none)">◗</span>
          <input
            className="cs-tl-num"
            type="number" min={0} max={5} step={0.1}
            disabled={!selClip}
            value={selClip ? Number((((selClip.fadeIn && selClip.fadeIn.sec) || 0)).toFixed(1)) : 0}
            title="Fade-in duration at this clip's start (seconds; 0 = none)"
            onChange={(e) => selClip && patchClipFade(selClip.id, 'fadeIn', e.target.value, selClip.fadeIn && selClip.fadeIn.color)}
          />
          <select
            className="cs-tl-select"
            disabled={!selClip || !(selClip.fadeIn && selClip.fadeIn.sec > 0)}
            value={(selClip && selClip.fadeIn && selClip.fadeIn.color) || 'black'}
            title="Fade-in color"
            onChange={(e) => selClip && patchClipFade(selClip.id, 'fadeIn', selClip.fadeIn && selClip.fadeIn.sec, e.target.value)}
          >
            <option value="black">⚫</option>
            <option value="white">⚪</option>
          </select>
          <span className="cs-tl-time" title="Fade OUT at this clip's end (seconds; 0 = none)">◖</span>
          <input
            className="cs-tl-num"
            type="number" min={0} max={5} step={0.1}
            disabled={!selClip}
            value={selClip ? Number((((selClip.fadeOut && selClip.fadeOut.sec) || 0)).toFixed(1)) : 0}
            title="Fade-out duration at this clip's end (seconds; 0 = none)"
            onChange={(e) => selClip && patchClipFade(selClip.id, 'fadeOut', e.target.value, selClip.fadeOut && selClip.fadeOut.color)}
          />
          <select
            className="cs-tl-select"
            disabled={!selClip || !(selClip.fadeOut && selClip.fadeOut.sec > 0)}
            value={(selClip && selClip.fadeOut && selClip.fadeOut.color) || 'black'}
            title="Fade-out color"
            onChange={(e) => selClip && patchClipFade(selClip.id, 'fadeOut', selClip.fadeOut && selClip.fadeOut.sec, e.target.value)}
          >
            <option value="black">⚫</option>
            <option value="white">⚪</option>
          </select>
          <span className="cs-tl-time" title="Cross-dissolve into the NEXT clip (seconds; 0 = hard cut). Baked as a true dissolve on Publish; the preview approximates it with a brief fade-through.">⇄</span>
          <input
            className="cs-tl-num"
            type="number" min={0} max={3} step={0.25}
            disabled={!selClip || selIdx >= clips.length - 1}
            value={selClip ? Number((selClip.dissolveSec || 0).toFixed(2)) : 0}
            title={selClip && selIdx >= clips.length - 1
              ? 'Last clip — nothing to dissolve into'
              : 'Cross-dissolve into the next clip (seconds; 0 = hard cut)'}
            onChange={(e) => selClip && patchClip(selClip.id, { dissolveSec: Math.max(0, Math.min(3, Number(e.target.value) || 0)) })}
          />
          </span>
          <button
            className="cs-tl-tbtn"
            title="Extract every clip's audio waveform (on-demand service — skips clips already analyzed or with no audio)"
            aria-label="Analyze all clip audio"
            onClick={analyzeAllClipAudio}
            disabled={!clips.length}
          >
            🌊
          </button>
        </div>
        )}
        {/* Speed math + speed ramp share ONE dense row under the toolbar —
            shown only when the 🏎 expander in the toolbar is on. */}
        {tab === 'assemble' && selClip && speedRowOpen && (
          <div className="cs-tl-speedrow">
            {selClip.assetId && (
              <ClipMotionPanel
                clip={selClip}
                asset={selAsset}
                timelineFps={output?.fps || 30}
                onSetSpeed={(v) => patchClip(selClip.id, { speed: v })}
                onDeclareSpeed={(x) => patchClip(selClip.id, { realSpeedX: x > 1.01 ? x : 0 })}
              />
            )}
            <SpeedRampPanel
              clip={selClip}
              srcLen={clipSourceLength(selClip)}
              playheadSrc={selPlayheadSrc}
              clipStartTl={clipStartOffset(clips, selClip.id)}
              onChange={(keys) => patchClip(selClip.id, { speedKeys: keys })}
            />
          </div>
        )}
        <div className="cs-tl-transport" role="group" aria-label="Playback controls">
          <span className="cs-tl-grp" role="group" aria-label="Transport">
          <button className="cs-tl-tbtn cs-tl-frame" title="Jump playhead to this clip's first frame" aria-label="Jump to selected clip's first frame" onClick={() => jumpToClipEdge('start')} disabled={!selClip}>⇤</button>
          <button className="cs-tl-tbtn cs-tl-frame" title="Jump back 10 frames (⇧←)" aria-label="Jump back 10 frames" onClick={() => stepFrame(-1, 10)} disabled={!clips.length}>◀◀</button>
          <button className="cs-tl-tbtn cs-tl-frame" title="Step back one frame (←)" aria-label="Step back one frame" onClick={() => stepFrame(-1)} disabled={!clips.length}>◀</button>
          <button className="cs-tl-tbtn cs-tl-tplay" title="Play / Pause (space)" aria-label={transportPlaying ? 'Pause' : 'Play'} onClick={transportToggle} disabled={!clips.length}>
            {transportPlaying ? '❚❚' : '►'}
          </button>
          <button className="cs-tl-tbtn cs-tl-frame" title="Step forward one frame (→)" aria-label="Step forward one frame" onClick={() => stepFrame(1)} disabled={!clips.length}>▶</button>
          <button className="cs-tl-tbtn cs-tl-frame" title="Jump forward 10 frames (⇧→)" aria-label="Jump forward 10 frames" onClick={() => stepFrame(1, 10)} disabled={!clips.length}>▶▶</button>
          <button className="cs-tl-tbtn cs-tl-frame" title="Jump playhead to this clip's last frame" aria-label="Jump to selected clip's last frame" onClick={() => jumpToClipEdge('end')} disabled={!selClip}>⇥</button>
          <span className="cs-tl-time">{fmt(curTime)} / {fmt(movieDur)}</span>
          </span>
          <span className="cs-tl-grp" role="group" aria-label="Frame tools">
          <button
            className="cs-tl-tbtn"
            title="Set this clip's timeline thumbnail to the current frame"
            aria-label="Set clip thumbnail to current frame"
            onClick={setThumbAtPlayhead}
            disabled={!clips.length}
          >
            🖼
          </button>
          <button
            className="cs-tl-tbtn"
            title="Grab a still from the current frame — with this clip's color/light look + crop/zoom — and save it as an editable, exportable photo"
            aria-label="Grab still from current frame"
            onClick={grabAtPlayhead}
            disabled={!clips.length || !!grabLoading}
          >
            {grabLoading ? '…' : '📷'}
          </button>
          <button
            className={`cs-tl-tbtn cs-tl-qual${editSmooth ? ' is-on' : ''}`}
            aria-label={editSmooth ? 'Smooth preview' : 'Full preview'}
            title={
              editSmooth
                ? 'Smooth preview: playing a light low-res edit proxy for snappy scrubbing. Click for Full quality (color checks).'
                : 'Full preview: playing the high-quality proxy/original. Click for Smooth (snappy scrubbing).'
            }
            onClick={() => setPreviewQuality(!editSmooth)}
          >
            {editSmooth ? '⚡' : '◎'}
          </button>
          </span>
          {/* Timeline zoom + playhead readout portal in here (from WaveTimeline). */}
          <span className="cs-tl-zoomslot" ref={setTlControlsEl} />
          {/* Restore chip for this tab's hidden right panel — lives here so it's
              always rediscoverable from the ever-present timeline toolbar. */}
          {!tabVis.side && (
            <button
              className="cs-tl-tbtn cs-restorechip"
              title={`Show the ${sidePanelName} panel`}
              aria-label={`Show ${sidePanelName} panel`}
              onClick={() => togglePanel(tab, 'side')}
            >
              ＋ {sidePanelName}
            </button>
          )}
          <button
            className={`cs-tl-tbtn${tlHidden ? ' cs-restorechip' : ''}`}
            title={tlHidden ? 'Show the clip timeline (this tab)' : 'Hide the clip timeline for a bigger preview (this tab)'}
            aria-label={tlHidden ? 'Show clip timeline' : 'Hide clip timeline'}
            onClick={() => togglePanel(tab, 'timeline')}
          >
            {tlHidden ? '＋ timeline' : '－'}
          </button>
        </div>
      </div>
      {!tlHidden && (
      <WaveTimeline
        clips={clips}
        songs={songs}
        movieDur={movieDur}
        onPatchSong={patchSong}
        playheadTime={curTime}
        selectedSongId={selectedSongId}
        onSelectSong={(sid) => { setSelectedSongId(sid); setSelectedClipId(null); }}
        onDeleteSong={(sid) => { removeSong(sid); setSelectedSongId(null); }}
        selectedClipId={selectedClipId}
        onSelectClip={(cid) => { setSelectedClipId(cid); setSelectedSongId(null); setExpanded(null); }}
        onDeselect={() => { setSelectedClipId(null); setSelectedSongId(null); setExpanded(null); }}
        onReorderClips={tab === 'assemble' ? reorderClips : undefined}
        onPatchClip={tab === 'assemble' ? patchClip : undefined}
        clipAudio={clipAudio}
        clipThumbs={clipThumbs}
        masterUrl={masterUrlForThumbs}
        controlsEl={tlControlsEl}
        onSetClipThumb={setClipThumb}
        onAnalyzeClip={tab === 'assemble' ? analyzeClipAudio : undefined}
        onToggleClipAudio={(cid) => {
          const c = clips.find((x) => x.id === cid);
          patchClip(cid, { audioMuted: !c?.audioMuted });
        }}
        onSeek={handleTimelineSeek}
      />
      )}
      {!tlHidden && tab === 'assemble' && selSong && (
        <div className="cs-tl-edit cs-tl-music" role="group" aria-label="Music editing">
          <button
            className="cs-tl-tbtn"
            title="Close music controls (deselect song)"
            aria-label="Close music controls"
            onClick={() => { setSelectedSongId(null); setExpanded(null); }}
          >
            ✕
          </button>
          <span className="cs-tl-time cs-tl-musiclabel" title={selSong.label}>♪ {selSong.label}</span>
          <span className="cs-tl-sep" />
          <label className="cs-tl-mlbl">Start
            <input
              className="cs-tl-num" type="number" min={0} step="0.1"
              value={Number((selSong.movieOffset || 0).toFixed(2))}
              title="When the song starts in the movie (seconds)"
              onChange={(e) => patchSong(selSong.id, { movieOffset: Math.max(0, Number(e.target.value) || 0) })}
            />
          </label>
          <label className="cs-tl-mlbl">Trim in
            <input
              className="cs-tl-num" type="number" min={0} step="0.1"
              value={Number((selSong.trimStart || 0).toFixed(2))}
              title="Skip this many seconds into the song"
              onChange={(e) => {
                const v = Math.max(0, Number(e.target.value) || 0);
                patchSong(selSong.id, { trimStart: Math.min(v, Math.max(0, selSongTrimEnd - 0.5)) });
              }}
            />
          </label>
          <label className="cs-tl-mlbl">Trim out
            <input
              className="cs-tl-num" type="number" min={0} step="0.1"
              value={Number((selSongTrimEnd || 0).toFixed(2))}
              title="End the song slice at this point (seconds into the song)"
              onChange={(e) => {
                const v = Number(e.target.value) || 0;
                const cap = selSong.songDurationSec || v;
                patchSong(selSong.id, { trimEnd: Math.max((selSong.trimStart || 0) + 0.5, Math.min(v, cap)) });
              }}
            />
          </label>
          <button
            className="cs-tl-tbtn"
            title="End the song exactly at the end of the last clip (trims the song's out point — set a fade-out after this)"
            aria-label="Snap song end to movie end"
            onClick={() => {
              const off = selSong.movieOffset || 0;
              const ts = selSong.trimStart || 0;
              const cap = selSong.songDurationSec || Infinity;
              const target = ts + Math.max(0.5, movieDur - off);
              if (target > cap + 0.01) {
                // Song is shorter than the remaining movie: pull its START back
                // instead so its natural end lands on the movie end.
                const len = (selSongTrimEnd || cap) - ts;
                patchSong(selSong.id, { movieOffset: Math.max(0, movieDur - len) });
              } else {
                patchSong(selSong.id, { trimEnd: Math.min(target, cap) });
              }
            }}
          >
            ⇥
          </button>
          <label className="cs-tl-mlbl" title="Repeat the trimmed slice until the movie ends (baked into the publish + previewed here)">
            <input
              type="checkbox"
              checked={!!selSong.loop}
              onChange={(e) => patchSong(selSong.id, { loop: e.target.checked })}
            />{' '}⟳ Loop
          </label>
          <label className="cs-tl-mlbl">Vol
            <input
              className="cs-tl-mvol" type="range" min={0} max={1} step="0.05"
              value={selSong.volume ?? 1}
              title={`Volume ${Math.round((selSong.volume ?? 1) * 100)}%`}
              onChange={(e) => patchSong(selSong.id, { volume: Number(e.target.value) })}
            />
          </label>
          <label className="cs-tl-mlbl">Fade in
            <input
              className="cs-tl-num" type="number" min={0} max={20} step="0.5"
              value={Number((selSong.fadeInSec || 0).toFixed(1))}
              onChange={(e) => patchSong(selSong.id, { fadeInSec: Math.max(0, Number(e.target.value) || 0) })}
            />
          </label>
          <label className="cs-tl-mlbl">Fade out
            <input
              className="cs-tl-num" type="number" min={0} max={20} step="0.5"
              value={Number((selSong.fadeOutSec || 0).toFixed(1))}
              onChange={(e) => patchSong(selSong.id, { fadeOutSec: Math.max(0, Number(e.target.value) || 0) })}
            />
          </label>
          <span className="cs-tl-sep" />
          {(selSong.url || selSong.libraryId) && (
            <button
              className="cs-tl-tbtn"
              disabled={selSong.waveform?.status === 'analyzing'}
              aria-label="Analyze song waveform"
              title={(selSong.peaks && selSong.peaks.length)
                ? 'Re-extract this song’s waveform'
                : 'Extract this song’s waveform (analyzes the audio)'}
              onClick={() => analyzeSong(selSong)}
            >
              {selSong.waveform?.status === 'analyzing'
                ? '…'
                : (selSong.peaks && selSong.peaks.length) ? '↻' : '🌊'}
            </button>
          )}
          <button
            className="cs-tl-tbtn"
            title="Open this song's full waveform trim / align editor below"
            aria-label="Edit song waveform"
            onClick={() => setExpanded(expanded === selSong.id ? null : selSong.id)}
          >
            ✎
          </button>
          <button
            className="cs-tl-tbtn cs-tl-del"
            title="Remove this song (Delete)"
            aria-label="Remove song"
            onClick={() => { removeSong(selSong.id); setSelectedSongId(null); setExpanded(null); }}
          >
            🗑
          </button>
        </div>
      )}
      {withFades && (
        <div style={{ display: 'flex', gap: 28, marginTop: 16, flexWrap: 'wrap' }}>
          <label style={{ fontSize: 13, color: 'var(--muted)', flex: '1 1 240px' }}>
            Fade in from black: {intro.toFixed(1)}s
            <input className="cs-slider" type="range" min="0" max="8" step="0.5" value={intro}
              onChange={(e) => setIntro(Number(e.target.value))} />
          </label>
          <label style={{ fontSize: 13, color: 'var(--muted)', flex: '1 1 240px' }}>
            Fade to black at end: {outro.toFixed(1)}s
            <input className="cs-slider" type="range" min="0" max="8" step="0.5" value={outro}
              onChange={(e) => setOutro(Number(e.target.value))} />
          </label>
        </div>
      )}
    </div>
    );
  };

  return (
    <div className={`cs-page cs-edit-page${tab !== 'details' && tab !== 'review' ? ' cs-edit-locked' : ''}`}>
      <div className="cs-editbar cs-editbar-wrap">
        <div className="cs-editbar-left">
          <button className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => navigate(`/watch/${id}`)} title="Back to the published cut">←</button>
          <h1>
            {meta.title || movie.title}
            {movie.lineage?.name ? <span className="cs-editor-branchname">⎇ {movie.lineage.name}</span> : null}
          </h1>
          {/* Compact numbered tabs double as the production stepper. */}
          <nav className="cs-editor-tabs" aria-label="Editor sections">
            <button className={`cs-editor-tab ${tab === 'assemble' ? 'on' : ''}`} onClick={() => setTab('assemble')} title="Assemble · clips, audio, timeline">
              <span className="cs-editor-tab-n">1</span> Assemble
            </button>
            <button className={`cs-editor-tab ${tab === 'color' ? 'on' : ''}`} onClick={() => setTab('color')} title="Color & Light · grade, auto, styles">
              <span className="cs-editor-tab-n">2</span> Color
            </button>
            <button className={`cs-editor-tab ${tab === 'reframe' ? 'on' : ''}`} onClick={() => setTab('reframe')} title="Reframe · subject crop, social export">
              <span className="cs-editor-tab-n">3</span> Reframe
            </button>
            <button className={`cs-editor-tab ${tab === 'details' ? 'on' : ''}`} onClick={() => setTab('details')} title="Details · title, poster, hero">
              <span className="cs-editor-tab-n">4</span> Details
            </button>
            <button className={`cs-editor-tab ${tab === 'review' ? 'on' : ''}`} onClick={() => setTab('review')} title="Review · full edit tree, confirm, publish/export">
              <span className="cs-editor-tab-n">5</span> Review
            </button>
          </nav>
          {/* Proof set companion: the saved bake sheet is where this stage's
              choices are actually judged on real frames — Color lands in the
              look-tile mode, Reframe in the crop-keyframe mode. */}
          {(tab === 'color' || tab === 'reframe') && latestProofSheet && (
            <button
              className="cs-btn cs-btn-ghost cs-btn-sm"
              title={(tab === 'color'
                ? `Review this movie's proof set in color grade mode — de-log bases × look columns per clip (${latestProofSheet.title || 'latest bake sheet'})`
                : `Review this movie's proof set in crop mode — bake frames with crop keyframe tagging per clip (${latestProofSheet.title || 'latest bake sheet'})`)
                + (latestProofSheet.movieId !== id ? ' — from another version of this movie' : '')}
              onClick={() => navigate(`/proofs?page=${latestProofSheet.id}&mode=${tab === 'color' ? 'color' : 'crop'}&movie=${id}`)}
            >
              {tab === 'color' ? '🎨 Proof set' : '✂ Proof set'}
            </button>
          )}
        </div>
        <div className="cs-editbar-actions">
          {savingErr && <span style={{ color: 'var(--accent)', fontSize: 12 }}>{savingErr}</span>}
          {rendering ? (
            <span className="cs-help" style={{ fontSize: 12 }}>{renderEtaLabel(render, now)}</span>
          ) : (
            <span
              className={`cs-draftpill ${unpublished ? 'dirty' : 'clean'}`}
              title={unpublished
                ? 'Your edits are saved as a draft. Publish to render the shareable stream.'
                : 'The published stream matches your current edits.'}
            >
              {unpublished ? '● Draft' : '✓ Live'}
            </span>
          )}
          {masterBuilding ? (
            <span className="cs-help" style={{ fontSize: 12 }}>
              {jobEtaLabel(movie.master, { verb: 'Building preview', estSec: masterEtaSec, nowMs: now })}
            </span>
          ) : (masterMissing || masterStale) && clips.length ? (
            <span className="cs-stalechip" title="The preview master (Reframe / Quick Compare / social export) is older than your current cut.">
              ⚠ Preview out of date
            </span>
          ) : null}
          <button
            className="cs-btn cs-btn-ghost cs-btn-sm"
            disabled={!clips.length || masterBuilding}
            onClick={buildPreview}
            title={masterMissing || masterStale
              ? `Preview is out of date — rebuild the master used by Reframe, Quick Compare and social export (~${fmtDuration(masterEtaSec)}).`
              : `Rebuild the preview master used by Reframe, Quick Compare and social export (~${fmtDuration(masterEtaSec)}).`}
          >
            {masterBuilding ? 'Building…' : (masterMissing || masterStale) ? '↻ Rebuild preview •' : '↻ Rebuild preview'}
          </button>
          <button
            className="cs-btn cs-btn-ghost cs-btn-sm"
            disabled={!clips.length}
            onClick={() => setComparing(true)}
            title="Quick compare: graded vs ungraded master"
          >
            ⇆ Compare
          </button>
          <button
            className="cs-btn cs-btn-ghost cs-btn-sm"
            onClick={() => { setTab('details'); setVersionsFocus((v) => v + 1); }}
            title="Versions: save this edit as a new named version, see the family tree, diff versions, pull clips across"
          >
            ⎇ Versions
          </button>
          <div className="cs-segment cs-segment-sm cs-savemode" role="group" aria-label="Save mode"
            title="Autosave persists every edit automatically. Manual keeps changes local until you press Save.">
            <button className={!autosave ? 'on' : ''} onClick={() => setSaveMode(false)}>Manual</button>
            <button className={autosave ? 'on' : ''} onClick={() => setSaveMode(true)}>Auto</button>
          </div>
          <button
            className={`cs-btn cs-btn-ghost cs-btn-sm${!autosave && dirty ? ' is-on' : ''}`}
            onClick={save}
            disabled={autosave && saved && !dirty}
            title={dirty ? 'You have unsaved changes' : 'All changes saved'}
          >
            {saved ? 'Saved ✓' : autosave ? 'Save now' : dirty ? 'Save •' : 'Save'}
          </button>
          <button className="cs-btn cs-btn-primary cs-btn-sm" disabled={!clips.length || rendering} onClick={publish}>
            {rendering ? 'Publishing…' : unpublished ? 'Publish' : 'Republish'}
          </button>
        </div>
      </div>

      {tab === 'color' && (
        <LiveGradeStudio
          clips={clips}
          movieId={id}
          sdkPreviews={movie?.sdkPreviews || null}
          movieOutput={movie?.output || null}
          movieGrade={gradeState}
          onClipGrade={onClipGrade}
          onMovieGrade={onMovieGrade}
          assetLooks={assetLooks}
          onApplyAssetLook={applyAssetLook}
          onClearClipLook={clearClipLook}
          onClipSpeed={(cid, speed) => patchClip(cid, { speed })}
          onTime={handleStudioTime}
          seekRef={studioSeekRef}
          ctlRef={colorCtlRef}
          onPlayingChange={handlePlayingChange}
          footer={renderTimeline(false, true)}
          initialTime={curTimeRef.current}
          initialPlaying={playStateRef.current}
          onUndo={undo}
          onRedo={redo}
          canUndo={canUndo}
          canRedo={canRedo}
          sideHidden={!tabVis.side}
          onToggleSide={() => togglePanel('color', 'side')}
        />
      )}

      {tab === 'reframe' && (
        <ColorReframeStudio
          id={id}
          movie={movie}
          clips={clips}
          onClipReframe={onClipReframe}
          initialTab="reframe"
          onTime={handleStudioTime}
          seekRef={studioSeekRef}
          ctlRef={reframeCtlRef}
          onPlayingChange={handlePlayingChange}
          footer={renderTimeline(false, true)}
          initialTime={editToMaster(clips, curTimeRef.current)}
          initialPlaying={playStateRef.current}
          sideHidden={!tabVis.side}
          onToggleSide={() => togglePanel('reframe', 'side')}
        />
      )}

      {tab === 'review' && (
        <ReviewExport
          movie={movie}
          movieId={id}
          clips={clips}
          songs={songs}
          movieGrade={gradeState}
          intro={intro}
          outro={outro}
          hasOriginalAudio={hasOriginalAudio}
          output={output}
          unpublished={unpublished}
          rendering={rendering}
          onPublish={publish}
          master={movie.master || {}}
          masterMissing={masterMissing}
          masterStale={masterStale}
          masterBuilding={masterBuilding}
          onBuildPreview={buildPreview}
          reframe={movie.reframe || {}}
          clipReframes={clipReframes}
          exports={socialExports || []}
          pubCost={pubCost}
          onGoTab={setTab}
          onOpenClip={openClipInStudio}
          onGrabStill={(cid) => { const c = clips.find((x) => x.id === cid); if (c) openGrab(c, c.inSec || 0); }}
          grabLoadingId={grabLoading}
          onOutput={(patch) => setOutput((o) => ({ ...o, ...patch }))}
        />
      )}

      {tab === 'assemble' && (
      <div className={`cs-studio cs-studio-assemble${tabVis.side ? '' : ' cs-side-off'}`}>
      <div className="cs-studio-main">
        {/* ---- PREVIEW ---- */}
        {clips.length ? (
          <CompositionPlayer
            key={clips.map((c) => c.id).join(',')}
            clips={playerClips}
            songs={songs}
            hasOriginalAudio={hasOriginalAudio}
            intro={intro}
            outro={outro}
            poster={posterBg}
            onTime={handlePreviewTime}
            seekRef={playerSeekRef}
            ctlRef={playerCtlRef}
            onPlayingChange={handlePlayingChange}
            editing={editSmooth}
            initialTime={curTimeRef.current}
            initialPlaying={playStateRef.current}
          />
        ) : (
          <div className="cs-studio-stage">
            <div className="cs-empty" style={{ border: 'none' }}>
              Add clips to build your movie, then preview it here.
            </div>
          </div>
        )}
        {/* ---- TIMELINE ---- */}
        {renderTimeline(false, true)}
      </div>

      <div className="cs-studio-side">
      {/* ---- CLIPS ---- */}
      <div className="cs-section">
        <div className="cs-section-head">
          <h2>Clips</h2>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <button className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => setPickingClips(true)}>
              ＋ From trips
            </button>
            <label className="cs-btn cs-btn-ghost cs-btn-sm" style={{ cursor: 'pointer' }}>
              ⬆ Upload clip
              <input type="file" accept="video/*" hidden onChange={(e) => uploadClipFile(e.target.files?.[0])} />
            </label>
            <button
              type="button"
              className="cs-panelhide"
              title="Hide this panel (clips, audio, publish) — restore it with the ＋ clips chip in the timeline toolbar"
              aria-label="Hide side panel"
              onClick={() => togglePanel('assemble', 'side')}
            >
              −
            </button>
          </div>
        </div>
        <p className="cs-section-sub">
          Add subclips from any trip and arrange them in order. Trim each one to just the part you
          want — they play back-to-back.
        </p>

        {clipBusy && (
          <div style={{ marginBottom: 12 }}>
            <div style={{ color: 'var(--muted)', marginBottom: 6 }}>Uploading clip… {Math.round(clipProgress * 100)}%</div>
            <div className="cs-progress"><div style={{ width: `${Math.round(clipProgress * 100)}%` }} /></div>
          </div>
        )}
        {clipError && <div className="cs-empty" style={{ marginBottom: 12 }}>{clipError}</div>}

        {clips.length === 0 ? (
          <div className="cs-empty">No clips yet. Add footage from your trips to get started.</div>
        ) : (
          clips.map((c, idx) => {
            const dur = c.sourceDurationSec || 0;
            const isOpen = clipExpanded === c.id;
            const infoOpen = clipInfoOpen.has(c.id);
            const len = clipLength(c);
            const speedKeys = clipSpeedKeys(c);
            const ramp = hasSpeedRamp(c);
            // Ramp readout: the SET of key speeds in ramp order, consecutive
            // duplicates collapsed — e.g. "1→4.76→8×".
            const speedSet = [];
            for (const k of speedKeys) {
              const sv = Math.round(k.speed * 100) / 100;
              if (!speedSet.length || Math.abs(speedSet[speedSet.length - 1] - sv) > 0.001) speedSet.push(sv);
            }
            const rampTitle = speedKeys
              .map((k) => `@${fmtTimePrecise(k.t)} src → ${Math.round(k.speed * 100) / 100}×`)
              .join('  ·  ');
            const rf = clipReframe(c);
            const rfZoomed = rf.manualCrop.scale < 0.999 || rf.zoomKeys.some((k) => k.scale < 0.999);
            const fadeInSec = (c.fadeIn && Number(c.fadeIn.sec)) || 0;
            const fadeOutSec = (c.fadeOut && Number(c.fadeOut.sec)) || 0;
            return (
              <div
                key={c.id}
                className={`cs-songrow cs-cliprow${selectedClipId === c.id ? ' is-selected' : ''}`}
                onClick={() => setSelectedClipId(c.id)}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap' }}>
                  <button
                    className={`cs-clipexp${infoOpen ? ' is-open' : ''}`}
                    title={infoOpen ? 'Hide clip details' : 'Show every parameter, metadata field and edit on this clip'}
                    onClick={(e) => { e.stopPropagation(); toggleClipInfo(c.id); }}
                  >{infoOpen ? '▾' : '▸'}</button>
                  <span
                    className="cs-step-num"
                    style={{ background: CLIP_COLORS[idx % CLIP_COLORS.length], color: '#04201c', cursor: 'pointer' }}
                    title="Select this clip"
                    onClick={(e) => { e.stopPropagation(); setSelectedClipId(c.id); }}
                  >{idx + 1}</span>
                  <ClipRowThumb entry={clipThumbs[c.id]} masterUrl={masterUrlForThumbs} clip={c} />
                  <input className="cs-input" style={{ flex: '1 1 90px', minWidth: 70 }} value={c.label} onChange={(e) => patchClip(c.id, { label: e.target.value })} />
                  <span className="cs-badge">{dur ? fmt(len) : 'measuring…'}</span>
                  {c.useGrade !== false && Array.isArray(c.gradeKeyframes) && c.gradeKeyframes.length >= 2 ? (
                    <span className="cs-badge" title={`Keyframed look — the grade interpolates across ${c.gradeKeyframes.length} keyframes during the bake`} style={{ background: '#142d2a', color: '#41d9c4' }}>
                      🎨×{c.gradeKeyframes.length}
                    </span>
                  ) : c.useGrade !== false && c.grade && (
                    <span className="cs-badge" title="A saved color/light look is baked into this clip" style={{ background: '#142d2a', color: '#41d9c4' }}>
                      🎨
                    </span>
                  )}
                  {rfZoomed && (
                    <span className="cs-badge" title={`Subject reframe baked into the publish (${rf.waypoints.length} waypoint${rf.waypoints.length === 1 ? '' : 's'}, ${rf.zoomKeys.length} zoom key${rf.zoomKeys.length === 1 ? '' : 's'})`} style={{ background: '#1c2440', color: '#7ea6ff' }}>
                      🎯
                    </span>
                  )}
                  {dur > 0 && ramp ? (
                    <span className="cs-badge" title={`Speed ramp — ${rampTitle}`} style={{ background: '#3a2d12', color: '#f5c451' }}>
                      {speedSet.join('→')}×
                    </span>
                  ) : dur > 0 && clipSpeed(c) !== 1 && (
                    <span className="cs-badge" title="Playback speed" style={{ background: '#3a2d12', color: '#f5c451' }}>
                      {clipSpeed(c)}× {clipSpeed(c) < 1 ? 'slow-mo' : 'fast'}
                    </span>
                  )}
                  {clipRealSpeed(c) > 0 && (() => {
                    const rx = clipRealSpeed(c);
                    const atReal = Math.abs(clipSpeed(c) - rx) < 0.02;
                    return (
                      <button
                        className={`cs-badge cs-realbadge${atReal ? ' is-on' : ''}`}
                        title={atReal
                          ? 'Playing at real-life motion'
                          : `Raw slow-mo source. Set ${rx}× to play this clip at real-life motion (click).`}
                        onClick={(e) => { e.stopPropagation(); patchClip(c.id, { speed: rx }); }}
                      >
                        ⏱ {atReal ? '✓' : `${rx}×`}
                      </button>
                    );
                  })()}
                  {(fadeInSec > 0 || fadeOutSec > 0) && (
                    <span className="cs-badge" title={`Clip fades — in: ${fadeInSec.toFixed(1)}s ${(c.fadeIn && c.fadeIn.color) === 'white' ? 'white' : 'black'} · out: ${fadeOutSec.toFixed(1)}s ${(c.fadeOut && c.fadeOut.color) === 'white' ? 'white' : 'black'}`}>
                      🌗
                    </span>
                  )}
                  {(c.dissolveSec || 0) > 0 && idx < clips.length - 1 && (
                    <span className="cs-badge" title={`Cross-dissolves ${Number(c.dissolveSec).toFixed(1)}s into the next clip when published`}>
                      ⇄{Number(c.dissolveSec).toFixed(1)}s
                    </span>
                  )}
                  <span className="cs-clipacts">
                    <button className="cs-btn cs-btn-ghost cs-btn-sm cs-btn-ico" disabled={idx === 0} onClick={(e) => { e.stopPropagation(); moveClip(idx, -1); }} title="Move up">⬆</button>
                    <button className="cs-btn cs-btn-ghost cs-btn-sm cs-btn-ico" disabled={idx === clips.length - 1} onClick={(e) => { e.stopPropagation(); moveClip(idx, 1); }} title="Move down">⬇</button>
                    <button
                      className={`cs-btn cs-btn-ghost cs-btn-sm cs-btn-ico${isOpen ? ' is-on' : ''}`}
                      onClick={(e) => { e.stopPropagation(); setSelectedClipId(c.id); setClipExpanded(isOpen ? null : c.id); }}
                      disabled={!dur}
                      title={isOpen ? 'Close the trim / speed / fades editor' : 'Trim this clip — in/out points, speed, fades, dissolve'}
                    >✂️</button>
                    {c.assetId && c.tripId && (
                      <button
                        className="cs-btn cs-btn-ghost cs-btn-sm cs-btn-ico"
                        onClick={(e) => { e.stopPropagation(); openGrab(c, c.inSec || 0); }}
                        disabled={grabLoading === c.id}
                        title="Grab an editable still from this clip — with its color/light look + crop/zoom reframe — and save it as an exportable photo"
                      >
                        {grabLoading === c.id ? '⏳' : '📸'}
                      </button>
                    )}
                    {c.assetId && c.tripId && (
                      <button
                        className="cs-btn cs-btn-ghost cs-btn-sm cs-btn-ico"
                        onClick={(e) => { e.stopPropagation(); extractProofStrip(c, idx); }}
                        disabled={proofBusy === c.id || !dur}
                        title={`Extract ${PROOF_FRAMES} frames across this clip as a reviewable proof strip — dial grade + framing per frame, then apply them back as bake keyframes`}
                      >
                        {proofBusy === c.id ? '⏳' : '🎞'}
                      </button>
                    )}
                    <button
                      className="cs-btn cs-btn-ghost cs-btn-sm cs-btn-ico"
                      onClick={(e) => { e.stopPropagation(); grabClipFrame(c); }}
                      disabled={(clipGrab.busy && clipGrab.id === c.id) || !c.s3Key}
                      title={
                        c.s3Key
                          ? `Quick export a full-resolution still at the current playhead (${fmt(previewTime)}) — raw, opens in a new tab · ~10–20s`
                          : c.isRawVideo
                          ? 'Full-res stills from RED clips aren’t supported yet'
                          : 'Frame export needs raw footage'
                      }
                    >
                      {clipGrab.busy && clipGrab.id === c.id ? '⏳' : '🖼'}
                    </button>
                    <button className="cs-btn cs-btn-ghost cs-btn-sm cs-btn-ico" onClick={(e) => { e.stopPropagation(); deleteClip(c.id); }} title="Remove this clip from the movie">🗑</button>
                  </span>
                </div>

                {infoOpen && (
                  <div className="cs-clipmeta" onClick={(e) => e.stopPropagation()}>
                    <div className="cs-clipmeta-grid">
                      <div>
                        <label>Source</label>
                        <span title={c.assetId ? `Trip ${c.tripId || '—'} · asset ${c.assetId}` : (c.s3Key || c.url || '—')}>
                          {c.assetId ? `${c.label || 'asset'} · ${String(c.assetId).slice(0, 10)}…` : c.s3Key ? 'uploaded file' : c.url ? 'external url' : '—'}
                        </span>
                      </div>
                      <div>
                        <label>Trim (src)</label>
                        <span>{fmtTimePrecise(c.inSec || 0)} → {fmtTimePrecise(c.outSec || dur)} of {fmt(dur)}</span>
                      </div>
                      <div>
                        <label>Timeline</label>
                        <span>{fmt(len)} from {fmtTimePrecise(clipSourceLength(c))} src</span>
                      </div>
                      <div>
                        <label>fps</label>
                        <span>{c.fps || c.srcFps ? snapFps(c.fps || c.srcFps) : '—'}{c.isRawVideo ? ' · RED' : ''}</span>
                      </div>
                      <div>
                        <label>Speed</label>
                        <span>{ramp ? `ramp ${speedSet.join('→')}×` : `${clipSpeed(c)}×${clipSpeed(c) === 1 ? '' : clipSpeed(c) < 1 ? ' slow-mo' : ' fast'}`}</span>
                      </div>
                      <div>
                        <label>Real motion</label>
                        <span>{clipRealSpeed(c) > 0 ? `${clipRealSpeed(c)}× = real` : '—'}</span>
                      </div>
                      <div>
                        <label>Fade in</label>
                        <span>{fadeInSec > 0 ? `${fadeInSec.toFixed(1)}s ${(c.fadeIn && c.fadeIn.color) === 'white' ? '⚪ white' : '⚫ black'}` : '—'}</span>
                      </div>
                      <div>
                        <label>Fade out</label>
                        <span>{fadeOutSec > 0 ? `${fadeOutSec.toFixed(1)}s ${(c.fadeOut && c.fadeOut.color) === 'white' ? '⚪ white' : '⚫ black'}` : '—'}</span>
                      </div>
                      <div>
                        <label>Dissolve</label>
                        <span>{(c.dissolveSec || 0) > 0 && idx < clips.length - 1 ? `${Number(c.dissolveSec).toFixed(1)}s → next clip` : '—'}</span>
                      </div>
                      <div>
                        <label>Reframe</label>
                        <span>
                          {rfZoomed
                            ? `${rf.cropMode === 'manual' ? 'static crop' : 'subject'}${rf.waypoints.length ? ` · ${rf.waypoints.length} waypoint${rf.waypoints.length === 1 ? '' : 's'}` : ''}${rf.zoomKeys.length ? ` · ${rf.zoomKeys.length} zoom key${rf.zoomKeys.length === 1 ? '' : 's'}` : ''}`
                            : '—'}
                        </span>
                      </div>
                      <div>
                        <label>Grade</label>
                        <span>
                          {c.useGrade !== false && Array.isArray(c.gradeKeyframes) && c.gradeKeyframes.length >= 2
                            ? `keyframed ×${c.gradeKeyframes.length}`
                            : c.grade
                              ? (c.useGrade !== false ? 'saved look' : 'saved look (off — raw)')
                              : 'none'}
                        </span>
                      </div>
                      <div>
                        <label>Thumb frame</label>
                        <span>{c.thumbFrac != null ? `${Math.round(c.thumbFrac * 100)}% into clip` : 'auto (50%)'}</span>
                      </div>
                    </div>
                    {speedKeys.length >= 2 && (
                      <div className="cs-clipmeta-keys">
                        <label>Speed keys</label>
                        {speedKeys.map((k, ki) => (
                          <span key={ki} className="cs-badge" title={`Source second ${fmtTimePrecise(k.t)} plays at ${Math.round(k.speed * 100) / 100}×`}>
                            @{fmtTimePrecise(k.t)} · {Math.round(k.speed * 100) / 100}×
                          </span>
                        ))}
                      </div>
                    )}
                    {c.assetId && c.tripId && (
                      <div className="cs-cliplook">
                        {c.grade ? (
                          <>
                            <div className="cs-segment cs-segment-sm">
                              <button className={c.useGrade !== false ? 'on' : ''} onClick={() => setClipLook(c.id, true)} title="Use the saved color/light look">
                                Saved look
                              </button>
                              <button className={c.useGrade === false ? 'on' : ''} onClick={() => setClipLook(c.id, false)} title="Use the raw, ungraded source">
                                Raw
                              </button>
                            </div>
                            {c.gradeLocked !== true && !(Array.isArray(c.gradeKeyframes) && c.gradeKeyframes.length) && (
                              <button
                                className="cs-btn cs-btn-ghost cs-btn-sm"
                                onClick={(e) => { e.stopPropagation(); clearClipLook(c.id); }}
                                title="This look was copied from the source asset automatically (older editor behavior) — if the file is already graded this doubles the look. Remove it to publish this clip raw."
                              >
                                ⚠ Copied from asset — remove
                              </button>
                            )}
                          </>
                        ) : assetLooks[c.assetId] ? (
                          <button
                            className="cs-btn cs-btn-ghost cs-btn-sm"
                            onClick={(e) => { e.stopPropagation(); applyAssetLook(c.id); }}
                            title="The source clip has a saved color/light look that is NOT applied here. Click to copy it onto this movie clip — nothing is ever applied without this click."
                          >
                            🎨 Asset look available — Apply
                          </button>
                        ) : (
                          <span className="cs-help" style={{ margin: 0 }}>No saved look yet — grade this clip to reuse it here.</span>
                        )}
                        <button
                          className="cs-btn cs-btn-ghost cs-btn-sm"
                          onClick={() => navigate(`/clip/${c.tripId}/${c.assetId}/color`)}
                          title="Open the side-by-side color & light editor for this clip"
                        >
                          🎨 {c.grade ? 'Re-edit clip color' : 'Edit clip color'} ↗
                        </button>
                        {c.proofStrip && (
                          <>
                            <button
                              className="cs-btn cs-btn-ghost cs-btn-sm"
                              onClick={(e) => { e.stopPropagation(); navigate('/proofs'); }}
                              title={`Review “${c.proofStrip.sheetName}” on the proof sheets page`}
                            >
                              🔍 Review strip ↗
                            </button>
                            <button
                              className="cs-btn cs-btn-ghost cs-btn-sm"
                              onClick={(e) => { e.stopPropagation(); promoteProofStrip(c); }}
                              disabled={proofBusy === c.id}
                              title="Read the reviewed frames' grade + crop back onto this clip as grade keyframes + reframe waypoints (the publish interpolates smoothly between them)"
                            >
                              {proofBusy === c.id ? 'Working…' : '⬆ Frames → keyframes'}
                            </button>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                )}
                {clipGrab.id === c.id && clipGrab.err && (
                  <div className="cs-help" style={{ color: '#e88', marginTop: 6 }}>{clipGrab.err}</div>
                )}
                {proofNote && proofNote.clipId === c.id && (
                  <div className="cs-help" style={{ color: proofNote.err ? '#e88' : 'var(--surf)', marginTop: 6 }}>
                    {proofNote.err || proofNote.msg}
                  </div>
                )}
                {clipGrab.result?.clipId === c.id && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8, flexWrap: 'wrap' }}>
                    <img
                      src={httpsify(clipGrab.result.url)}
                      alt="grabbed frame"
                      style={{ height: 44, borderRadius: 6, border: '1px solid #2a2a34' }}
                    />
                    <span className="cs-help">
                      Frame {clipGrab.result.frame}
                      {clipGrab.result.approxFps ? ' (≈30 fps)' : ` @ ${Math.round(clipGrab.result.fps)} fps`}
                      {' · clip '}{clipGrab.result.clipIdx + 1} “{clipGrab.result.clipLabel}”
                      {' · '}{clipGrab.result.movieTitle}
                      {' · '}{fmt(clipGrab.result.atSec)} · full resolution
                    </span>
                    <a
                      className="cs-btn cs-btn-ghost cs-btn-sm"
                      href={httpsify(clipGrab.result.url)}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Open
                    </a>
                    <button className="cs-btn cs-btn-primary cs-btn-sm" onClick={downloadGrab}>⤓ Download</button>
                  </div>
                )}

                {isOpen && dur > 0 && (
                  <div style={{ marginTop: 14 }}>
                    <div style={{ fontSize: 13, color: 'var(--muted)' }}>
                      Trim window: {fmt(c.inSec || 0)} → {fmt(c.outSec || dur)} of {fmt(dur)} (keeps {fmt(len)})
                      {(() => {
                        const before = c.inSec || 0;
                        const after = Math.max(0, dur - (c.outSec || dur));
                        return (before > 0.05 || after > 0.05)
                          ? ` · trimmed source available: ${fmt(before)} before + ${fmt(after)} after`
                          : '';
                      })()}
                    </div>
                    <label style={{ fontSize: 13, color: 'var(--muted)', display: 'block', marginTop: 8 }}>
                      Start (in): {fmt(c.inSec || 0)}
                      <input className="cs-slider" type="range" min="0" max={dur} step="0.5" value={c.inSec || 0}
                        onChange={(e) => {
                          const v = Math.min(Number(e.target.value), (c.outSec || dur) - 0.5);
                          patchClip(c.id, { inSec: Math.max(0, v) });
                        }} />
                    </label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4, flexWrap: 'wrap' }}>
                      <button className="cs-btn cs-btn-ghost cs-btn-sm" title="Bring back 1s of source before the current start" disabled={(c.inSec || 0) <= 0}
                        onClick={() => patchClip(c.id, { inSec: Math.max(0, (c.inSec || 0) - 1) })}>−1s</button>
                      <button className="cs-btn cs-btn-ghost cs-btn-sm" title="Trim 1s more off the start" disabled={(c.inSec || 0) + 1.2 >= (c.outSec || dur)}
                        onClick={() => patchClip(c.id, { inSec: Math.min((c.outSec || dur) - 0.2, (c.inSec || 0) + 1) })}>+1s</button>
                      <input
                        className="cs-input" type="number" min={0} max={dur} step="0.01" style={{ width: 110 }}
                        key={`in:${c.id}:${c.inSec || 0}`} defaultValue={Number((c.inSec || 0).toFixed(2))}
                        title="Exact in-point (source seconds)"
                        onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                        onBlur={(e) => {
                          const v = Number(e.currentTarget.value);
                          if (Number.isFinite(v)) patchClip(c.id, { inSec: Math.max(0, Math.min(v, (c.outSec || dur) - 0.2)) });
                        }}
                      />
                      <span className="cs-help" style={{ margin: 0 }}>exact in (s)</span>
                    </div>
                    <label style={{ fontSize: 13, color: 'var(--muted)', display: 'block', marginTop: 8 }}>
                      End (out): {fmt(c.outSec || dur)}
                      <input className="cs-slider" type="range" min="0" max={dur} step="0.5" value={c.outSec || dur}
                        onChange={(e) => {
                          const v = Math.max(Number(e.target.value), (c.inSec || 0) + 0.5);
                          patchClip(c.id, { outSec: Math.min(dur, v) });
                        }} />
                    </label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4, flexWrap: 'wrap' }}>
                      <button className="cs-btn cs-btn-ghost cs-btn-sm" title="Trim 1s more off the end" disabled={(c.outSec || dur) - 1.2 <= (c.inSec || 0)}
                        onClick={() => patchClip(c.id, { outSec: Math.max((c.inSec || 0) + 0.2, (c.outSec || dur) - 1) })}>−1s</button>
                      <button className="cs-btn cs-btn-ghost cs-btn-sm" title="Bring back 1s of source after the current end" disabled={(c.outSec || dur) + 0.01 >= dur}
                        onClick={() => patchClip(c.id, { outSec: Math.min(dur, (c.outSec || dur) + 1) })}>+1s</button>
                      <input
                        className="cs-input" type="number" min={0} max={dur} step="0.01" style={{ width: 110 }}
                        key={`out:${c.id}:${c.outSec || dur}`} defaultValue={Number((c.outSec || dur).toFixed(2))}
                        title="Exact out-point (source seconds)"
                        onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                        onBlur={(e) => {
                          const v = Number(e.currentTarget.value);
                          if (Number.isFinite(v)) patchClip(c.id, { outSec: Math.min(dur, Math.max(v, (c.inSec || 0) + 0.2)) });
                        }}
                      />
                      <span className="cs-help" style={{ margin: 0 }}>exact out (s)</span>
                    </div>
                    <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #23232c' }}>
                      <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 8 }}>
                        {fmt(len * clipSpeed(c))} of source → {fmt(len)} on timeline
                      </div>
                      <ClipInspector clip={c} onSpeed={(sp) => patchClip(c.id, { speed: sp })} />
                    </div>
                    <div style={{ display: 'flex', gap: 24, marginTop: 8, flexWrap: 'wrap' }}>
                      {[['fadeIn', 'Fade in'], ['fadeOut', 'Fade out']].map(([key, lbl]) => {
                        const f = c[key];
                        const sec = (f && Number(f.sec)) || 0;
                        return (
                          <label key={key} style={{ fontSize: 13, color: 'var(--muted)', flex: '1 1 200px' }}>
                            {lbl}{key === 'fadeIn' ? ' (clip start)' : ' (clip end)'}: {sec.toFixed(1)}s
                            <input className="cs-slider" type="range" min="0" max="5" step="0.1" value={sec}
                              onChange={(e) => patchClipFade(c.id, key, e.target.value, f && f.color)} />
                            <span className="cs-segment cs-segment-sm" style={{ marginTop: 4, display: 'inline-flex' }}>
                              <button type="button" className={!f || f.color !== 'white' ? 'on' : ''} disabled={sec <= 0}
                                onClick={() => patchClipFade(c.id, key, sec, 'black')} title={`${lbl} to/from black`}>⚫ Black</button>
                              <button type="button" className={f && f.color === 'white' ? 'on' : ''} disabled={sec <= 0}
                                onClick={() => patchClipFade(c.id, key, sec, 'white')} title={`${lbl} to/from white`}>⚪ White</button>
                            </span>
                          </label>
                        );
                      })}
                    </div>
                    {idx < clips.length - 1 && (
                      <label style={{ fontSize: 13, color: 'var(--muted)', display: 'block', marginTop: 8 }}>
                        Cross-dissolve into next clip: {(c.dissolveSec || 0).toFixed(1)}s
                        <input className="cs-slider" type="range" min="0" max="3" step="0.25" value={c.dissolveSec || 0}
                          onChange={(e) => patchClip(c.id, { dissolveSec: Number(e.target.value) })} />
                        <span className="cs-help" style={{ display: 'block' }}>Baked as a true dissolve when you Publish; the preview approximates it with a brief fade-through at the cut.</span>
                      </label>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* ---- SOUNDTRACKS ---- */}
      <div className="cs-section">
        <div className="cs-section-head">
          <h2>Audio channels</h2>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <button className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => setPicking(true)}>＋ From library</button>
            <button className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => setPickingYt(true)}>＋ From YouTube</button>
            <button className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => songFileRef.current?.click()} disabled={songUpBusy}>
              {songUpBusy ? 'Uploading…' : '＋ Upload'}
            </button>
            <input ref={songFileRef} type="file" accept="audio/*" style={{ display: 'none' }} onChange={uploadSong} />
          </div>
        </div>
        <p className="cs-section-sub">
          This is the <b>baked</b> soundtrack lane — songs here are trimmable, draggable on the
          timeline, and rendered into the movie when you Publish. Each song is its own channel.
          Place one or more along the movie and set per-channel volume and fade in/out; overlap two
          channels (the next starts before the previous ends) to cross-dissolve between them. Gaps
          fall back to the clips’ original audio. To move or trim a song: click its block on the
          timeline (Assemble tab) and drag it — or drag its edges — or use the numeric fields in the
          music toolbar.
        </p>
        {songUpErr && <div className="cs-help" style={{ color: '#e88', marginBottom: 8 }}>{songUpErr}</div>}

        <label style={{ display: 'block', marginBottom: 16, color: '#ccc' }}>
          <input type="checkbox" checked={hasOriginalAudio} onChange={(e) => setHasOriginalAudio(e.target.checked)} />{' '}
          Clips have usable original audio (used where no song is placed)
        </label>

        {songs.length === 0 ? (
          <div className="cs-empty">
            No songs on the timeline yet. Add one here to bake it into the movie — it appears as a
            draggable block on the timeline where you can trim and position it.
            {viewerSongs.length > 0 ? ' (Your viewer soundtrack options below are a separate lane — use “Add to timeline” to bring one over.)' : ''}
          </div>
        ) : (
          songs.map((s) => {
            const isOpen = expanded === s.id;
            const trimEnd = s.trimEnd && s.trimEnd > 0 ? s.trimEnd : 0;
            const ws = s.trimStart || 0;
            const songDur = s.songDurationSec || trimEnd || 0;
            const windowLen = (trimEnd > ws ? trimEnd : songDur) - ws;
            const offset = s.movieOffset || 0;
            return (
              <div key={s.id} className="cs-songrow">
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                  <input className="cs-input" style={{ flex: '1 1 200px' }} value={s.label} onChange={(e) => patchSong(s.id, { label: e.target.value })} />
                  <span className="cs-badge">{SOURCE_BADGE[s.source] || s.source}</span>
                  <button className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => setExpanded(isOpen ? null : s.id)}>
                    {isOpen ? 'Done' : 'Trim / align'}
                  </button>
                  <button className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => removeSong(s.id)}>Remove</button>
                </div>

                <div style={{ marginTop: 12, fontSize: 13, color: 'var(--muted)' }}>
                  Song {fmt(songDur)} · plays {s.loop ? `on repeat from ${fmt(offset)} to the end` : `${fmt(windowLen)} · starts at ${fmt(offset)}`} in a {fmt(movieDur)} movie
                </div>

                <label style={{ display: 'block', marginTop: 10, fontSize: 13, color: 'var(--muted)' }} title="Repeat the trimmed slice until the movie ends (baked into the publish + previewed live)">
                  <input type="checkbox" checked={!!s.loop} onChange={(e) => patchSong(s.id, { loop: e.target.checked })} />{' '}
                  ⟳ Loop this song to the end of the movie
                </label>

                <div style={{ display: 'flex', gap: 28, marginTop: 14, flexWrap: 'wrap' }}>
                  <label style={{ fontSize: 13, color: 'var(--muted)', flex: '1 1 280px' }}>
                    Start in movie: {fmt(offset)}
                    <input className="cs-slider" type="range" min="0" max={Math.max(movieDur || 0, offset, 1)} step="0.5" value={offset}
                      onChange={(e) => patchSong(s.id, { movieOffset: Number(e.target.value) || 0 })} />
                  </label>
                  <label style={{ fontSize: 13, color: 'var(--muted)', flex: '0 0 180px' }}>
                    Volume {Math.round((s.volume ?? 1) * 100)}%
                    <input className="cs-slider" type="range" min="0" max="1" step="0.05" value={s.volume ?? 1}
                      onChange={(e) => patchSong(s.id, { volume: Number(e.target.value) })} />
                  </label>
                </div>
                <div style={{ display: 'flex', gap: 28, marginTop: 10, flexWrap: 'wrap' }}>
                  <label style={{ fontSize: 13, color: 'var(--muted)', flex: '1 1 200px' }}>
                    Fade in: {(s.fadeInSec || 0).toFixed(1)}s
                    <input className="cs-slider" type="range" min="0" max="8" step="0.5" value={s.fadeInSec || 0}
                      onChange={(e) => patchSong(s.id, { fadeInSec: Number(e.target.value) })} />
                  </label>
                  <label style={{ fontSize: 13, color: 'var(--muted)', flex: '1 1 200px' }}>
                    Fade out: {(s.fadeOutSec || 0).toFixed(1)}s
                    <input className="cs-slider" type="range" min="0" max="8" step="0.5" value={s.fadeOutSec || 0}
                      onChange={(e) => patchSong(s.id, { fadeOutSec: Number(e.target.value) })} />
                  </label>
                </div>

                {isOpen && s.source === 'upload' && s.url && (
                  <div style={{ marginTop: 14 }}>
                    <WaveformEditor url={s.url} trimStart={s.trimStart || 0} trimEnd={trimEnd}
                      onChange={({ trimStart, trimEnd: te }) => patchSong(s.id, { trimStart, trimEnd: te })}
                      onDuration={(d) => {
                        const p = { songDurationSec: d };
                        if (!s.trimEnd || s.trimEnd <= 0) p.trimEnd = d;
                        patchSong(s.id, p);
                      }} />
                  </div>
                )}
                {isOpen && s.source === 'youtube' && (
                  <div style={{ marginTop: 14 }}>
                    <label style={{ fontSize: 13, color: 'var(--muted)', display: 'block' }}>
                      Song start: {fmt(ws)}
                      <input className="cs-slider" type="range" min="0" max={Math.max(songDur || 1, ws)} step="1" value={ws}
                        onChange={(e) => patchSong(s.id, { trimStart: Number(e.target.value) || 0 })} />
                    </label>
                    <div className="cs-help" style={{ marginTop: 8 }}>YouTube has no waveform — set the start by ear with the preview above.</div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* ---- VIEWER SOUNDTRACK OPTIONS ---- */}
      <div className="cs-section">
        <div className="cs-section-head">
          <h2>Viewer soundtrack options</h2>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <button className="cs-btn cs-btn-ghost cs-btn-sm" disabled={viewerSongs.length >= 4} onClick={() => setPickingViewer(true)}>＋ From library</button>
            <button className="cs-btn cs-btn-ghost cs-btn-sm" disabled={viewerSongs.length >= 4} onClick={() => setPickingViewerYt(true)}>＋ From YouTube</button>
            <button className="cs-btn cs-btn-ghost cs-btn-sm" disabled={viewerSongUpBusy || viewerSongs.length >= 4} onClick={() => viewerSongFileRef.current?.click()}>
              {viewerSongUpBusy ? 'Uploading…' : '＋ Upload'}
            </button>
            <input ref={viewerSongFileRef} type="file" accept="audio/*" style={{ display: 'none' }} onChange={uploadViewerSong} />
          </div>
        </div>
        <p className="cs-section-sub">
          Add up to 4 tracks viewers can choose from on the watch page. The chosen song plays over the
          movie (the movie’s own audio is muted while a song is playing). These are overlaid live, so
          changing them takes effect immediately — no re-publish needed. <b>Songs in this lane always
          play from their beginning and can’t be trimmed or repositioned</b> — for that, use
          “→ Timeline” to copy one up to the Audio channels lane, where it becomes a draggable,
          trimmable block that’s baked into the publish.
        </p>
        {viewerSongUpErr && <div className="cs-help" style={{ color: '#e88', marginBottom: 8 }}>{viewerSongUpErr}</div>}
        {viewerSongs.length === 0 ? (
          <div className="cs-empty">No viewer songs yet. Viewers will just hear the movie’s own audio.</div>
        ) : (
          <>
            {viewerSongs.map((s, i) => {
              const isDefault = viewerSongDefault ? viewerSongDefault === s.id : i === 0;
              return (
                <div key={s.id} className="cs-songrow" style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                  <button
                    className={`cs-btn cs-btn-sm ${isDefault ? 'cs-btn-primary' : 'cs-btn-ghost'}`}
                    title={isDefault ? 'Plays first' : 'Make this the default'}
                    onClick={() => setViewerSongDefault(s.id)}
                  >
                    {isDefault ? '★ Default' : '☆ Default'}
                  </button>
                  <input className="cs-input" style={{ flex: '1 1 200px' }} value={s.label} onChange={(e) => renameViewerSong(s.id, e.target.value)} />
                  <span className="cs-badge">{SOURCE_BADGE[s.source] || s.source}</span>
                  <span className="cs-help">{s.songDurationSec ? fmt(s.songDurationSec) : ''}</span>
                  <button
                    className="cs-btn cs-btn-ghost cs-btn-sm"
                    title="Copy this song up to the Audio channels lane, where you can trim it, drag it along the timeline, loop it, and bake it into the publish"
                    onClick={() => viewerSongToTimeline(s)}
                  >
                    → Timeline
                  </button>
                  <button className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => removeViewerSong(s.id)}>Remove</button>
                </div>
              );
            })}
            <div className="cs-songrow" style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <button
                className={`cs-btn cs-btn-sm ${viewerSongDefault === 'orig' ? 'cs-btn-primary' : 'cs-btn-ghost'}`}
                title="Start on the movie's own audio; songs are offered as options"
                onClick={() => setViewerSongDefault('orig')}
              >
                {viewerSongDefault === 'orig' ? '★ Default' : '☆ Default'}
              </button>
              <span style={{ flex: '1 1 200px', color: 'var(--muted)' }}>Original audio (no song)</span>
            </div>
          </>
        )}
        <div className="cs-help" style={{ marginTop: 8 }}>{viewerSongs.length}/4 options{viewerSongs.length >= 4 ? ' (max)' : ''}. Remember to Save.</div>
      </div>

      {/* ---- MOVIE FADES ---- */}
      <div className="cs-section">
        <div className="cs-section-head"><h2>Movie fades</h2></div>
        <label style={{ fontSize: 13, color: 'var(--muted)', display: 'block', marginBottom: 12 }}>
          Fade in from black: {intro.toFixed(1)}s
          <input className="cs-slider" type="range" min="0" max="8" step="0.5" value={intro}
            onChange={(e) => setIntro(Number(e.target.value))} />
        </label>
        <label style={{ fontSize: 13, color: 'var(--muted)', display: 'block' }}>
          Fade to black at end: {outro.toFixed(1)}s
          <input className="cs-slider" type="range" min="0" max="8" step="0.5" value={outro}
            onChange={(e) => setOutro(Number(e.target.value))} />
        </label>
      </div>

      {/* ---- PUBLISH ---- */}
      <div className="cs-section">
        <div className="cs-section-head">
          <h2>Publish</h2>
          {render.status && <span className="cs-badge">{render.status}</span>}
        </div>
        <p className="cs-section-sub">
          Bakes your clips + soundtrack (fades, crossfades, fade-to-black) into one smooth movie
          file. Viewers get instant, gap-free playback. Editing the title, poster, hero, or other
          details saves instantly and never needs a re-publish — only clip/soundtrack changes do.
        </p>

        {/* ---- OUTPUT QUALITY ---- */}
        <div className="cs-output" style={{ marginBottom: 14 }}>
          <div className="cs-output-head">
            <strong>Output quality</strong>
            <span className="cs-help">Controls the final published resolution, frame rate and bitrate.</span>
          </div>
          <div className="cs-output-grid">
            <div className="cs-field" style={{ margin: 0 }}>
              <label>Resolution</label>
              <select
                className="cs-input"
                value={output.height}
                onChange={(e) => setOutput((o) => ({ ...o, height: Number(e.target.value) }))}
              >
                {OUTPUT_RESOLUTIONS.map((r) => (
                  <option key={r.value} value={r.value}>{r.label}</option>
                ))}
              </select>
            </div>
            <div className="cs-field" style={{ margin: 0 }}>
              <label>Aspect ratio</label>
              <select
                className="cs-input"
                value={output.aspect || 'source'}
                onChange={(e) => setOutput((o) => ({ ...o, aspect: e.target.value }))}
              >
                {OUTPUT_ASPECT.map((a) => (
                  <option key={a.value} value={a.value}>{a.label}</option>
                ))}
              </select>
            </div>
            <div className="cs-field" style={{ margin: 0 }}>
              <label>Frame rate</label>
              <select
                className="cs-input"
                value={output.fps}
                onChange={(e) => setOutput((o) => ({ ...o, fps: Number(e.target.value) }))}
              >
                {OUTPUT_FPS.map((f) => (
                  <option key={f.value} value={f.value}>{f.label}</option>
                ))}
              </select>
            </div>
            <div className="cs-field" style={{ margin: 0 }}>
              <label>Bitrate / quality</label>
              <select
                className="cs-input"
                value={output.quality}
                onChange={(e) => setOutput((o) => ({ ...o, quality: e.target.value }))}
              >
                {OUTPUT_QUALITY.map((q) => (
                  <option key={q.value} value={q.value}>{q.label}</option>
                ))}
              </select>
            </div>
          </div>
          {hasRedClips && (
            <label className="cs-output-red" style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
              <input
                type="checkbox"
                checked={output.height === 0 || output.redSource === 'raw'}
                disabled={output.height === 0}
                onChange={(e) => setOutput((o) => ({ ...o, redSource: e.target.checked ? 'raw' : 'proxy' }))}
              />
              <span>
                Re-decode RED clips from the original RAW <code>.R3D</code> for maximum fidelity
                {output.height === 0 && ' (required for “Max” resolution)'}
                <span className="cs-help" style={{ display: 'block' }}>
                  Slower & heavier than using the existing UHD proxy, but avoids any proxy compression.
                </span>
              </span>
            </label>
          )}
          <label className="cs-output-red" style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
            <input
              type="checkbox"
              checked={!!output.prores}
              onChange={(e) => setOutput((o) => ({ ...o, prores: e.target.checked }))}
            />
            <span>
              Also export a ProRes 422 HQ archival master
              <span className="cs-help" style={{ display: 'block' }}>
                A 10-bit mezzanine <code>.mov</code> for re-edits / external grading. Best quality, but
                very large files.
              </span>
            </span>
          </label>

          {/* ---- BAKE SETTINGS (publish tail) ---- */}
          {(() => {
            const cfgR = gradingConfig().render || {};
            const defHeights = Array.isArray(cfgR.renditionHeights) ? cfgR.renditionHeights : [2160, 1440, 1080];
            const defPreset = cfgR.renditionPreset || 'fast';
            const heights = Array.isArray(output.renditionHeights) ? output.renditionHeights : defHeights;
            const preset = output.renditionPreset || defPreset;
            const isDefault = !Array.isArray(output.renditionHeights) && !output.renditionPreset;
            const toggleH = (h) => {
              const next = heights.includes(h) ? heights.filter((x) => x !== h) : [...heights, h];
              setOutput((o) => ({ ...o, renditionHeights: next.sort((a, b) => b - a) }));
            };
            return (
              <div className="cs-output-red" style={{ marginTop: 10 }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                  <strong style={{ fontSize: 13 }}>Bake settings</strong>
                  <span className="cs-help">
                    Download renditions + encode speed for this movie.{' '}
                    {isDefault ? 'Following the live defaults.' : (
                      <button type="button" className="cs-btn cs-btn-ghost cs-btn-sm" style={{ padding: '0 6px' }}
                        onClick={() => setOutput((o) => ({ ...o, renditionHeights: null, renditionPreset: null }))}>
                        ↺ reset to defaults
                      </button>
                    )}
                  </span>
                </div>
                <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'center', marginTop: 6 }}>
                  <span style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                    <span className="cs-help">Renditions:</span>
                    {[2880, 2160, 1440, 1080].map((h) => (
                      <label key={h} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13 }}>
                        <input type="checkbox" checked={heights.includes(h)} onChange={() => toggleH(h)} />
                        {h}p
                      </label>
                    ))}
                  </span>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                    <span className="cs-help">Encode speed:</span>
                    <select className="cs-input" style={{ width: 'auto', padding: '2px 6px' }} value={preset}
                      onChange={(e) => setOutput((o) => ({ ...o, renditionPreset: e.target.value }))}>
                      <option value="fast">fast — recommended</option>
                      <option value="medium">medium — ~2-3× slower, ~5-10% smaller</option>
                      <option value="slow">slow — smallest, much slower</option>
                    </select>
                  </label>
                </div>
                <span className="cs-help" style={{ display: 'block', marginTop: 4 }}>
                  The full-resolution master always publishes regardless — renditions are the extra
                  downloadable sizes on the Watch page.
                  {heights.includes(2880) && ' ⏱ 2880p is HEVC and adds ~20–25 min to every publish.'}
                </span>
              </div>
            );
          })()}
          <div className="cs-output-cost">
            <div>
              <strong>Est. processing cost</strong>
              <span className="cs-help" style={{ display: 'block' }}>
                {pubCost.wantRaw
                  ? `RAW re-decode fans out across ephemeral tasks (~${pubCost.jobs.reduce((s, j) => s + j.chunks, 0)} task${pubCost.jobs.length ? '' : 's'}); composite runs on the always-on box.`
                  : 'Renders on the always-on backend — no extra per-publish charge.'}
              </span>
            </div>
            <div className="cs-output-cost-amt">{fmtUSD(pubCost.usd)}</div>
          </div>
          {(output.height === 0 || output.height >= 1440 || output.redSource === 'raw') && (
            <div className="cs-output-warn">
              ⏱ High-resolution / RAW renders are far heavier — expect substantially longer publish
              times{output.height === 0 ? ' and very large files (full sensor resolution)' : ''}.
            </div>
          )}
          {movie.download?.status === 'ready' && movie.download?.key && (
            <div className="cs-output-dl">
              <button
                type="button"
                className="cs-btn cs-btn-ghost cs-btn-sm"
                onClick={() =>
                  downloadAsset({
                    key: movie.download.key,
                    name: `${meta.title || 'movie'} ${movie.download.height || ''}p`.trim(),
                    bucket: 'movies',
                  })
                }
              >
                ⤓ Download master ({movie.download.height ? `${movie.download.height}p` : 'MP4'}
                {movie.download.fps ? ` · ${movie.download.fps}fps` : ''}
                {movie.download.sizeBytes ? ` · ${(movie.download.sizeBytes / 1e9).toFixed(2)} GB` : ''})
              </button>
            </div>
          )}
          {movie.downloadRenditions?.status === 'ready' && (movie.downloadRenditions.items || []).map((r) => (
            <div className="cs-output-dl" key={r.key}>
              <button
                type="button"
                className="cs-btn cs-btn-ghost cs-btn-sm"
                onClick={() =>
                  downloadAsset({
                    key: r.key,
                    name: `${meta.title || 'movie'} ${r.height}p`.trim(),
                    bucket: 'movies',
                  })
                }
              >
                ⤓ Download {r.height}p ({r.codec === 'hevc' ? 'HEVC' : 'H.264'}
                {r.sizeBytes ? ` · ${r.sizeBytes >= 1e9 ? `${(r.sizeBytes / 1e9).toFixed(2)} GB` : `${Math.round(r.sizeBytes / 1e6)} MB`}` : ''})
              </button>
            </div>
          ))}
          {movie.proresMaster?.status === 'ready' && movie.proresMaster?.key && (
            <div className="cs-output-dl">
              <button
                type="button"
                className="cs-btn cs-btn-ghost cs-btn-sm"
                onClick={() =>
                  downloadAsset({
                    key: movie.proresMaster.key,
                    name: `${meta.title || 'movie'} ${movie.proresMaster.height || ''}p ProRes`.trim(),
                    bucket: 'movies',
                  })
                }
              >
                ⤓ Download ProRes 422 HQ ({movie.proresMaster.height ? `${movie.proresMaster.height}p` : 'MOV'}
                {movie.proresMaster.fps ? ` · ${movie.proresMaster.fps}fps` : ''}
                {movie.proresMaster.sizeBytes ? ` · ${(movie.proresMaster.sizeBytes / 1e9).toFixed(2)} GB` : ''})
              </button>
            </div>
          )}
          {movie.download?.status === 'rendering' && (
            <div className="cs-help" style={{ marginTop: 6 }}>Building downloadable master…</div>
          )}
          {movie.download?.status === 'error' && (
            <div className="cs-help" style={{ marginTop: 6, color: 'var(--accent)' }}>
              Downloadable master failed: {movie.download.message || 'unknown error'}
            </div>
          )}
        </div>

        {render.status === 'rendering' && render.jobId && (
          <div style={{ marginTop: 10 }}>
            <JobFanoutGraph jobId={render.jobId} height={320} title="RAW re-decode · live fan-out" />
          </div>
        )}
        {render.status === 'rendering' && (
          <div>
            <div className="cs-help">
              {renderEtaLabel(render, now)} — you can leave this page; it keeps rendering.
            </div>
            <div className="cs-progress" style={{ marginTop: 8 }}>
              <div style={{ width: `${render.progress || 3}%` }} />
            </div>
          </div>
        )}
        {render.status === 'queued' && <div className="cs-help">Queued — the render worker will pick this up shortly.</div>}
        {render.status === 'ready' && (
          <div style={{ color: 'var(--surf)', fontSize: 14 }}>
            ✓ Published. The Watch page now plays the rendered movie.
            {compositionDirty && (
              <span style={{ display: 'block', color: '#e0a414', marginTop: 4 }}>
                You’ve changed clips/soundtrack since the last publish — re-publish to bake it in.
              </span>
            )}
          </div>
        )}
        {render.status === 'error' && (
          <div className="cs-empty" style={{ marginTop: 8 }}>Render failed: {render.message || 'unknown error'}</div>
        )}
        <BakeReceipt receipt={render.receipt} style={{ marginTop: 10 }} />
        <div style={{ marginTop: 12, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <button className="cs-btn cs-btn-primary" disabled={!clips.length || rendering} onClick={publish}>
            {rendering ? 'Publishing…' : render.status === 'ready' ? 'Re-publish' : 'Publish movie'}
          </button>
          <button
            className="cs-btn cs-btn-ghost"
            disabled={!clips.length || resolveExp.busy}
            onClick={exportToResolve}
            title="Download an FCPXML timeline + LUTs to finish this edit in DaVinci Resolve (~10–30s to build the bundle)"
          >
            {resolveExp.busy ? 'Building export… (~20s)' : '⤓ Export to DaVinci Resolve'}
          </button>
          {RESOLVE_FINISH_ON && (
            <button
              className="cs-btn cs-btn-ghost"
              disabled={!clips.length || proFinish.busy}
              onClick={finishInResolve}
              title="Render this edit on the managed DaVinci Resolve Studio node (a full render — typically several minutes; progress shows below)"
            >
              {proFinish.busy ? 'Queuing…' : '◆ Pro finish (Resolve)'}
            </button>
          )}
        </div>
        {RESOLVE_FINISH_ON && movie.resolveFinish?.status && (
          <div className="cs-help" style={{ marginTop: 6 }}>
            Pro finish: {movie.resolveFinish.status}
            {movie.resolveFinish.status === 'ready' && movie.resolveFinish.url && (
              <> — <a href={movie.resolveFinish.url} target="_blank" rel="noreferrer">download master</a></>
            )}
            {movie.resolveFinish.status === 'error' && movie.resolveFinish.message
              ? ` (${movie.resolveFinish.message})`
              : ''}
          </div>
        )}
        {proFinish.err && (
          <div className="cs-help" style={{ marginTop: 6, color: 'var(--accent)' }}>
            Pro finish failed: {proFinish.err}
          </div>
        )}
        {resolveExp.err && (
          <div className="cs-help" style={{ marginTop: 6, color: 'var(--accent)' }}>
            Resolve export failed: {resolveExp.err}
          </div>
        )}
        <div className="cs-help" style={{ marginTop: 6 }}>
          Resolve export bundles an FCPXML timeline, your per-clip color LUTs, and a media manifest
          (with download links) into a .zip you open in your own copy of DaVinci Resolve — free or Studio.
        </div>
        <div className="cs-help" style={{ marginTop: 8 }}>
          Note: YouTube tracks can’t be baked into the downloadable file (licensed stream only), but
          they still play in-app — synced over the video during playback. Uploaded songs are baked
          into the final mix.
        </div>
      </div>

      {/* ---- EDIT VERSIONS ---- */}
      <div className="cs-section">
        <div className="cs-section-head">
          <h2>Edit versions</h2>
          <button
            className="cs-btn cs-btn-ghost cs-btn-sm"
            disabled={versionBusy === 'save'}
            onClick={async () => {
              const label = window.prompt('Label this version (optional):', '');
              if (label === null) return;
              setVersionBusy('save');
              try { await saveEditVersion(id, label.trim()); } catch { /* list stays live */ }
              setVersionBusy('');
            }}
          >
            {versionBusy === 'save' ? 'Saving…' : '＋ Save current version'}
          </button>
        </div>
        <p className="cs-section-sub">
          Every publish snapshots the exact edit it baked — clips, trims, speed ramps, grades,
          soundtrack, output settings. Restore any version to bring that whole edit back (your
          current edit is auto-saved first, so restores are never one-way).
        </p>
        {!editVersions ? (
          <div className="cs-help">Loading versions…</div>
        ) : !editVersions.length ? (
          <div className="cs-help">No saved versions yet — the next publish will create one automatically.</div>
        ) : (
          <div className="cs-versions">
            {editVersions.map((v) => (
              <div key={v.id} className="cs-version-row">
                <span className={`cs-badge ${v.reason === 'publish' ? 'cs-badge-publish' : ''}`}>
                  {v.reason === 'publish' ? 'publish' : v.reason === 'manual' ? 'manual' : 'auto'}
                </span>
                <span className="cs-version-label" title={new Date(v.takenAt).toLocaleString()}>
                  <strong>{v.label || new Date(v.takenAt).toLocaleString()}</strong>
                  <em>
                    {new Date(v.takenAt).toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}
                    {v.clipCount ? ` · ${v.clipCount} clips` : ''}
                    {v.durationSec ? ` · ${fmt(v.durationSec)}` : ''}
                  </em>
                </span>
                <button
                  className="cs-btn cs-btn-ghost cs-btn-sm"
                  disabled={!!versionBusy || rendering}
                  title={rendering ? 'Wait for the current render to finish' : 'Restore this edit (current edit is auto-saved first)'}
                  onClick={async () => {
                    if (!window.confirm(`Restore “${v.label || 'this version'}”?\n\nYour current edit will be auto-saved as a new version first.`)) return;
                    setVersionBusy(v.id);
                    try { await restoreEditVersion(id, v); } catch { /* keep current edit on failure */ }
                    setVersionBusy('');
                  }}
                >
                  {versionBusy === v.id ? 'Restoring…' : '↩ Restore'}
                </button>
                <button
                  className="cs-btn cs-btn-ghost cs-btn-sm"
                  disabled={!!versionBusy}
                  title="Delete this saved version"
                  onClick={() => {
                    if (window.confirm('Delete this saved version? This cannot be undone.')) {
                      deleteEditVersion(id, v.id).catch(() => {});
                    }
                  }}
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
      </div>
      </div>
      )}

      {tab === 'details' && (<>
      {/* ---- DETAILS ---- */}
      <div className="cs-section">
        <div className="cs-section-head"><h2>Details</h2></div>
        <div className="cs-row-2">
          <div>
            <div className="cs-poster" style={posterBg ? { backgroundImage: `url(${posterBg})` } : undefined}>
              {!posterBg && 'No poster image'}
            </div>
            <div className="cs-help">Poster preview (uses hero, else thumbnail)</div>
          </div>
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 120px', gap: 12 }}>
              <div className="cs-field" style={{ margin: 0 }}>
                <label>Title</label>
                <input className="cs-input" value={meta.title} onChange={setM('title')} />
              </div>
              <div className="cs-field" style={{ margin: 0 }}>
                <label>Year</label>
                <input className="cs-input" type="number" value={meta.year} onChange={setM('year')} />
              </div>
            </div>
            <div className="cs-field" style={{ marginTop: 12 }}>
              <label>Location</label>
              <input className="cs-input" value={meta.location} onChange={setM('location')} />
            </div>
            <div className="cs-field">
              <label>Synopsis</label>
              <textarea className="cs-input" rows={3} value={meta.synopsis} onChange={setM('synopsis')} style={{ resize: 'vertical' }} />
            </div>
            <div className="cs-field">
              <label>Categories (comma-separated)</label>
              <input className="cs-input" value={meta.categories} onChange={setM('categories')} />
            </div>
            <div className="cs-field">
              <label>Thumbnail image URL</label>
              <input className="cs-input" value={meta.thumbUrl} onChange={setM('thumbUrl')} />
            </div>
            <div className="cs-field">
              <label>Hero image URL (large banner)</label>
              <input className="cs-input" value={meta.heroUrl} onChange={setM('heroUrl')} />
            </div>
            <label style={{ color: '#ccc', fontSize: 14 }}>
              <input type="checkbox" checked={meta.featured} onChange={setM('featured')} /> Feature on the home hero banner
            </label>
            <div className="cs-help" style={{ marginTop: 4 }}>
              When featured, this movie fills the homepage hero. Configure its frame &amp; autoplay below.
            </div>
          </div>
        </div>
      </div>

      <div className="cs-section" id="hero">
        <div className="cs-section-head">
          <h2>Homepage hero {meta.featured ? '' : '(not currently featured)'}</h2>
        </div>
        <p className="cs-section-sub">
          The homepage shows this movie&apos;s poster frame, then autoplays a muted segment after a delay.
          {!meta.featured && ' Tick “Feature on the home hero banner” above to show it on the homepage.'}
        </p>
        <div className="cs-row-2">
          <div>
            <HeroFramePreview clips={clips} time={hero.startSec} posterUrl={hero.posterUrl} />
            <div className="cs-help">Frame at segment start ({fmt(hero.startSec)}) — drag “Segment start” to choose</div>
          </div>
          <div>
            <div className="cs-field" style={{ marginTop: 0 }}>
              <label>Delay before autoplay: {hero.delaySec}s</label>
              <input className="cs-slider" type="range" min="0" max="15" step="1"
                value={hero.delaySec}
                onChange={(e) => setHero((h) => ({ ...h, delaySec: Number(e.target.value) }))} />
            </div>
            <div className="cs-field">
              <label>Segment start: {fmt(hero.startSec)}</label>
              <input className="cs-slider" type="range" min="0" max={Math.max(movieDur, 1)} step="0.5"
                value={Math.min(hero.startSec, movieDur)}
                onChange={(e) => setHero((h) => ({ ...h, startSec: Number(e.target.value) }))} />
              <button className="cs-btn cs-btn-ghost cs-btn-sm" style={{ marginTop: 6 }}
                onClick={() => setHero((h) => ({ ...h, startSec: Math.round(previewTime * 2) / 2 }))}>
                Use current preview time ({fmt(previewTime)})
              </button>
            </div>
            <div className="cs-field">
              <label>Segment length: {hero.durationSec}s</label>
              <input className="cs-slider" type="range" min="4" max="30" step="1"
                value={hero.durationSec}
                onChange={(e) => setHero((h) => ({ ...h, durationSec: Number(e.target.value) }))} />
            </div>
            <div className="cs-field">
              <label>Hero poster image URL (the selectable frame shown before autoplay)</label>
              <input className="cs-input" value={hero.posterUrl}
                placeholder="Defaults to the hero/thumbnail image"
                onChange={(e) => setHero((h) => ({ ...h, posterUrl: e.target.value }))} />
              <button className="cs-btn cs-btn-ghost cs-btn-sm" style={{ marginTop: 6 }}
                disabled={heroGrabBusy}
                onClick={grabHeroPoster}>
                {heroGrabBusy ? 'Grabbing…' : 'Grab poster from this frame (max-res)'}
              </button>
              {heroGrabErr && <div className="cs-help" style={{ color: '#e88' }}>{heroGrabErr}</div>}
            </div>
          </div>
        </div>
      </div>

      {/* ---- VERSIONS (branches / lineage / diff / cherry-pick) ---- */}
      <MovieVersionsPanel
        movie={{ id, ...movie }}
        navigate={navigate}
        focusSignal={versionsFocus}
        onPullClip={pullClipFromVersion}
        onAdoptSongs={adoptSongsFromVersion}
        onAdoptGrade={adoptGradeFromVersion}
        onAdoptOutput={adoptOutputFromVersion}
      />

      {/* ---- DANGER ---- */}
      <div className="cs-section" style={{ borderColor: '#3a1c20' }}>
        <div className="cs-section-head"><h2 style={{ color: '#e88' }}>Delete movie</h2></div>
        <p className="cs-section-sub">Removes this movie from the catalog. Uploaded files are kept in storage.</p>
        <button className="cs-btn cs-btn-danger" onClick={onDelete}>Delete “{meta.title || movie.title}”</button>
      </div>

      </>)}

      {picking && <SongLibraryModal onClose={() => setPicking(false)} onPick={attachFromLibrary} />}
      {pickingYt && <YouTubeSearchModal onClose={() => setPickingYt(false)} onPick={attachYouTube} />}
      {pickingViewer && <SongLibraryModal onClose={() => setPickingViewer(false)} onPick={addViewerFromLibrary} />}
      {pickingViewerYt && <YouTubeSearchModal onClose={() => setPickingViewerYt(false)} onPick={addViewerYouTube} />}
      {pickingClips && <FootagePickerModal multi onClose={() => setPickingClips(false)} onPick={addFootageClips} />}
      {comparing && <QuickCompareModal movie={movie} onClose={() => setComparing(false)} />}
      {grab && grab.asset && (
        <FrameGrabber
          asset={grab.asset}
          tripId={grab.clip.tripId}
          gradeOverride={grab.clip.useGrade === false ? null : (grab.clip.grade || null)}
          startTime={grab.startTime}
          movieId={id}
          movieTitle={movie?.title || ''}
          onClose={() => setGrab(null)}
        />
      )}
      {grabErr && (
        <div className="cs-toast cs-toast-err" role="alert" onClick={() => setGrabErr(null)}>
          {grabErr}
          <button className="cs-toast-x" aria-label="Dismiss" onClick={() => setGrabErr(null)}>✕</button>
        </div>
      )}
    </div>
  );
}
